import { useCallback, useEffect, useMemo, useRef, useState, Suspense, lazy } from 'react';
import './design-system.css';
import './App.css';
import './styles/mobile.css';
import { Layout } from './components/layout';
import { formatCurrency, detectDefaultCurrency } from './utils/currency';
// ── Immediate-load critical components ──
import VoiceTest from './components/VoiceTest';
import ModernContainer from './components/ModernContainer';
import OnboardingWizard from './components/OnboardingWizard';
import BusinessContextEditor from './components/BusinessContextEditor';
import AIFeedbackWidget from './components/AIFeedbackWidget';
import SurveyModal from './components/SurveyModal';
import UpgradeBanner from './components/UpgradeBanner';
import MetaConnectedModal from './components/MetaConnectedModal';
import CookieConsent from './components/CookieConsent';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import { ErrorBoundary } from './components/ErrorBoundary';
import NotFoundPage from './components/NotFoundPage';
import ComingSoon from './components/ComingSoon';
import NotificationBell from './components/NotificationBell';
import BookingsAvailableBanner from './components/BookingsAvailableBanner';
import ModernSidebar from './components/layout/ModernSidebar';
import ModernHomePage from './components/views/ModernHomePage';

// ── Lazy-loaded view components (code-split) ──
const Bookings = lazy(() => import('./components/views/Bookings'));
const Payments = lazy(() => import('./components/views/Payments'));
const CalendarSync = lazy(() => import('./components/views/CalendarSync'));
const NotificationPreferences = lazy(() => import('./components/views/NotificationPreferences'));
const CrmSync = lazy(() => import('./components/views/CrmSync'));
const AnalyticsDashboard = lazy(() => import('./components/views/AnalyticsDashboard'));
const SalesDashboard = lazy(() => import('./components/views/SalesDashboard'));
const AdminDashboard = lazy(() => import('./components/views/admin/AdminDashboard'));
const DeveloperDashboard = lazy(() => import('./components/views/DeveloperDashboard'));
const DataCapturerDashboard = lazy(() => import('./components/views/DataCapturerDashboard'));
const ClientDashboard = lazy(() => import('./components/views/ClientDashboard'));
const Recordings = lazy(() => import('./components/views/Recordings'));
const IVRBuilder = lazy(() => import('./components/views/IVRBuilder'));
const Campaigns = lazy(() => import('./components/views/Campaigns'));
const MyAIChat = lazy(() => import('./components/views/MyAIChat'));
const BookingSettings = lazy(() => import('./components/views/BookingSettings'));
const AIFeedback = lazy(() => import('./components/views/AIFeedback'));
const LiveChat = lazy(() => import('./components/views/LiveChat'));
const SocialChannelsSettings = lazy(() => import('./components/views/SocialChannelsSettings'));
const LandingPage = lazy(() => import('./components/views/LandingPage'));
const PricingPage = lazy(() => import('./components/views/PricingPage'));
const SupportDashboard = lazy(() => import('./components/views/SupportDashboard'));
const FinancialDashboard = lazy(() => import('./components/views/FinancialDashboard'));
const LegalCentreAuth = lazy(() => import('./components/views/LegalCentreAuth'));
const PublicLegalPage = lazy(() => import('./components/views/PublicLegalPage'));
const ReferralManager = lazy(() => import('./components/views/ReferralManager'));
const AffiliateDashboard = lazy(() => import('./components/views/AffiliateDashboard'));
const CallbacksManager = lazy(() => import('./components/views/CallbacksManager'));
const PrivacyPage = lazy(() => import('./components/views/PrivacyPage'));
const TermsPage = lazy(() => import('./components/views/TermsPage'));
const CookiePolicyPage = lazy(() => import('./components/views/CookiePolicyPage'));
const DataDeletionPage = lazy(() => import('./components/views/DataDeletionPage'));
const AboutUs = lazy(() => import('./components/views/AboutUs'));
const HomeDashboard = lazy(() => import('./components/views/HomeDashboard'));
const OpsConsole = lazy(() => import('./components/views/OpsConsole'));
const ClientWorkspace = lazy(() => import('./components/views/ClientWorkspace'));
const TeamManager = lazy(() => import('./components/views/admin/TeamManager'));
const PermissionManager = lazy(() => import('./components/views/admin/PermissionManager'));
const TeamMemberInvite = lazy(() => import('./components/views/TeamMemberInvite'));
const SubscriptionManager = lazy(() => import('./components/views/SubscriptionManager'));
const UserProfile = lazy(() => import('./components/views/admin/UserProfile'));
const ComplianceDashboard = lazy(() => import('./components/views/admin/ComplianceDashboard'));
const RoleplayDemo = lazy(() => import('./components/views/RoleplayDemo'));
const UnifiedInbox = lazy(() => import('./components/views/UnifiedInbox'));
const ChannelInbox = lazy(() => import('./components/views/ChannelInbox'));
const CrmView = lazy(() => import('./components/views/CrmView'));
const SettingsView = lazy(() => import('./components/views/admin/SettingsView'));
const OnboardingChecklist = lazy(() => import('./components/views/OnboardingChecklist'));
const BillingHistory = lazy(() => import('./components/views/BillingHistory'));
const SystemHealth = lazy(() => import('./components/views/SystemHealth'));
const CEODashboard = lazy(() => import('./components/views/CEODashboard'));
const PhoneNumberSetup = lazy(() => import('./components/views/PhoneNumberSetup'));
const PhoneNumberRequestsManager = lazy(() => import('./components/views/PhoneNumberRequestsManager'));
const TwilioSetup = lazy(() => import('./components/views/TwilioSetup'));
const ClientLookup = lazy(() => import('./components/views/ClientLookup'));
const GlobalNumberInventory = lazy(() => import('./components/views/GlobalNumberInventory'));

// ── Lazy-loaded CRM Module ──
const LeadsView = lazy(() => import('./components/views/crm/LeadsView'));
const OpportunitiesView = lazy(() => import('./components/views/crm/OpportunitiesView'));
const AccountsView = lazy(() => import('./components/views/crm/AccountsView'));
const ContactsView = lazy(() => import('./components/views/crm/ContactsView'));
const QuotationsView = lazy(() => import('./components/views/crm/QuotationsView'));
const CRMDashboard = lazy(() => import('./components/views/crm/CRMDashboard'));

// ── Lazy-loaded Help Desk Module ──
const TicketsView = lazy(() => import('./components/views/helpdesk/TicketsView'));
const TicketDetailView = lazy(() => import('./components/views/helpdesk/TicketDetailView'));
const KnowledgeBaseView = lazy(() => import('./components/views/helpdesk/KnowledgeBaseView'));
const AssetsView = lazy(() => import('./components/views/helpdesk/AssetsView'));
const SLAManagementView = lazy(() => import('./components/views/helpdesk/SLAManagementView'));
const HelpDeskDashboard = lazy(() => import('./components/views/helpdesk/HelpDeskDashboard'));

// ── Lazy-loaded Project Management Module ──
const ProjectsView = lazy(() => import('./components/views/projects/ProjectsView'));
const ProjectDetailView = lazy(() => import('./components/views/projects/ProjectDetailView'));
const TasksView = lazy(() => import('./components/views/projects/TasksView'));
const MilestonesView = lazy(() => import('./components/views/projects/MilestonesView'));
const TimeLoggingView = lazy(() => import('./components/views/projects/TimeLoggingView'));
const ProjectDashboard = lazy(() => import('./components/views/projects/ProjectDashboard'));

// ── Loading fallback for lazy-loaded components ──
const LazyFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    background: 'var(--surface)',
    borderRadius: '8px',
    margin: '20px'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid var(--border)',
        borderTop: '3px solid var(--accent)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
      }} />
      <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Loading...</p>
    </div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// ── Helper to wrap lazy components with Suspense ──
const Lazy = ({ component: Component, ...props }) => (
  <Suspense fallback={<LazyFallback />}>
    <Component {...props} />
  </Suspense>
);

const emptyClient = {
  business_name: '',
  phone_number: '',
  ai_prompt: '',
  voice_preference: 'alloy',
  notification_email: '',
  business_hours: '',
};

const onboardingSteps = [
  {
    id: 'whatsapp',
    title: 'Assign WhatsApp routing',
    detail: 'We connect your WhatsApp number and map it to the right workspace.',
  },
  {
    id: 'facebook-instagram',
    title: 'Assign Facebook + Instagram messaging',
    detail: 'We connect Meta messaging channels to your workspace and unify replies in one inbox.',
  },
  {
    id: 'email',
    title: 'Assign email inboxes',
    detail: 'We route inbound email addresses into Oakdesk for your team.',
  },
  {
    id: 'ai-provider',
    title: 'Configure AI behavior',
    detail: 'Oakdesk manages the AI stack while you define tone and business rules.',
  },
  {
    id: 'first-client',
    title: 'Set up your business',
    detail: 'Define greetings, hours, routing, and escalation paths.',
  },
  {
    id: 'admin-rights',
    title: 'Assign admin rights',
    detail: 'Promote trusted operators via Access management.',
  },
  {
    id: 'test-flow',
    title: 'Run a test flow',
    detail: 'Trigger a call or message to validate end-to-end.',
  },
];

const statusLabel = {
  idle: 'Idle',
  loading: 'Loading...',
  success: 'Ready',
  error: 'Error',
};

const views = [
  // ── Primary navigation (shown in sidebar) ──
  { id: 'affiliate-dashboard', label: 'My Referrals' },
  { id: 'data-capturer-dashboard', label: 'My Leads' },
  { id: 'ceo-dashboard',     label: 'Command Centre' },
  { id: 'ops-console',       label: 'Command' },
  { id: 'client-workspace',  label: 'Home' },
  { id: 'clients-mgmt',      label: 'Clients' },
  { id: 'unified-inbox',     label: 'Inbox' },
  { id: 'whatsapp',          label: 'WhatsApp' },
  { id: 'facebook',          label: 'Facebook' },
  { id: 'instagram',         label: 'Instagram' },
  { id: 'email',             label: 'Email' },
  { id: 'live-chat',         label: 'Live Chat' },
  { id: 'campaigns',         label: 'Campaigns' },
  { id: 'sales-dashboard',   label: 'Sales' },
  { id: 'crm',               label: 'CRM' },
  { id: 'client-lookup',     label: 'Client Lookup' },
  { id: 'bookings',          label: 'Bookings' },
  { id: 'payments',          label: 'Payments' },
  { id: 'analytics-dashboard', label: 'Analytics' }, // was "Analytics Dashboard" — merged
  { id: 'financial',         label: 'Revenue' },
  { id: 'support',           label: 'Support' },
  { id: 'callbacks',         label: 'Callbacks' },
  { id: 'referrals',         label: 'Referrals' },
  { id: 'legal',             label: 'Legal Centre' },
  { id: 'legal-public',      label: 'Legal & Policies' },
  { id: 'knowledge-base',    label: 'Knowledge Base' },
  { id: 'recordings',        label: 'Recordings' },
  { id: 'ivr',               label: 'Phone Menu' },
  { id: 'business-context',  label: 'Business Profile' },
  { id: 'booking-settings',  label: 'Booking Settings' },
  { id: 'social-channels',   label: 'Channels' },
  { id: 'phone-number-setup', label: 'Phone Numbers' },
  { id: 'phone-number-requests', label: 'Number Requests' },
  { id: 'team-manager',      label: 'Team' },
  { id: 'global-number-inventory', label: 'Number Pool' },
  { id: 'billing',           label: 'Billing' },
  { id: 'settings',          label: 'Settings' },
  { id: 'pricing',           label: 'Upgrade' },
  { id: 'my-ai-chat',        label: 'Chat with My AI' },
  { id: 'onboarding',        label: 'Setup' },
  { id: 'notifications',     label: 'Notifications' },

  { id: 'onboarding-checklist', label: 'Setup Checklist' },
  { id: 'billing-history',  label: 'Billing History' },
  { id: 'system-health',    label: 'System Health' },

  // ── CRM Module ──
  { id: 'crm-dashboard',     label: 'CRM Dashboard' },
  { id: 'leads-view',        label: 'Leads' },
  { id: 'opportunities-view', label: 'Opportunities' },
  { id: 'accounts-view',     label: 'Accounts' },
  { id: 'contacts-view',     label: 'Contacts' },
  { id: 'quotations-view',   label: 'Quotations' },

  // ── Help Desk Module ──
  { id: 'helpdesk-dashboard', label: 'Help Desk Dashboard' },
  { id: 'tickets-view',      label: 'Tickets' },
  { id: 'ticket-detail-view', label: 'Ticket Detail' },
  { id: 'knowledge-base-view', label: 'Knowledge Base' },
  { id: 'assets-view',       label: 'Assets' },
  { id: 'sla-management',    label: 'SLA Management' },

  // ── Project Management Module ──
  { id: 'projects-dashboard', label: 'Projects Dashboard' },
  { id: 'projects-view',     label: 'Projects' },
  { id: 'project-detail-view', label: 'Project Detail' },
  { id: 'tasks-view',        label: 'Tasks' },
  { id: 'milestones-view',   label: 'Milestones' },
  { id: 'time-logging-view', label: 'Time Logging' },

  // ── Legal pages (accessible via URL or nav) ──
  { id: 'privacy',           label: 'Privacy Policy' },
  { id: 'terms',             label: 'Terms of Service' },
  { id: 'about-us',          label: 'About Us' },
  { id: 'profile',           label: 'My Profile' },
  { id: 'ai-roleplay',      label: 'AI Demo Call' },

  // ── Still-routable but not in sidebar ──
  { id: 'home',              label: 'Home' },
  { id: 'manager',           label: 'Manager' },
  { id: 'overview',          label: 'Overview' },
  { id: 'company',           label: 'Company' },
  { id: 'portal',            label: 'Portal' },
  { id: 'admin-users',       label: 'Users' },
  { id: 'channels',          label: 'Channel Config' },
  { id: 'analytics',         label: 'Analytics (legacy)' },
  { id: 'crm-sync',          label: 'CRM Sync' },
  { id: 'calendar-sync',     label: 'Calendar Sync' },
  { id: 'tokens',            label: 'Tokens' },
  { id: 'sentiment',         label: 'Sentiment' },
  { id: 'ab-testing',        label: 'A/B Testing' },
  { id: 'compliance',        label: 'Compliance' },
  { id: 'ai-feedback',       label: 'AI Feedback' },
  { id: 'ai-insights',       label: 'AI Insights' },
  { id: 'ai-test',           label: 'Demo Call' },
  { id: 'tests',             label: 'System Tests' },
  { id: 'sales-chat',        label: 'Sales Chat' },
  { id: 'permissions',       label: 'Permissions' },
];

// ── Developer sidebar — full system access ──
const SIDEBAR_SECTIONS_DEVELOPER = [
  { section: 'Ops',       ids: ['ceo-dashboard', 'ops-console', 'clients-mgmt', 'system-health', 'compliance', 'global-number-inventory'] },
  { section: 'Business',  ids: ['sales-dashboard', 'financial', 'crm', 'bookings', 'campaigns', 'analytics-dashboard', 'referrals'] },
  { section: 'CRM',       ids: ['crm-dashboard', 'leads-view', 'opportunities-view', 'accounts-view', 'contacts-view', 'quotations-view'] },
  { section: 'Help Desk', ids: ['helpdesk-dashboard', 'tickets-view', 'knowledge-base-view', 'assets-view', 'sla-management'] },
  { section: 'Projects',  ids: ['projects-dashboard', 'projects-view', 'tasks-view', 'milestones-view', 'time-logging-view'] },
  { section: 'Channels',  ids: ['unified-inbox', 'whatsapp', 'facebook', 'instagram', 'email', 'live-chat'] },
  { section: 'Config',    ids: ['twilio-setup', 'phone-number-setup', 'phone-number-requests', 'business-context', 'booking-settings', 'social-channels', 'recordings', 'ivr', 'team-manager', 'settings', 'legal'] },
];

// ── Staff sidebar (merged Admin + Agent) — operations + sales + support, NO financials, NO dev tools ──
const SIDEBAR_SECTIONS_STAFF = [
  { section: '📊 Dashboard',   ids: ['ceo-dashboard', 'sales-dashboard', 'crm-dashboard', 'helpdesk-dashboard', 'analytics-dashboard'] },
  { section: '💼 Pipeline',    ids: ['leads-view', 'opportunities-view', 'accounts-view', 'contacts-view', 'bookings', 'campaigns'] },
  { section: '👥 Contacts',    ids: ['crm', 'data-capturer-dashboard', 'client-lookup'] },
  { section: '🎫 Support',     ids: ['tickets-view', 'support', 'knowledge-base-view', 'sla-management', 'assets-view'] },
  { section: '📋 Projects',    ids: ['projects-view', 'projects-dashboard', 'tasks-view', 'milestones-view', 'time-logging-view'] },
  { section: '💬 Channels',    ids: ['unified-inbox', 'whatsapp', 'facebook', 'instagram', 'email', 'live-chat'] },
  { section: '⚙️ Settings',    ids: ['business-context', 'booking-settings', 'social-channels', 'phone-number-setup', 'twilio-setup', 'recordings', 'ivr', 'settings', 'legal'] },
  { section: '🔧 Operations',  ids: ['clients-mgmt', 'system-health', 'compliance', 'referrals', 'quotations-view', 'phone-number-requests'] },
];

// ── Client/User sidebar — own workspace only ──
const SIDEBAR_SECTIONS_USER = [
  { section: 'Home',      ids: ['client-workspace'] },
  { section: 'Inbox',     ids: ['unified-inbox', 'whatsapp', 'facebook', 'instagram', 'email', 'campaigns'] },
  { section: 'Work',      ids: ['crm', 'crm-dashboard', 'bookings', 'analytics-dashboard', 'projects-view', 'my-ai-chat'] },
  { section: 'Settings',  ids: ['phone-number-setup', 'booking-settings', 'social-channels', 'settings'] },
];

// ── CEO sidebar — total access including financials AND developer tools ──
const SIDEBAR_SECTIONS_CEO = [
  { section: 'Ops',       ids: ['ceo-dashboard', 'ops-console', 'clients-mgmt', 'system-health', 'compliance', 'global-number-inventory'] },
  { section: 'Business',  ids: ['sales-dashboard', 'financial', 'crm', 'bookings', 'campaigns', 'analytics-dashboard', 'referrals'] },
  { section: 'CRM',       ids: ['crm-dashboard', 'leads-view', 'opportunities-view', 'accounts-view', 'contacts-view', 'quotations-view'] },
  { section: 'Help Desk', ids: ['helpdesk-dashboard', 'tickets-view', 'knowledge-base-view', 'assets-view', 'sla-management'] },
  { section: 'Projects',  ids: ['projects-dashboard', 'projects-view', 'tasks-view', 'milestones-view', 'time-logging-view'] },
  { section: 'Channels',  ids: ['unified-inbox', 'whatsapp', 'facebook', 'instagram', 'email', 'live-chat'] },
  { section: 'Config',    ids: ['twilio-setup', 'phone-number-setup', 'phone-number-requests', 'business-context', 'booking-settings', 'social-channels', 'recordings', 'ivr', 'ai-test', 'ab-testing', 'tokens', 'settings', 'legal'] },
];

// ── Affiliate sidebar — their own referral stats only ──
const SIDEBAR_SECTIONS_AFFILIATE = [
  { section: 'Affiliate', ids: ['affiliate-dashboard'] },
];

// ── Data Capturer sidebar — their own leads only ──
const SIDEBAR_SECTIONS_DATA_CAPTURER = [
  { section: 'Leads', ids: ['data-capturer-dashboard', 'client-lookup'] },
];

// Active section set is chosen per role (see sidebarNavigation memo below)
const SIDEBAR_SECTIONS = SIDEBAR_SECTIONS_DEVELOPER; // fallback; real value comes from memo

const MODULE_DEFINITIONS = [
  {
    id: 'ai-voice',
    label: 'AI Voice Receptionist',
    description: 'Answer inbound calls automatically with a custom AI voice agent.',
    views: ['manager', 'ai-test', 'knowledge-base', 'sentiment', 'ab-testing', 'recordings', 'ivr'],
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp Messaging',
    description: 'Manage customer conversations via WhatsApp.',
    views: ['whatsapp'],
  },
  {
    id: 'social',
    label: 'Social Messaging',
    description: 'Handle Facebook Messenger and Instagram DMs.',
    views: ['facebook', 'instagram'],
  },
  {
    id: 'email',
    label: 'Email',
    description: 'Send and receive business emails from the dashboard.',
    views: ['email'],
  },
  {
    id: 'crm',
    label: 'CRM & Contacts',
    description: 'Track contacts, deals, and customer relationships.',
    views: ['crm', 'crm-sync'],
  },
  {
    id: 'bookings',
    label: 'Bookings & Scheduling',
    description: 'Accept appointments and sync with your calendar.',
    views: ['bookings', 'calendar-sync', 'notifications'],
  },
  {
    id: 'analytics',
    label: 'Analytics & Insights',
    description: 'Track performance, conversation quality, and AI effectiveness.',
    views: ['analytics', 'analytics-dashboard', 'ai-insights', 'ai-feedback'],
  },
  {
    id: 'compliance',
    label: 'Compliance & Audit',
    description: 'Audit logs and regulatory compliance tools.',
    views: ['compliance'],
  },
  {
    id: 'bulk-messaging',
    label: 'Bulk Messaging',
    description: 'Send SMS and WhatsApp campaigns to all your contacts at once.',
    views: ['campaigns'],
  },
  {
    id: 'live-chat',
    label: 'Live Chat Widget',
    description: 'Embed a real-time chat widget on your website and manage conversations from the dashboard.',
    views: ['live-chat'],
  },
];

const CORE_USER_VIEWS = new Set([
  'client-workspace', 'business-context', 'billing', 'settings', 'onboarding',
  'unified-inbox', 'notifications', 'support', 'pricing', 'social-channels', 'phone-number-setup',
  'projects-view', 'project-detail-view', 'tasks-view', 'milestones-view', 'time-logging-view',
  'my-ai-chat', 'booking-settings',
]);

const formatNavLabel = (label, fallbackIcon = '') => {
  const trimmed = `${label}`.trim();
  const firstChar = trimmed.charAt(0);
  if (firstChar && firstChar.charCodeAt(0) > 127) {
    const parts = trimmed.split(' ');
    if (parts.length > 1) {
      return { icon: parts[0], label: parts.slice(1).join(' ') };
    }
  }
  return { icon: fallbackIcon, label: trimmed };
};

const ROLE_CEO = 'ceo';
const ROLE_STAFF = 'staff';
const ROLE_DEVELOPER = 'developer';
const ROLE_USER = 'user';
const ROLE_AFFILIATE = 'affiliate';
const ROLE_DATA_CAPTURER = 'data_capturer';

// Old roles mapped to new structure: admin → ceo, agent → staff, sales/support → staff
const RETIRED_ROLE_ALIASES = new Set(['admin', 'agent', 'sales', 'support']);

const ROLE_LABELS = {
  ceo: 'CEO',
  staff: 'Staff',
  developer: 'Developer',
  user: 'Client User',
  affiliate: 'Affiliate',
  data_capturer: 'Data Capturer',
};

const normalizeRole = (role) => {
  if (!role) return ROLE_USER;
  const normalized = `${role}`.toLowerCase();
  if (normalized === 'admin') return ROLE_CEO;
  if (['agent', 'sales', 'support'].includes(normalized)) return ROLE_STAFF;
  return normalized;
};

const isStaffRole = (role) => [ROLE_STAFF, ROLE_DEVELOPER, ROLE_CEO, ROLE_AFFILIATE, ROLE_DATA_CAPTURER].includes(role);

// CEO: Total access including financials AND developer tools
const CEO_VIEW_IDS = new Set([
  // Operations & Dev Tools
  'ceo-dashboard', 'ops-console', 'clients-mgmt', 'system-health', 'compliance', 'ai-feedback', 'ai-test', 'ab-testing', 'tokens', 'global-number-inventory', 'team-manager',
  // Business & Financials
  'crm', 'bookings', 'campaigns', 'analytics-dashboard', 'financial', 'sales-dashboard', 'referrals',
  // Channels
  'unified-inbox', 'whatsapp', 'facebook', 'instagram', 'email', 'live-chat',
  // Configuration
  'business-context', 'booking-settings', 'social-channels', 'channels', 'knowledge-base', 'recordings', 'ivr',
  'calendar-sync', 'crm-sync', 'settings', 'phone-number-setup', 'phone-number-requests',
  // legacy routes
  'home', 'portal',
]);

// Developer: Dev tools only, NO financials
const DEVELOPER_VIEW_IDS = new Set([
  // Operations & Dev Tools (NO financials)
  'ops-console', 'system-health', 'ai-feedback', 'ai-test', 'ab-testing', 'tokens',
  // Business (NO financials)
  'crm', 'bookings', 'campaigns', 'analytics-dashboard', 'sales-dashboard',
  // Channels
  'unified-inbox', 'whatsapp', 'facebook', 'instagram', 'email',
  // Configuration
  'business-context', 'booking-settings', 'social-channels', 'channels', 'knowledge-base', 'recordings', 'ivr',
  'team-manager', 'calendar-sync', 'crm-sync', 'settings', 'phone-number-setup', 'phone-number-requests',
  // legacy routes
  'home', 'portal',
]);

// Staff (merged Admin + Agent): Operations + CRM + Inbox, NO dev tools, NO financials
const STAFF_VIEW_IDS = new Set([
  // Operations (NO dev tools, NO financials)
  'clients-mgmt', 'system-health', 'compliance',
  // Business (NO financials)
  'crm', 'bookings', 'campaigns', 'analytics-dashboard', 'sales-dashboard', 'referrals', 'support', 'client-lookup', 'data-capturer-dashboard',
  'crm-dashboard', 'leads-view', 'opportunities-view', 'contacts-view', 'knowledge-base',
  // Channels
  'unified-inbox', 'whatsapp', 'facebook', 'instagram', 'email', 'live-chat',
  // Configuration
  'business-context', 'booking-settings', 'social-channels', 'channels', 'recordings', 'ivr',
  'calendar-sync', 'crm-sync', 'settings', 'phone-number-setup', 'phone-number-requests',
  // legacy routes
  'home', 'portal',
]);

// Client users: inbox + CRM + bookings + analytics
const USER_VIEW_IDS = new Set([
  'client-workspace',
  'unified-inbox', 'whatsapp', 'facebook', 'instagram', 'email', 'campaigns',
  'crm', 'bookings', 'analytics-dashboard',
  'projects-view', 'project-detail-view', 'tasks-view', 'milestones-view', 'time-logging-view',
  'social-channels', 'settings', 'pricing', 'phone-number-setup', 'my-ai-chat', 'booking-settings',
]);

// Affiliate: their own referral stats only — nothing else
const AFFILIATE_VIEW_IDS = new Set([
  'affiliate-dashboard',
]);

// Data Capturer: their own assigned/unclaimed leads only — nothing else
const DATA_CAPTURER_VIEW_IDS = new Set([
  'data-capturer-dashboard',
]);

const getAllowedViewIds = (role) => {
  if (role === ROLE_CEO) return new Set(CEO_VIEW_IDS);
  if (role === ROLE_STAFF) return new Set(STAFF_VIEW_IDS);
  if (role === ROLE_DEVELOPER) return new Set(DEVELOPER_VIEW_IDS);
  if (role === ROLE_DATA_CAPTURER) return new Set(STAFF_VIEW_IDS); // DATA_CAPTURER is part of STAFF
  if (role === ROLE_AFFILIATE) return new Set(AFFILIATE_VIEW_IDS);
  return new Set(USER_VIEW_IDS);
};

const getDefaultViewForRole = (role) => {
  if (role === ROLE_CEO) return 'ceo-dashboard';
  if (role === ROLE_STAFF) return 'crm';
  if (role === ROLE_DEVELOPER) return 'ops-console';
  if (role === ROLE_DATA_CAPTURER) return 'data-capturer-dashboard'; // DATA_CAPTURER is part of STAFF
  if (role === ROLE_AFFILIATE) return 'affiliate-dashboard';
  return 'client-workspace';
};

const roleLabel = (role) => {
  const normalized = normalizeRole(role);
  return ROLE_LABELS[normalized] || 'User';
};

// Removed unused channelLabel function - was never called

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '');
  }
  // Default to same-origin requests (works with local proxy and production reverse proxy).
  return '';
};

const AUTH_DISABLED_TOKEN = 'auth-disabled';
const originalFetch = window.fetch.bind(window);
// Wrap fetch to automatically attach auth headers and API base URL.
const apiFetch = (input, init = {}) => {
  let url = input;
  if (typeof url === 'string' && url.startsWith('/')) {
    url = getApiBaseUrl() + url;
  }
  const headers = new Headers(init.headers || {});
  const token = localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token');
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return originalFetch(url, { ...init, headers });
};
window.fetch = apiFetch;

const formatErrorDetail = (detail) => {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        if (typeof item === 'object') return item.msg || item.detail || JSON.stringify(item);
        return String(item);
      })
      .filter(Boolean)
      .join(' | ');
  }
  if (typeof detail === 'object') return detail.msg || detail.detail || JSON.stringify(detail);
  return String(detail);
};

const parseJsonResponse = async (response, fallbackMessage = 'Unexpected non-JSON response from server.') => {
  const raw = await response.text();
  if (!raw) return {};

  try {
    return JSON.parse(raw);
  } catch (_error) {
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${fallbackMessage}`);
    }
    throw new Error(fallbackMessage);
  }
};

const AUDIT_LOG = [
  {
    id: 'evt-001',
    action: 'Role updated',
    actor: 'Admin Console',
    detail: 'Promoted Jordan to Admin.',
    ts: '2026-02-18 14:22',
  },
  {
    id: 'evt-002',
    action: 'Access revoked',
    actor: 'Security Policy',
    detail: 'Suspended inactive account access.',
    ts: '2026-02-17 09:10',
  },
  {
    id: 'evt-003',
    action: 'Invite sent',
    actor: 'Ops Team',
    detail: 'Invited new client operator.',
    ts: '2026-02-16 18:05',
  },
];

function EmptyStateCard({ title, detail, actionLabel, onAction }) {
  return (
    <div className="empty-card">
      <div>
        <h4>{title}</h4>
        <p>{detail}</p>
      </div>
      {actionLabel && onAction && (
        <button className="ghost" type="button" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function SkeletonList({ rows = 4 }) {
  return (
    <div className="skeleton-list" aria-hidden="true">
      {Array.from({ length: rows }).map((_, idx) => (
        <div key={`skeleton-${idx}`} className="skeleton-card">
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line skeleton-line--short" />
        </div>
      ))}
    </div>
  );
}

function App() {
  const [view, setView] = useState(() => {
    const token = localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token');
    return token ? 'home' : 'landing';
  });
  const [clients, setClients] = useState([]);
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [clientForm, setClientForm] = useState(emptyClient);
  const [healthSummary, setHealthSummary] = useState(null);
  const [notice, setNotice] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [tableDensity, setTableDensity] = useState(
    () => localStorage.getItem('oakdesk_density') || 'comfortable'
  );
  const [selectedClientId, setSelectedClientId] = useState('');
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [contactForm, setContactForm] = useState({ full_name: '', phone: '', email: '', status: 'lead' });
  const [callbackForm, setCallbackForm] = useState({ full_name: '', phone: '', email: '', message: '' });
  const [callbackSubmitted, setCallbackSubmitted] = useState(false);
  const [dealForm, setDealForm] = useState({ title: '', stage: 'new', value: '', probability: 50, currency: 'USD' });
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [whatsappConversations, setWhatsappConversations] = useState([]);
  const [selectedWAConversation, setSelectedWAConversation] = useState(null);
  const [waMessageInput, setWaMessageInput] = useState('');
  const [waAutoRefreshEnabled, setWaAutoRefreshEnabled] = useState(true);
  const [waRefreshInterval, setWaRefreshInterval] = useState(3000); // 3 seconds
  const [waMessageSending, setWaMessageSending] = useState({});
  const [waFailedMessages, setWaFailedMessages] = useState(new Set());
  const [emailConversations, setEmailConversations] = useState([]);
  const [selectedEmailConversation, setSelectedEmailConversation] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailAutoRefreshEnabled, setEmailAutoRefreshEnabled] = useState(true);
  const emailRefreshInterval = 5000;
  const [salesChatConversations, setSalesChatConversations] = useState([]);
  const [selectedSalesConversation, setSelectedSalesConversation] = useState(null);
  const [salesChatMessages, setSalesChatMessages] = useState([]);
  const [salesChatInput, setSalesChatInput] = useState('');
  const [salesChatProfile, setSalesChatProfile] = useState({ name: '', email: '', phone: '' });
  const [salesChatActiveOnly, setSalesChatActiveOnly] = useState(true);
  const [voiceTestFile, setVoiceTestFile] = useState(null);
  const [voiceTestResult, setVoiceTestResult] = useState(null);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceRecordingUrl, setVoiceRecordingUrl] = useState('');
  const mediaRecorderRef = useRef(null);
  const voiceChunksRef = useRef([]);
  const [clientNumbers, setClientNumbers] = useState([]);
  const [clientInboxes, setClientInboxes] = useState([]);
  const [poolDids, setPoolDids] = useState([]);
  const [selectedPoolDidId, setSelectedPoolDidId] = useState('');
  const [status, setStatus] = useState({
    clients: 'idle',
    calls: 'idle',
    call: 'idle',
    create: 'idle',
    health: 'idle',
    contacts: 'idle',
    deals: 'idle',
    analytics: 'idle',
    whatsapp: 'idle',
    whatsappSend: 'idle',
    email: 'idle',
    emailSend: 'idle',
    emailLoad: 'idle',
    salesChat: 'idle',
    salesChatMessages: 'idle',
    salesChatSend: 'idle',
    salesChatAssign: 'idle',
    voiceTest: 'idle',
    channelNumbers: 'idle',
    channelInboxes: 'idle',
    channelNumberCreate: 'idle',
    channelInboxCreate: 'idle',
    adminUsers: 'idle',
    adminUserCreate: 'idle',
    inbox: 'idle',
    inboxSummary: 'idle',
    adminInvites: 'idle',
    inviteCreate: 'idle',
    aiInsights: 'idle',
    billing: 'idle',
    billingUpdate: 'idle',
    sentiment: 'idle',
    sentimentAnalyze: 'idle',
    abTests: 'idle',
    abTestCreate: 'idle',
    knowledgeBase: 'idle',
    kbCreate: 'idle',
    compliance: 'idle',
    recordingCreate: 'idle',
  });

  const [showClientForm, setShowClientForm] = useState(false);
  const [whatsappMetrics, setWhatsappMetrics] = useState({ totalConversations: 0, activeConversations: 0, totalMessages: 0 });
  const [systemStatus, setSystemStatus] = useState({ backend: 'checking', frontend: 'ok', database: 'checking' });
  const [aiProviders, setAiProviders] = useState([]);
  const [currentAiProvider, setCurrentAiProvider] = useState('openai');
  const [selectedAiModel, setSelectedAiModel] = useState('gpt-4o-mini');
  const [aiProviderApiKey, setAiProviderApiKey] = useState('');
  const [aiIsMockMode, setAiIsMockMode] = useState(false);
  const [aiTestPrompt, setAiTestPrompt] = useState('');
  const [aiTestStatus, setAiTestStatus] = useState('idle');
  const [demoSessionId, setDemoSessionId] = useState('');
  const [demoLookupSessionId, setDemoLookupSessionId] = useState('');
  const [demoRoleplayCompany, setDemoRoleplayCompany] = useState('');
  const [demoMessages, setDemoMessages] = useState([]);
  const [demoSummary, setDemoSummary] = useState('');
  const [demoInput, setDemoInput] = useState('');
  const [demoAudioUrl, setDemoAudioUrl] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('alloy');
  const [unifiedInbox, setUnifiedInbox] = useState([]);
  const [selectedInboxMessage, setSelectedInboxMessage] = useState(null);
  const [inboxSummary, setInboxSummary] = useState(null);
  const [inboxSummaryStatus, setInboxSummaryStatus] = useState('idle');
  const [handoffStatus, setHandoffStatus] = useState(null); // null | { status, handoff }
  const [handoffQueue, setHandoffQueue] = useState([]);
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [landingMenuOpen, setLandingMenuOpen] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token') || '');
  const [authUser, setAuthUser] = useState(null);
  const [authRole, setAuthRole] = useState(localStorage.getItem('oakdesk_role') || '');
  const [roleOverride, setRoleOverride] = useState('');
  const [authMode, setAuthMode] = useState('login');
  const [authNotice, setAuthNotice] = useState('');
  const [authNoticeType, setAuthNoticeType] = useState('error'); // 'error' | 'success'
  const [postAuthView, setPostAuthView] = useState('');
  const [authFlowToken, setAuthFlowToken] = useState('');
  const [authResetEmail, setAuthResetEmail] = useState('');
  const [authNewPassword, setAuthNewPassword] = useState('');
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    full_name: '',
    business_name: '',
    referral_code: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [enabledModules, setEnabledModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeBusinessName, setUpgradeBusinessName] = useState('');
  const [upgradeStatus, setUpgradeStatus] = useState('idle');
  const [upgradeError, setUpgradeError] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientFilterStatus, setClientFilterStatus] = useState('all');
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminInvites, setAdminInvites] = useState([]);
  const [adminUserForm, setAdminUserForm] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'user',
    client_id: '',
  });
  const [inviteForm, setInviteForm] = useState({
    full_name: '',
    email: '',
    role: 'user',
    organisation_name: '',
  });
  const [numberForm, setNumberForm] = useState({
    client_id: '',
    channel: 'voice',
    phone_number: '',
    label: '',
    provider: '',
    access_token: '',
    phone_number_id: '',
    page_id: '',
  });
  const [credEditId, setCredEditId] = useState(null);
  const [credEditForm, setCredEditForm] = useState({ access_token: '', phone_number_id: '', page_id: '' });
  const [inboxForm, setInboxForm] = useState({
    client_id: '',
    email_address: '',
    sender_name: '',
    provider: '',
  });
  const [aiInsights, setAiInsights] = useState(null);
  const [billingUsage, setBillingUsage] = useState(null);
  const [billingWindowDays, setBillingWindowDays] = useState(30);
  const [billingForm, setBillingForm] = useState({
    monthly_call_limit: '',
    monthly_whatsapp_limit: '',
    monthly_email_limit: '',
    monthly_message_limit: '',
    monthly_seat_limit: '',
  });

  // --- Trial status ---
  const [trialStatus, setTrialStatus] = useState(null);

  // --- Meta OAuth success modal ---
  const [metaConnectedPlatforms, setMetaConnectedPlatforms] = useState(null);

  // --- Sales Dashboard ---
  const [salesDashboard, setSalesDashboard] = useState(null);
  const [salesDashboardLoading, setSalesDashboardLoading] = useState(false);

  // --- Token Balance ---
  const [tokenBalance, setTokenBalance] = useState(null);
  const [tokenTransactions, setTokenTransactions] = useState([]);
  const [tokenTopUpForm, setTokenTopUpForm] = useState({ client_id: '', amount: '', notes: '' });
  const [tokenTopUpStatus, setTokenTopUpStatus] = useState('idle');
  const [sidebarOpen, setSidebarOpen] = useState(
    () => localStorage.getItem('oakdesk_sidebar_open') !== 'false'
  );
  const [sidebarWidth, setSidebarWidth] = useState(
    () => Number(localStorage.getItem('oakdesk_sidebar_width')) || 260
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('oakdesk_sidebar_collapsed') === 'true'
  );
  const [editingUserId, setEditingUserId] = useState(null);
  const [userEditForm, setUserEditForm] = useState({ full_name: '', client_id: '' });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    detail: '',
    confirmLabel: 'Confirm',
  });

  // --- Sentiment Analysis ---
  const [sentimentAnalyzeText, setSentimentAnalyzeText] = useState('');
  const [sentimentResult, setSentimentResult] = useState(null);
  const [sentimentDashboard, setSentimentDashboard] = useState(null);
  const [sentimentEscalations, setSentimentEscalations] = useState([]);

  // --- A/B Testing ---
  const [abTests, setAbTests] = useState([]);
  const [abTestForm, setAbTestForm] = useState({ name: '', description: '', metric: 'conversion_rate' });
  const [selectedAbTest, setSelectedAbTest] = useState(null);
  const [abVariantForm, setAbVariantForm] = useState({ name: '', config: '{}' });
  const [abResults, setAbResults] = useState(null);

  // --- Knowledge Base ---
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKb, setSelectedKb] = useState(null);
  const [kbForm, setKbForm] = useState({ name: '', description: '', auto_sync: false });
  const [kbDocForm, setKbDocForm] = useState({ title: '', content: '', source_url: '' });
  const [kbSearchQuery, setKbSearchQuery] = useState('');
  const [kbSearchResults, setKbSearchResults] = useState([]);
  const [kbDocuments, setKbDocuments] = useState([]);
  const [kbStats, setKbStats] = useState(null); // eslint-disable-line no-unused-vars

  // --- Compliance & Recordings ---
  const [recordings, setRecordings] = useState([]);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [recordingForm, setRecordingForm] = useState({ call_id: '', duration_seconds: 0, file_url: '' });
  const [recordingAccessLogs, setRecordingAccessLogs] = useState([]);

  const isLanding = view === 'landing';
  const isLandingLayout = view === 'landing' || view === 'auth' || view === 'legal-public' || view === 'privacy' || view === 'terms' || view === 'about-us' || view === 'cookie-policy' || view === 'data-deletion';
  const isAuthenticated = Boolean(authToken || authUser);
  const getBaseRole = useCallback(
    (user) => normalizeRole(user?.role || authRole || localStorage.getItem('oakdesk_role') || ''),
    [authRole]
  );

  const getForcedRole = useCallback(
    (user) => (user?.email === 'kylehanger@protonmail.com' ? ROLE_DEVELOPER : getBaseRole(user)),
    [getBaseRole]
  );

  const resolvedRole = useMemo(
    () => normalizeRole(roleOverride || getForcedRole(authUser)),
    [authUser, getForcedRole, roleOverride]
  );
  const allowedViewIds = useMemo(() => getAllowedViewIds(resolvedRole), [resolvedRole]);
  const isAdmin = isStaffRole(resolvedRole);
  const isDeveloper = resolvedRole === ROLE_DEVELOPER;
  const canSwitchViewRole = isDeveloper || isAdmin;

  const effectiveViewIds = useMemo(() => {
    if (isAdmin || isDeveloper) return allowedViewIds;
    if (!enabledModules || enabledModules.length === 0) return allowedViewIds;
    const moduleViews = new Set(CORE_USER_VIEWS);
    MODULE_DEFINITIONS
      .filter((m) => enabledModules.includes(m.id))
      .forEach((m) => m.views.forEach((v) => moduleViews.add(v)));
    return new Set([...allowedViewIds].filter((id) => moduleViews.has(id)));
  }, [allowedViewIds, enabledModules, isAdmin, isDeveloper]);

  const sidebarNavigation = useMemo(() => {
    const viewMap = new Map(views.map((item) => [item.id, item.label]));
    const used = new Set();

    // Pick the right section set for the current role
    let activeSections;
    if (resolvedRole === ROLE_DEVELOPER) {
      activeSections = SIDEBAR_SECTIONS_DEVELOPER;
    } else if (resolvedRole === ROLE_STAFF || resolvedRole === ROLE_DATA_CAPTURER) {
      activeSections = SIDEBAR_SECTIONS_STAFF;
    } else if (resolvedRole === ROLE_CEO) {
      activeSections = SIDEBAR_SECTIONS_CEO;
    } else if (resolvedRole === ROLE_AFFILIATE) {
      activeSections = SIDEBAR_SECTIONS_AFFILIATE;
    } else {
      activeSections = SIDEBAR_SECTIONS_USER;
    }

    const sections = activeSections
      .map((section) => {
        const items = section.ids
          .filter((id) => effectiveViewIds.has(id) && viewMap.has(id))
          .map((id) => {
            used.add(id);
            const { label } = formatNavLabel(viewMap.get(id));
            return { id, label };
          });

        return items.length ? { section: section.section, items } : null;
      })
      .filter(Boolean);

    // No "remaining" dump — unlisted views are intentionally hidden
    const remaining = views
      .filter((item) => false) // intentionally empty — clean sidebar only
      .map((item) => {
        const { icon, label } = formatNavLabel(item.label);
        return { id: item.id, icon, label };
      });

    if (remaining.length) {
      sections.push({ section: 'More', items: remaining });
    }

    return sections;
  }, [effectiveViewIds]);

  const selectedClient = useMemo(
    () => clients.find((client) => `${client.id}` === `${selectedClientId}`) || null,
    [clients, selectedClientId]
  );

  const resolveClientName = useCallback(
    (clientId) => clients.find((client) => `${client.id}` === `${clientId}`)?.business_name || `Client ${clientId}`,
    [clients]
  );

  const resolveUserName = useCallback(
    (userId) => adminUsers.find((user) => `${user.id}` === `${userId}`)?.full_name || `User ${userId}`,
    [adminUsers]
  );

  const callsByClient = useMemo(() => {
    if (!selectedClient) return [];
    return calls.filter((call) => call.client_id === selectedClient.id);
  }, [calls, selectedClient]);

  useEffect(() => {
    // Lock to dark mode — the purple/blue premium theme
    document.documentElement.removeAttribute('data-theme');
  }, []);

  useEffect(() => {
    if (!authUser) return;
    setSalesChatProfile((prev) => ({
      name: prev.name || authUser.full_name || '',
      email: prev.email || authUser.email || '',
      phone: prev.phone || authUser.phone || '',
    }));
  }, [authUser]);

  // Lock non-admin users to their own client (covers page refresh + token restore)
  useEffect(() => {
    if (authUser && !isStaffRole(normalizeRole(getForcedRole(authUser)))) {
      setSelectedClientId(`${authUser.client_id}`);
    }
  }, [authUser, getForcedRole]);

  // Initialize currency from localStorage or detect from browser locale
  useEffect(() => {
    try {
      const savedCurrency = localStorage.getItem('preferredCurrency') || detectDefaultCurrency();
      setSelectedCurrency(savedCurrency);
      setDealForm((prev) => ({ ...prev, currency: savedCurrency }));
    } catch (error) {
      console.warn('Failed to initialize currency:', error);
    }
  }, []);

  // Save currency preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('preferredCurrency', selectedCurrency);
    } catch (error) {
      console.warn('Failed to save currency preference:', error);
    }
  }, [selectedCurrency]);

  useEffect(() => () => {
    if (voiceRecordingUrl) {
      URL.revokeObjectURL(voiceRecordingUrl);
    }
  }, [voiceRecordingUrl]);

  const formattedStructuredData = useMemo(() => {
    if (!selectedCall?.structured_data_json) {
      return 'No structured data captured yet.';
    }
    try {
      const parsed = JSON.parse(selectedCall.structured_data_json);
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      return selectedCall.structured_data_json;
    }
  }, [selectedCall]);

  const latestCalls = useMemo(() => calls.slice(0, 5), [calls]);

  const viewLabelMap = useMemo(
    () => new Map(views.map((item) => [item.id, item.label])),
    []
  );
  const currentViewLabel = viewLabelMap.get(view) || 'Dashboard';
  const primaryAreaLabel = resolvedRole === ROLE_USER ? 'Client Workspace' : 'Ops Console';

  const activityTimeline = useMemo(() => {
    const items = [];
    latestCalls.forEach((call) => {
      items.push({
        id: `call-${call.id}`,
        title: `Call #${call.id} recorded`,
        detail: call.caller_number || 'Unknown caller',
        timestamp: call.start_time || new Date().toISOString(),
        type: 'call',
      });
    });
    return items.slice(0, 6);
  }, [latestCalls]);

  const handleSidebarResize = useCallback((nextWidth) => {
    const clamped = Math.min(360, Math.max(220, Math.round(nextWidth)));
    setSidebarWidth(clamped);
  }, []);

  const openConfirm = useCallback((options) => {
    setConfirmDialog({
      open: true,
      title: options.title,
      detail: options.detail,
      confirmLabel: options.confirmLabel || 'Confirm',
      onConfirm: options.onConfirm,
    });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const sidebarCollapsedWidth = 72;
  const effectiveSidebarWidth = sidebarCollapsed ? sidebarCollapsedWidth : sidebarWidth;

  const auditLog = AUDIT_LOG;

  const pushToast = useCallback((type, message) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((prev) => [...prev, { id, type, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3500);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showNotice = useCallback((type, message) => {
    setNotice({ type, message });
    pushToast(type, message);
    window.setTimeout(() => setNotice(null), 4000);
  }, [pushToast]);

  const loadCurrentUser = useCallback(async (tokenOverride) => {
    const token = tokenOverride || localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token') || authToken;
    try {
      const response = await fetch('/auth/me');
      if (!response.ok) {
        if (!token) {
          return null;
        }
        throw new Error('Failed to load user');
      }
      const data = await response.json();
      setAuthUser(data);
      setAuthRole(data.role);
      localStorage.setItem('oakdesk_role', data.role);
      if (!token) {
        setAuthToken(AUTH_DISABLED_TOKEN);
      }
      return data;
    } catch (error) {
      if (token && token !== AUTH_DISABLED_TOKEN) {
        localStorage.removeItem('oakdesk_token');
        sessionStorage.removeItem('oakdesk_token');
      }
      localStorage.removeItem('oakdesk_role');
      setAuthToken('');
      setAuthRole('');
      setAuthUser(null);
      return null;
    }
  }, [authToken]);

  const handleNavigate = useCallback(async (targetView) => {
    if (!isAuthenticated) {
      const resolvedUser = await loadCurrentUser();
      if (resolvedUser) {
        const role = normalizeRole(resolvedUser.role || authRole);
        const roleViews = getAllowedViewIds(role);
        const resolvedView = roleViews.has(targetView)
          ? targetView
          : getDefaultViewForRole(role);
        setView(resolvedView);
        return;
      }
    }
    if (isAuthenticated) {
      if (!allowedViewIds.has(targetView) && targetView !== 'landing' && targetView !== 'auth') {
        setView(getDefaultViewForRole(resolvedRole));
        return;
      }
      setView(targetView);
      return;
    }
    setPostAuthView(targetView);
    setAuthMode('login');
    setView('auth');
  }, [allowedViewIds, authRole, isAuthenticated, loadCurrentUser, resolvedRole]);

  const loadAdminUsers = useCallback(async () => {
    setStatus((prev) => ({ ...prev, adminUsers: 'loading' }));
    try {
      const response = await fetch('/admin/users');
      if (!response.ok) {
        throw new Error('Failed to load users');
      }
      const data = await parseJsonResponse(response, 'Access data returned invalid response.');
      setAdminUsers(data);
      setStatus((prev) => ({ ...prev, adminUsers: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, adminUsers: 'error' }));
      showNotice('error', error.message || 'User load failed');
    }
  }, [showNotice]);

  const loadAdminInvites = useCallback(async () => {
    setStatus((prev) => ({ ...prev, adminInvites: 'loading' }));
    try {
      const response = await fetch('/admin/invites');
      if (!response.ok) {
        throw new Error('Failed to load invites');
      }
      const data = await parseJsonResponse(response, 'Invite data returned invalid response.');
      setAdminInvites(data);
      setStatus((prev) => ({ ...prev, adminInvites: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, adminInvites: 'error' }));
      showNotice('error', error.message || 'Invite load failed');
    }
  }, [showNotice]);

  const loadClientNumbers = useCallback(async (clientId) => {
    setStatus((prev) => ({ ...prev, channelNumbers: 'loading' }));
    try {
      const query = clientId ? `?client_id=${clientId}` : '';
      const response = await fetch(`/admin/channels/numbers${query}`);
      if (!response.ok) {
        throw new Error('Failed to load numbers');
      }
      const data = await parseJsonResponse(response, 'Number data returned invalid response.');
      setClientNumbers(data);
      setStatus((prev) => ({ ...prev, channelNumbers: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, channelNumbers: 'error' }));
      showNotice('error', error.message || 'Number load failed');
    }
  }, [showNotice]);

  const loadPoolDids = useCallback(async () => {
    try {
      const response = await fetch('/admin/sip/dids?status_filter=available');
      if (!response.ok) {
        throw new Error('Failed to load DID pool');
      }
      const data = await parseJsonResponse(response, 'Pool DID data returned invalid response.');
      setPoolDids(data);
    } catch (error) {
      showNotice('error', error.message || 'DID pool load failed');
    }
  }, [showNotice]);

  const handleClaimFromPool = useCallback(async () => {
    const clientId = numberForm.client_id || selectedClientId;
    if (!selectedPoolDidId || !clientId) {
      showNotice('error', 'Pick a client and a pool DID first.');
      return;
    }
    try {
      const response = await fetch(`/admin/channels/numbers/${selectedPoolDidId}/claim-from-pool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: Number(clientId) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatErrorDetail(data.detail) || 'Failed to assign DID from pool');
      }
      setSelectedPoolDidId('');
      await Promise.all([loadClientNumbers(clientId), loadPoolDids()]);
      if (data.reloaded) {
        showNotice('success', `DID ${data.did} assigned from pool and synced to Asterisk.`);
      } else {
        showNotice('error', data.detail || 'DID assigned, but Asterisk sync needs attention.');
      }
    } catch (error) {
      showNotice('error', error.message || 'DID assignment from pool failed');
    }
  }, [loadClientNumbers, loadPoolDids, numberForm.client_id, selectedClientId, selectedPoolDidId, showNotice]);

  const loadClientInboxes = useCallback(async (clientId) => {
    setStatus((prev) => ({ ...prev, channelInboxes: 'loading' }));
    try {
      const query = clientId ? `?client_id=${clientId}` : '';
      const response = await fetch(`/admin/channels/inboxes${query}`);
      if (!response.ok) {
        throw new Error('Failed to load inboxes');
      }
      const data = await parseJsonResponse(response, 'Inbox data returned invalid response.');
      setClientInboxes(data);
      setStatus((prev) => ({ ...prev, channelInboxes: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, channelInboxes: 'error' }));
      showNotice('error', error.message || 'Inbox load failed');
    }
  }, [showNotice]);

  const handleCreateNumber = useCallback(async (event) => {
    event.preventDefault();
    const clientId = numberForm.client_id || selectedClientId;
    if (!clientId || !numberForm.phone_number) {
      showNotice('error', 'Client ID and phone number are required.');
      return;
    }
    setStatus((prev) => ({ ...prev, channelNumberCreate: 'loading' }));
    try {
      const providerMeta = {};
      if (numberForm.channel === 'whatsapp') {
        if (numberForm.access_token) providerMeta.access_token = numberForm.access_token.trim();
        if (numberForm.phone_number_id) providerMeta.phone_number_id = numberForm.phone_number_id.trim();
      } else if (['facebook', 'instagram'].includes(numberForm.channel)) {
        if (numberForm.access_token) providerMeta.access_token = numberForm.access_token.trim();
        if (numberForm.page_id) providerMeta.page_id = numberForm.page_id.trim();
      }
      const payload = {
        client_id: Number(clientId),
        channel: numberForm.channel,
        phone_number: numberForm.phone_number.trim(),
        label: numberForm.label || undefined,
        provider: numberForm.provider || undefined,
        provider_meta: Object.keys(providerMeta).length ? JSON.stringify(providerMeta) : undefined,
      };
      const response = await fetch('/admin/channels/numbers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(formatErrorDetail(errorData.detail) || 'Number create failed');
      }
      setNumberForm({ client_id: clientId, channel: 'voice', phone_number: '', label: '', provider: '', access_token: '', phone_number_id: '', page_id: '' });
      await loadClientNumbers(clientId);
      setStatus((prev) => ({ ...prev, channelNumberCreate: 'success' }));
      showNotice('success', 'Number added');
    } catch (error) {
      setStatus((prev) => ({ ...prev, channelNumberCreate: 'error' }));
      showNotice('error', error.message || 'Number create failed');
    }
  }, [loadClientNumbers, numberForm, selectedClientId, showNotice]);

  const handleUpdateNumber = useCallback(async (numberId, updates) => {
    try {
      const response = await fetch(`/admin/channels/numbers/${numberId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(formatErrorDetail(errorData.detail) || 'Number update failed');
      }
      await loadClientNumbers(selectedClientId);
      showNotice('success', 'Number updated');
    } catch (error) {
      showNotice('error', error.message || 'Number update failed');
    }
  }, [loadClientNumbers, selectedClientId, showNotice]);

  const handleAssignDid = useCallback(async (number) => {
    if (!number || number.channel !== 'voice') {
      showNotice('error', 'DID assignment is only available for voice numbers.');
      return;
    }

    try {
      const response = await fetch(`/admin/channels/numbers/${number.id}/assign-did`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: 'from-provider', did: number.phone_number }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(formatErrorDetail(data.detail) || 'Failed to assign DID');
      }

      await loadClientNumbers(selectedClientId);
      if (data.reloaded) {
        showNotice('success', `DID ${data.did} assigned and synced to Asterisk.`);
      } else {
        showNotice('error', data.detail || 'DID saved, but Asterisk sync needs attention.');
      }
    } catch (error) {
      showNotice('error', error.message || 'DID assignment failed');
    }
  }, [loadClientNumbers, selectedClientId, showNotice]);

  const handleCreateInbox = useCallback(async (event) => {
    event.preventDefault();
    const clientId = inboxForm.client_id || selectedClientId;
    if (!clientId || !inboxForm.email_address) {
      showNotice('error', 'Client ID and email address are required.');
      return;
    }
    setStatus((prev) => ({ ...prev, channelInboxCreate: 'loading' }));
    try {
      const payload = {
        client_id: Number(clientId),
        email_address: inboxForm.email_address.trim().toLowerCase(),
        sender_name: inboxForm.sender_name || undefined,
        provider: inboxForm.provider || undefined,
      };
      const response = await fetch('/admin/channels/inboxes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(formatErrorDetail(errorData.detail) || 'Inbox create failed');
      }
      setInboxForm({ client_id: clientId, email_address: '', sender_name: '', provider: '' });
      await loadClientInboxes(clientId);
      setStatus((prev) => ({ ...prev, channelInboxCreate: 'success' }));
      showNotice('success', 'Inbox added');
    } catch (error) {
      setStatus((prev) => ({ ...prev, channelInboxCreate: 'error' }));
      showNotice('error', error.message || 'Inbox create failed');
    }
  }, [inboxForm, loadClientInboxes, selectedClientId, showNotice]);

  const handleUpdateInbox = useCallback(async (inboxId, updates) => {
    try {
      const response = await fetch(`/admin/channels/inboxes/${inboxId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(formatErrorDetail(errorData.detail) || 'Inbox update failed');
      }
      await loadClientInboxes(selectedClientId);
      showNotice('success', 'Inbox updated');
    } catch (error) {
      showNotice('error', error.message || 'Inbox update failed');
    }
  }, [loadClientInboxes, selectedClientId, showNotice]);

  const handleInviteCreate = useCallback(async (event) => {
    event.preventDefault();
    if (!inviteForm.full_name || !inviteForm.email || !inviteForm.organisation_name) {
      showNotice('error', 'Full name, email, and organisation name are required.');
      return;
    }
    setStatus((prev) => ({ ...prev, inviteCreate: 'loading' }));
    try {
      const payload = {
        full_name: inviteForm.full_name,
        email: inviteForm.email,
        role: inviteForm.role,
        organisation_name: inviteForm.organisation_name,
      };
      const response = await fetch('/admin/invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create invite');
      }
      const created = await response.json();
      if (created.invite_link) {
        showNotice('success', `Invite created. Link: ${created.invite_link}`);
      } else {
        showNotice('success', 'Invite sent');
      }
      setInviteForm({ full_name: '', email: '', role: 'customer', organisation_name: '' });
      await loadAdminInvites();
      setStatus((prev) => ({ ...prev, inviteCreate: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, inviteCreate: 'error' }));
      showNotice('error', error.message || 'Invite failed');
    }
  }, [inviteForm, loadAdminInvites, showNotice]);

  const handleInviteRevoke = useCallback(async (inviteId) => {
    try {
      const response = await fetch(`/admin/invites/${inviteId}`, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to revoke invite');
      }
      await loadAdminInvites();
      showNotice('success', 'Invite revoked');
    } catch (error) {
      showNotice('error', error.message || 'Invite revoke failed');
    }
  }, [loadAdminInvites, showNotice]);

  const requestPasswordReset = useCallback(async () => {
    if (!authResetEmail) {
      setAuthNotice('Email is required.');
      return;
    }
    try {
      const response = await fetch('/auth/password-reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authResetEmail }),
      });
      if (!response.ok) {
        throw new Error('Request failed');
      }
      setAuthNoticeType('success');
      setAuthNotice('If that email exists, a reset link has been sent.');
    } catch (error) {
      setAuthNoticeType('error');
      setAuthNotice('Password reset request failed.');
    }
  }, [authResetEmail]);

  const confirmPasswordReset = useCallback(async () => {
    if (!authFlowToken || !authNewPassword) {
      setAuthNotice('Reset token and new password are required.');
      return;
    }
    try {
      const response = await fetch('/auth/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: authFlowToken, password: authNewPassword }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Reset failed');
      }
      setAuthMode('login');
      setAuthNoticeType('success');
      setAuthNotice('Password updated. Please log in.');
      setAuthFlowToken('');
      setAuthNewPassword('');
    } catch (error) {
      setAuthNoticeType('error');
      setAuthNotice(error.message || 'Reset failed');
    }
  }, [authFlowToken, authNewPassword]);

  const acceptInvite = useCallback(async () => {
    if (!authFlowToken || !authNewPassword) {
      setAuthNotice('Invite token and password are required.');
      return;
    }
    try {
      const response = await fetch('/auth/invite/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: authFlowToken, password: authNewPassword }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Invite failed');
      }
      setAuthMode('login');
      setAuthNoticeType('success');
      setAuthNotice('Invite accepted. Please log in.');
      setAuthFlowToken('');
      setAuthNewPassword('');
    } catch (error) {
      setAuthNoticeType('error');
      setAuthNotice(error.message || 'Invite failed');
    }
  }, [authFlowToken, authNewPassword]);

  const verifyEmail = useCallback(async (token) => {
    try {
      const response = await fetch('/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!response.ok) {
        throw new Error('Verification failed');
      }
      setAuthNoticeType('success');
      setAuthNotice('Email verified. You can log in now.');
    } catch (error) {
      setAuthNoticeType('error');
      setAuthNotice('Email verification failed.');
    }
  }, []);

  const loadAiInsights = useCallback(async (clientId) => {
    if (!clientId) return;
    setStatus((prev) => ({ ...prev, aiInsights: 'loading' }));
    try {
      const response = await fetch(`/analytics/ai-insights/${clientId}?days=30`);
      if (!response.ok) {
        throw new Error('Failed to load AI insights');
      }
      const data = await response.json();
      setAiInsights(data);
      setStatus((prev) => ({ ...prev, aiInsights: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, aiInsights: 'error' }));
      showNotice('error', error.message || 'AI insights failed');
    }
  }, [showNotice]);

  const loadBillingUsage = useCallback(async (clientId, windowDays = billingWindowDays) => {
    if (!clientId) return;
    setStatus((prev) => ({ ...prev, billing: 'loading' }));
    try {
      const response = await fetch(`/billing/usage/${clientId}?days=${windowDays}`);
      if (!response.ok) {
        throw new Error('Failed to load billing usage');
      }
      const data = await response.json();
      setBillingUsage(data);
      setBillingForm({
        monthly_call_limit: data.limits.monthly_call_limit ?? '',
        monthly_whatsapp_limit: data.limits.monthly_whatsapp_limit ?? '',
        monthly_email_limit: data.limits.monthly_email_limit ?? '',
        monthly_message_limit: data.limits.monthly_message_limit ?? '',
        monthly_seat_limit: data.limits.monthly_seat_limit ?? '',
      });
      setStatus((prev) => ({ ...prev, billing: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, billing: 'error' }));
      showNotice('error', error.message || 'Billing load failed');
    }
  }, [billingWindowDays, showNotice]);

  const handleBillingUpdate = useCallback(async (event) => {
    event.preventDefault();
    if (!selectedClientId) {
      showNotice('error', 'Select a client first.');
      return;
    }
    setStatus((prev) => ({ ...prev, billingUpdate: 'loading' }));
    try {
      const payload = {
        monthly_call_limit: billingForm.monthly_call_limit === '' ? null : Number(billingForm.monthly_call_limit),
        monthly_whatsapp_limit: billingForm.monthly_whatsapp_limit === '' ? null : Number(billingForm.monthly_whatsapp_limit),
        monthly_email_limit: billingForm.monthly_email_limit === '' ? null : Number(billingForm.monthly_email_limit),
        monthly_message_limit: billingForm.monthly_message_limit === '' ? null : Number(billingForm.monthly_message_limit),
        monthly_seat_limit: billingForm.monthly_seat_limit === '' ? null : Number(billingForm.monthly_seat_limit),
      };
      const response = await fetch(`/billing/usage/${selectedClientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Billing update failed');
      }
      showNotice('success', 'Limits updated');
      await loadBillingUsage(selectedClientId, billingWindowDays);
      setStatus((prev) => ({ ...prev, billingUpdate: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, billingUpdate: 'error' }));
      showNotice('error', error.message || 'Billing update failed');
    }
  }, [billingForm, billingWindowDays, loadBillingUsage, selectedClientId, showNotice]);

  const handleAdminUserCreate = useCallback(async (event) => {
    event.preventDefault();
    setStatus((prev) => ({ ...prev, adminUserCreate: 'loading' }));
    try {
      const payload = {
        full_name: adminUserForm.full_name,
        email: adminUserForm.email,
        password: adminUserForm.password,
        role: adminUserForm.role,
        client_id: Number(adminUserForm.client_id),
      };
      const response = await fetch('/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create user');
      }
      showNotice('success', 'User created');
      setAdminUserForm({ full_name: '', email: '', password: '', role: 'customer', client_id: '' });
      await loadAdminUsers();
      setStatus((prev) => ({ ...prev, adminUserCreate: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, adminUserCreate: 'error' }));
      showNotice('error', error.message || 'User create failed');
    }
  }, [adminUserForm, loadAdminUsers, showNotice]);

  const handleAdminUserUpdate = useCallback(async (userId, updates) => {
    try {
      const response = await fetch(`/admin/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update user');
      }
      await loadAdminUsers();
      showNotice('success', 'User updated');
    } catch (error) {
      showNotice('error', error.message || 'User update failed');
    }
  }, [loadAdminUsers, showNotice]);

  const generateInboxSummary = useCallback(async () => {
    if (!selectedInboxMessage) return;
    if (!selectedInboxMessage.object?.id) {
      showNotice('error', 'Conversation details are missing.');
      return;
    }
    setInboxSummaryStatus('loading');
    try {
      const payload = {
        channel: selectedInboxMessage.type,
        conversation_id: selectedInboxMessage.object?.id,
        client_id: selectedInboxMessage.object?.client_id || Number(selectedClientId),
      };
      const response = await fetch('/ai/summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Summary failed');
      }
      const data = await response.json();
      setInboxSummary(data);
      setInboxSummaryStatus('success');
    } catch (error) {
      setInboxSummaryStatus('error');
      showNotice('error', error.message || 'Summary failed');
    }
  }, [selectedInboxMessage, selectedClientId, showNotice]);

  const loadHandoffStatus = useCallback(async (msg) => {
    if (!msg?.object?.id) return;
    try {
      const convType = msg.type;
      const convId = String(msg.object.id);
      const res = await fetch(`/handoff/status?conversation_type=${convType}&conversation_id=${convId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token')}` },
      });
      if (res.ok) setHandoffStatus(await res.json());
    } catch (_) {}
  }, []);

  const loadHandoffQueue = useCallback(async () => {
    try {
      const res = await fetch('/handoff/queue', {
        headers: { Authorization: `Bearer ${localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token')}` },
      });
      if (res.ok) setHandoffQueue((await res.json()).items || []);
    } catch (_) {}
  }, []);

  const requestHandoff = useCallback(async (msg) => {
    if (!msg?.object?.id) return;
    setHandoffLoading(true);
    try {
      const res = await fetch('/handoff/queue', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token')}`,
        },
        body: JSON.stringify({
          conversation_type: msg.type,
          conversation_id: String(msg.object.id),
          triggered_by: 'manual',
        }),
      });
      if (res.ok) {
        await loadHandoffStatus(msg);
        await loadHandoffQueue();
      }
    } catch (_) {}
    setHandoffLoading(false);
  }, [loadHandoffStatus, loadHandoffQueue]);

  const claimHandoff = useCallback(async (handoffId, msg) => {
    setHandoffLoading(true);
    try {
      const res = await fetch(`/handoff/claim/${handoffId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token')}` },
      });
      if (res.ok) {
        await loadHandoffStatus(msg);
        await loadHandoffQueue();
      }
    } catch (_) {}
    setHandoffLoading(false);
  }, [loadHandoffStatus, loadHandoffQueue]);

  const releaseHandoff = useCallback(async (handoffId, msg) => {
    setHandoffLoading(true);
    try {
      const res = await fetch(`/handoff/release/${handoffId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token')}` },
      });
      if (res.ok) {
        setHandoffStatus({ status: 'ai', handoff: null });
        await loadHandoffQueue();
      }
    } catch (_) {}
    setHandoffLoading(false);
  }, [loadHandoffQueue]);

  const loadClients = useCallback(async () => {
    setStatus((prev) => ({ ...prev, clients: 'loading' }));
    try {
      const response = await fetch('/clients');
      if (response.status === 401 || response.status === 403) {
        setStatus((prev) => ({ ...prev, clients: 'idle' }));
        return;
      }
      const data = await parseJsonResponse(response, 'Failed to parse clients response.');
      if (!response.ok) {
        throw new Error(formatErrorDetail(data?.detail) || 'Failed to load clients.');
      }
      const resolvedClients = Array.isArray(data) ? data : Array.isArray(data?.clients) ? data.clients : [];
      setClients(resolvedClients);
      setStatus((prev) => ({ ...prev, clients: 'success' }));
      if (resolvedClients.length) {
        setSelectedClientId((prev) => prev || `${resolvedClients[0].id}`);
      }
    } catch (error) {
      setStatus((prev) => ({ ...prev, clients: 'error' }));
      showNotice('error', error.message || 'Client fetch failed.');
    }
  }, [showNotice]);

  const loadCalls = useCallback(async () => {
    setStatus((prev) => ({ ...prev, calls: 'loading' }));
    try {
      const response = await fetch('/calls');
      if (response.status === 401 || response.status === 403) {
        setStatus((prev) => ({ ...prev, calls: 'idle' }));
        return;
      }
      const data = await parseJsonResponse(response, 'Failed to parse calls response.');
      if (!response.ok) {
        throw new Error(formatErrorDetail(data?.detail) || 'Failed to load calls.');
      }
      const resolvedCalls = Array.isArray(data) ? data : Array.isArray(data?.calls) ? data.calls : [];
      setCalls(resolvedCalls);
      setStatus((prev) => ({ ...prev, calls: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, calls: 'error' }));
      showNotice('error', error.message || 'Call fetch failed.');
    }
  }, [showNotice]);

  const loadCallDetail = async (callId) => {
    if (!callId) return;
    setStatus((prev) => ({ ...prev, call: 'loading' }));
    try {
      const response = await fetch(`/calls/${callId}`);
      if (!response.ok) {
        throw new Error('Failed to load call detail.');
      }
      const data = await response.json();
      setSelectedCall(data);
      setStatus((prev) => ({ ...prev, call: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, call: 'error' }));
      showNotice('error', error.message || 'Call detail failed.');
    }
  };

  const handleCreateClient = async (event) => {
    event.preventDefault();
    if (!clientForm.business_name || !clientForm.phone_number || !clientForm.ai_prompt) {
      showNotice('error', 'Business name, phone number, and AI prompt are required.');
      return;
    }
    setStatus((prev) => ({ ...prev, create: 'loading' }));
    try {
      const payload = {
        ...clientForm,
        notification_email: clientForm.notification_email || null,
        business_hours: clientForm.business_hours || null,
      };
      const response = await fetch('/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('Failed to create client.');
      }
      const created = await response.json();
      setClients((prev) => [created, ...prev]);
      setClientForm(emptyClient);
      setStatus((prev) => ({ ...prev, create: 'success' }));
      showNotice('success', 'Client created.');
    } catch (error) {
      setStatus((prev) => ({ ...prev, create: 'error' }));
      showNotice('error', error.message || 'Client creation failed.');
    }
  };

  const runHealthCheck = async () => {
    if (!isStaffRole(resolvedRole)) {
      showNotice('error', 'Health checks are restricted to admins and developers.');
      return;
    }
    setStatus((prev) => ({ ...prev, health: 'loading' }));
    try {
      const [clientsRes, callsRes] = await Promise.all([fetch('/clients'), fetch('/calls')]);
      if (!clientsRes.ok || !callsRes.ok) {
        throw new Error('Health check failed.');
      }
      const [clientData, callData] = await Promise.all([clientsRes.json(), callsRes.json()]);
      setHealthSummary({
        time: new Date().toLocaleTimeString(),
        clients: clientData.length,
        calls: callData.length,
      });
      setStatus((prev) => ({ ...prev, health: 'success' }));
      showNotice('success', 'Health check passed.');
    } catch (error) {
      setStatus((prev) => ({ ...prev, health: 'error' }));
      showNotice('error', error.message || 'Health check failed.');
    }
  };

  const startVoiceRecording = async () => {
    if (voiceRecording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      showNotice('error', 'Microphone access is not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      voiceChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          voiceChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blobType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(voiceChunksRef.current, { type: blobType });
        const file = new File([blob], `mic-test-${Date.now()}.webm`, { type: blobType });
        stream.getTracks().forEach((track) => track.stop());

        if (voiceRecordingUrl) {
          URL.revokeObjectURL(voiceRecordingUrl);
        }
        setVoiceRecordingUrl(URL.createObjectURL(blob));
        setVoiceTestFile(file);
        setVoiceTestResult(null);
        setVoiceRecording(false);
        showNotice('success', 'Recording captured. Ready to transcribe.');
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setVoiceRecording(true);
      showNotice('success', 'Recording started. Speak into your mic.');
    } catch (error) {
      showNotice('error', error.message || 'Microphone access failed.');
    }
  };

  const stopVoiceRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.stop();
  };

  const handleVoiceTestSubmit = async (event) => {
    event.preventDefault();
    if (!voiceTestFile) {
      showNotice('error', 'Select an audio file to transcribe.');
      return;
    }
    setStatus((prev) => ({ ...prev, voiceTest: 'loading' }));
    setVoiceTestResult(null);
    try {
      const formData = new FormData();
      formData.append('audio', voiceTestFile);
      formData.append('voice', selectedVoice);
      const response = await fetch('/test/transcribe-audio', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatErrorDetail(data.detail) || 'Voice test failed.');
      }
      setVoiceTestResult(data);
      setStatus((prev) => ({ ...prev, voiceTest: 'success' }));
      showNotice('success', 'Transcription completed.');
    } catch (error) {
      setStatus((prev) => ({ ...prev, voiceTest: 'error' }));
      showNotice('error', error.message || 'Voice test failed.');
    }
  };

  const loadSalesChatConversations = useCallback(async () => {
    setStatus((prev) => ({ ...prev, salesChat: 'loading' }));
    try {
      const query = salesChatActiveOnly ? '?active_only=true' : '';
      const response = await fetch(`/chat/conversations${query}`);
      if (!response.ok) {
        throw new Error('Failed to load sales conversations.');
      }
      const data = await response.json();
      const conversations = Array.isArray(data) ? data : [];
      setSalesChatConversations(conversations);
      setSelectedSalesConversation((prev) => {
        if (prev && conversations.some((conv) => conv.id === prev.id)) {
          return conversations.find((conv) => conv.id === prev.id) || prev;
        }
        return conversations[0] || null;
      });
      setStatus((prev) => ({ ...prev, salesChat: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, salesChat: 'error' }));
      showNotice('error', error.message || 'Sales chat load failed.');
    }
  }, [salesChatActiveOnly, showNotice]);

  const loadSalesChatMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    setStatus((prev) => ({ ...prev, salesChatMessages: 'loading' }));
    try {
      const response = await fetch(`/chat/conversations/${conversationId}/messages`);
      if (!response.ok) {
        throw new Error('Failed to load messages.');
      }
      const data = await response.json();
      setSalesChatMessages(Array.isArray(data) ? data : []);
      setStatus((prev) => ({ ...prev, salesChatMessages: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, salesChatMessages: 'error' }));
      showNotice('error', error.message || 'Message load failed.');
    }
  }, [showNotice]);

  const ensureSalesChatConversation = useCallback(async () => {
    if (selectedSalesConversation) return selectedSalesConversation;
    if (!authUser) return null;

    const payload = {
      visitor_name: salesChatProfile.name || authUser.full_name || '',
      visitor_email: salesChatProfile.email || authUser.email || '',
      visitor_phone: salesChatProfile.phone || '',
      extra_data: { user_id: authUser.id },
    };

    const response = await fetch('/chat/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(formatErrorDetail(data.detail) || 'Failed to start sales chat.');
    }
    setSalesChatConversations((prev) => [data, ...prev]);
    setSelectedSalesConversation(data);
    return data;
  }, [authUser, salesChatProfile, selectedSalesConversation]);

  const handleSalesChatSelect = useCallback(async (conversation) => {
    setSelectedSalesConversation(conversation);
    await loadSalesChatMessages(conversation?.id);
  }, [loadSalesChatMessages]);

  const handleSalesChatSend = useCallback(async (event) => {
    event.preventDefault();
    const message = salesChatInput.trim();
    if (!message) return;
    setStatus((prev) => ({ ...prev, salesChatSend: 'loading' }));
    try {
      const conversation = selectedSalesConversation || (isAdmin ? null : await ensureSalesChatConversation());
      if (!conversation) {
        throw new Error('Select a conversation to reply.');
      }
      const payload = {
        conversation_id: conversation.id,
        message_type: 'text',
        sender_type: isAdmin ? 'agent' : 'visitor',
        message,
      };
      const response = await fetch(`/chat/conversations/${conversation.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatErrorDetail(data.detail) || 'Message send failed.');
      }
      setSalesChatMessages((prev) => [...prev, data]);
      setSalesChatInput('');
      setStatus((prev) => ({ ...prev, salesChatSend: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, salesChatSend: 'error' }));
      showNotice('error', error.message || 'Message send failed.');
    }
  }, [ensureSalesChatConversation, isAdmin, salesChatInput, selectedSalesConversation, showNotice]);

  const handleSalesChatAssign = useCallback(async (conversationId, assignedTo) => {
    if (!conversationId) return;
    setStatus((prev) => ({ ...prev, salesChatAssign: 'loading' }));
    try {
      const response = await fetch(`/chat/conversations/${conversationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: assignedTo ? Number(assignedTo) : null }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatErrorDetail(data.detail) || 'Assignment update failed.');
      }
      setSalesChatConversations((prev) =>
        prev.map((conv) => (conv.id === data.id ? { ...conv, assigned_to: data.assigned_to } : conv))
      );
      setSelectedSalesConversation((prev) => (prev?.id === data.id ? { ...prev, assigned_to: data.assigned_to } : prev));
      setStatus((prev) => ({ ...prev, salesChatAssign: 'success' }));
      showNotice('success', 'Sales rep assigned.');
    } catch (error) {
      setStatus((prev) => ({ ...prev, salesChatAssign: 'error' }));
      showNotice('error', error.message || 'Assignment update failed.');
    }
  }, [showNotice]);


  const loadContacts = async (clientId = null) => {
    setStatus((prev) => ({ ...prev, contacts: 'loading' }));
    try {
      const url = clientId ? `/crm/contacts?client_id=${clientId}` : '/crm/contacts';
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load contacts');
      const data = await response.json();
      setContacts(data);
      setStatus((prev) => ({ ...prev, contacts: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, contacts: 'error' }));
      showNotice('error', error.message || 'Failed to load contacts');
    }
  };

  const loadDeals = async (clientId = null) => {
    setStatus((prev) => ({ ...prev, deals: 'loading' }));
    try {
      const url = clientId ? `/crm/deals?client_id=${clientId}` : '/crm/deals';
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load deals');
      const data = await response.json();
      setDeals(data);
      setStatus((prev) => ({ ...prev, deals: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, deals: 'error' }));
      showNotice('error', error.message || 'Failed to load deals');
    }
  };

  const loadAnalytics = async (clientId, days = 30) => {
    setStatus((prev) => ({ ...prev, analytics: 'loading' }));
    try {
      // Try new enhanced endpoint first
      let response = await fetch(`/analytics/enhanced/dashboard/${clientId}?days=${days}`);
      if (!response.ok) {
        // Fallback to original endpoint
        response = await fetch(`/analytics/stats/${clientId}?days=${days}`);
      }
      if (!response.ok) throw new Error('Failed to load analytics');
      const data = await response.json();
      setAnalytics(data);
      setStatus((prev) => ({ ...prev, analytics: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, analytics: 'error' }));
      showNotice('error', error.message || 'Failed to load analytics');
    }
  };

  const handleCreateContact = async (event) => {
    event.preventDefault();
    if (!selectedClient) {
      showNotice('error', 'Please select a client first');
      return;
    }
    try {
      const response = await fetch(`/crm/contacts?client_id=${selectedClient.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      if (!response.ok) throw new Error('Failed to create contact');
      const contact = await response.json();
      setContacts((prev) => [contact, ...prev]);
      setContactForm({ full_name: '', phone: '', email: '', status: 'lead' });
      showNotice('success', 'Contact created');
    } catch (error) {
      showNotice('error', error.message || 'Create contact failed');
    }
  };

  const handleCallbackFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setCallbackForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCallbackSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const { full_name, phone, email, message } = callbackForm;

      if (!full_name.trim() || !phone.trim() || !email.trim()) {
        showNotice('error', 'Please complete name, number and email before submitting.');
        return;
      }

      const subject = encodeURIComponent('Callback request from Oakdesk landing page');
      const body = encodeURIComponent(
        `Name: ${full_name}\nPhone: ${phone}\nEmail: ${email}\n\nMessage:\n${message}`
      );

      setCallbackSubmitted(true);
      setCallbackForm({ full_name: '', phone: '', email: '', message: '' });
      showNotice('success', 'Thanks! A sales specialist will call you back shortly.');

      if (typeof window !== 'undefined') {
        window.location.href = `mailto:sales@oakdesk.ai?subject=${subject}&body=${body}`;
      }
    },
    [callbackForm, showNotice]
  );

  // Landing page handlers
  const handleLandingGetStarted = useCallback((email, referralCode) => {
    setAuthForm(prev => ({
      ...prev,
      email: email || '',
      referral_code: referralCode || '',
    }));
    setPostAuthView('');
    setAuthMode('signup');
    setView('auth');
  }, []);

  const handleRequestCallback = useCallback(async (formData) => {
    try {
      await fetch('/api/callbacks/public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
    } catch (_) {}
  }, []);

  const handleCreateDeal = async (event) => {
    event.preventDefault();
    if (!selectedClient) {
      showNotice('error', 'Please select a client first');
      return;
    }
    try {
      const response = await fetch(`/crm/deals?client_id=${selectedClient.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dealForm, value: parseFloat(dealForm.value) || null }),
      });
      if (!response.ok) throw new Error('Failed to create deal');
      const deal = await response.json();
      setDeals((prev) => [deal, ...prev]);
      setDealForm({ title: '', stage: 'new', value: '', probability: 50, currency: selectedCurrency });
      showNotice('success', 'Deal created');
    } catch (error) {
      showNotice('error', error.message || 'Create deal failed');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      const response = await fetch(`/crm/contacts/${contactId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete contact');
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
      showNotice('success', 'Contact deleted');
    } catch (error) {
      showNotice('error', error.message || 'Delete failed');
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Delete this deal?')) return;
    try {
      const response = await fetch(`/crm/deals/${dealId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete deal');
      setDeals((prev) => prev.filter((d) => d.id !== dealId));
      showNotice('success', 'Deal deleted');
    } catch (error) {
      showNotice('error', error.message || 'Delete failed');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      const response = await fetch(`/clients/${clientId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete client');
      setClients((prev) => prev.filter((c) => c.id !== clientId));
      showNotice('success', 'Client deleted');
    } catch (error) {
      showNotice('error', error.message || 'Delete failed');
    }
  };

  const loadWhatsappConversations = useCallback(async (clientId = selectedClientId) => {
    if (!clientId) return;
    
    setStatus((prev) => ({ ...prev, whatsapp: 'loading' }));
    try {
      const response = await fetch(`/whatsapp/conversations?client_id=${clientId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const error = await parseJsonResponse(response, 'WhatsApp returned an invalid error response.').catch(() => ({}));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      const data = await parseJsonResponse(response, 'WhatsApp returned invalid JSON.');
      setWhatsappConversations(data || []);
      setStatus((prev) => ({ ...prev, whatsapp: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, whatsapp: 'error' }));
      showNotice('error', error.message || 'Failed to load conversations');
    }
  }, [selectedClientId, showNotice]);

  const loadWhatsappConversation = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    try {
      setStatus((prev) => ({ ...prev, whatsappLoad: 'loading' }));
      const response = await fetch(`/whatsapp/conversations/${conversationId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const error = await parseJsonResponse(response, 'WhatsApp conversation returned invalid error response.').catch(() => ({}));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      const data = await parseJsonResponse(response, 'WhatsApp conversation returned invalid JSON.');
      setSelectedWAConversation(data);
      setStatus((prev) => ({ ...prev, whatsappLoad: 'success' }));
      
      // Auto-scroll to bottom
      setTimeout(() => {
        const messageList = document.querySelector('.message-list');
        if (messageList) {
          messageList.scrollTop = messageList.scrollHeight;
        }
      }, 100);
    } catch (error) {
      setStatus((prev) => ({ ...prev, whatsappLoad: 'error' }));
      showNotice('error', error.message || 'Failed to load conversation');
    }
  }, [showNotice]);

  const handleRetryWhatsappMessage = async (messageId, conversationId, content) => {
    await handleSendWhatsappMessage(null, conversationId, content, messageId);
  };

  const handleSendWhatsappMessage = async (event, conversationId = null, messageContent = null, retryMessageId = null) => {
    if (event) event.preventDefault();
    
    const targetConversation = conversationId || selectedWAConversation?.id;
    const finalContent = messageContent || waMessageInput;
    
    if (!targetConversation || !finalContent.trim()) return;
    
    // Validate message
    if (finalContent.length > 4096) {
      showNotice('error', 'Message is too long (max 4096 characters)');
      return;
    }
    
    const messageKey = `${targetConversation}-${Date.now()}`;
    setWaMessageSending((prev) => ({ ...prev, [messageKey]: true }));
    setStatus((prev) => ({ ...prev, whatsappSend: 'loading' }));
    
    try {
      const response = await fetch(`/whatsapp/conversations/${targetConversation}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: targetConversation,
          content: finalContent.trim(),
        }),
      });
      
      if (!response.ok) {
        const error = await parseJsonResponse(response, 'WhatsApp send returned invalid error response.').catch(() => ({}));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      const result = await parseJsonResponse(response, 'WhatsApp send returned invalid JSON.');
      
      if (result.success) {
        // Clear input and show success
        setWaMessageInput('');
        showNotice('success', `Message sent (${result.message_id})`);
        
        // Remove from failed set if was a retry
        if (retryMessageId) {
          setWaFailedMessages((prev) => {
            const newSet = new Set(prev);
            newSet.delete(retryMessageId);
            return newSet;
          });
        }
        
        // Reload conversation
        await new Promise(r => setTimeout(r, 500));
        await loadWhatsappConversation(targetConversation);
      } else {
        // Handle partial failure (message saved but send failed)
        if (result.message_id) {
          setWaFailedMessages((prev) => new Set(prev).add(result.message_id));
          showNotice('warning', `Message saved but failed to send: ${result.error}`);
        } else {
          throw new Error(result.error || 'Unknown error');
        }
      }
    } catch (error) {
      setWaMessageSending((prev) => ({ ...prev, [messageKey]: false }));
      setStatus((prev) => ({ ...prev, whatsappSend: 'error' }));
      showNotice('error', error.message || 'Failed to send message');
    } finally {
      setWaMessageSending((prev) => {
        const newState = { ...prev };
        delete newState[messageKey];
        return newState;
      });
      setStatus((prev) => ({ ...prev, whatsappSend: 'idle' }));
    }
  };

  // Auto-refresh WhatsApp conversations
  useEffect(() => {
    if (!waAutoRefreshEnabled || !selectedClientId) return;
    
    const interval = setInterval(() => {
      loadWhatsappConversations();
      if (selectedWAConversation?.id) {
        loadWhatsappConversation(selectedWAConversation.id);
      }
    }, waRefreshInterval);
    
    return () => clearInterval(interval);
  }, [waAutoRefreshEnabled, waRefreshInterval, selectedClientId, selectedWAConversation?.id, loadWhatsappConversations, loadWhatsappConversation]);


  // Load WhatsApp metrics
  const loadWhatsappMetrics = useCallback(async (clientList = []) => {
    try {
      const results = await Promise.all(
        clientList.map(async (client) => {
          try {
            const resp = await fetch(`/whatsapp/conversations?client_id=${client.id}`);
            const convs = resp.ok ? await parseJsonResponse(resp, '') : [];
            const list = Array.isArray(convs) ? convs : [];
            return {
              total: list.length,
              active: list.filter((c) => c.is_active).length,
              messages: list.reduce((sum, c) => sum + (c.message_count || 0), 0),
            };
          } catch (_) {
            return { total: 0, active: 0, messages: 0 };
          }
        })
      );
      setWhatsappMetrics({
        totalConversations: results.reduce((s, r) => s + r.total, 0),
        activeConversations: results.reduce((s, r) => s + r.active, 0),
        totalMessages: results.reduce((s, r) => s + r.messages, 0),
      });
    } catch (_) {
      // Failed to load metrics
    }
  }, []);

  // Email functions
  const loadEmailConversations = useCallback(async (clientId = selectedClientId) => {
    if (!clientId) return;
    
    setStatus((prev) => ({ ...prev, email: 'loading' }));
    try {
      const response = await fetch(`/email/conversations?client_id=${clientId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await parseJsonResponse(response, 'Email conversations returned invalid JSON.');
      setEmailConversations(data || []);
      setStatus((prev) => ({ ...prev, email: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, email: 'error' }));
    }
  }, [selectedClientId]);

  const loadEmailConversation = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    setStatus((prev) => ({ ...prev, emailLoad: 'loading' }));
    try {
      const response = await fetch(`/email/conversations/${conversationId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await parseJsonResponse(response, 'Email conversation returned invalid JSON.');
      setSelectedEmailConversation(data);
      setStatus((prev) => ({ ...prev, emailLoad: 'success' }));
    } catch (error) {
      setStatus((prev) => ({ ...prev, emailLoad: 'error' }));
    }
  }, []);

  const handleSendEmail = async (event) => {
    if (event) event.preventDefault();
    
    if (!selectedEmailConversation || !emailBody.trim() || !emailSubject.trim()) return;
    
    setStatus((prev) => ({ ...prev, emailSend: 'loading' }));
    try {
      const response = await fetch(`/email/conversations/${selectedEmailConversation.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: selectedEmailConversation.id,
          recipient_email: selectedEmailConversation.contact_email,
          subject: emailSubject,
          body_text: emailBody,
        }),
      });
      
      if (!response.ok) {
        const error = await parseJsonResponse(response, 'Email send returned invalid error response.').catch(() => ({}));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      // Clear form
      setEmailBody('');
      setEmailSubject('');
      
      // Reload conversation
      await loadEmailConversation(selectedEmailConversation.id);
      setStatus((prev) => ({ ...prev, emailSend: 'success' }));
      showNotice('success', 'Email sent successfully');
    } catch (error) {
      setStatus((prev) => ({ ...prev, emailSend: 'error' }));
      showNotice('error', error.message || 'Failed to send email');
    }
  };

  // Auto-refresh Email conversations
  useEffect(() => {
    if (!emailAutoRefreshEnabled || !selectedClientId) return;
    
    const interval = setInterval(() => {
      loadEmailConversations();
      if (selectedEmailConversation?.id) {
        loadEmailConversation(selectedEmailConversation.id);
      }
    }, emailRefreshInterval);
    
    return () => clearInterval(interval);
  }, [emailAutoRefreshEnabled, emailRefreshInterval, selectedClientId, selectedEmailConversation?.id, loadEmailConversations, loadEmailConversation]);

  // Check system status
  const checkSystemStatus = useCallback(async () => {
    try {
      const response = await fetch('/clients', { method: 'GET' });
      setSystemStatus(prev => ({ ...prev, backend: response.ok ? 'ok' : 'error' }));
    } catch (e) {
      setSystemStatus(prev => ({ ...prev, backend: 'error' }));
    }
  }, []);

  const loadAiProviders = useCallback(async () => {
    try {
      const response = await fetch('/api/ai/status');
      if (response.ok) {
        const data = await response.json();
        setCurrentAiProvider(data.current_provider);
        setAiIsMockMode(data.is_mock_mode);
        // Set available models from current provider
        const providerInfo = data.models_info[data.current_provider];
        if (providerInfo && providerInfo.models.length > 0) {
          setSelectedAiModel(providerInfo.models[0]);
        }
      }
      
      // Get full provider list
      const providersResponse = await fetch('/api/ai/providers');
      if (providersResponse.ok) {
        const providersData = await providersResponse.json();
        setAiProviders(providersData.providers || []);
      }
    } catch (e) {
      console.error('Failed to load AI providers:', e);
    }
  }, []);

  const handleSwitchAiProvider = async () => {
    if (!aiProviders.length) {
      showNotice('No providers loaded', 'error');
      return;
    }

    try {
      setStatus(prev => ({ ...prev, settings: 'loading' }));
      const response = await fetch('/api/ai/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: currentAiProvider,
          api_key: aiProviderApiKey,
          model: selectedAiModel
        })
      });

      if (response.ok) {
        await response.json();
        showNotice(`✓ Switched to ${currentAiProvider}`, 'success');
        setAiProviderApiKey('');
        await loadAiProviders();
      } else {
        showNotice('Failed to switch provider', 'error');
      }
    } catch (e) {
      console.error('Error switching provider:', e);
      showNotice('Error switching provider', 'error');
    } finally {
      setStatus(prev => ({ ...prev, settings: 'idle' }));
    }
  };

  const handleStartDemoSession = useCallback(async () => {
    setAiTestStatus('loading');
    try {
      const response = await fetch('/demo/session/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roleplay_company: demoRoleplayCompany.trim() || undefined,
          opening_message: aiTestPrompt.trim() || undefined,
          voice: selectedVoice,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatErrorDetail(data.detail) || 'Failed to start demo session');
      }
      setDemoSessionId(data.session_id || '');
      setDemoMessages(Array.isArray(data.messages) ? data.messages : []);
      setDemoSummary(data.summary || '');
      setDemoAudioUrl(data.audio_url || '');
      setAiTestStatus('success');
      showNotice('success', 'Demo session started. Continue the roleplay below.');
    } catch (error) {
      setAiTestStatus('error');
      showNotice('error', error.message || 'Failed to start demo session');
    }
  }, [aiTestPrompt, demoRoleplayCompany, selectedVoice, showNotice]);

  const handleSendDemoMessage = useCallback(async () => {
    const message = demoInput.trim();
    if (!demoSessionId) {
      showNotice('error', 'Start a demo session first.');
      return;
    }
    if (!message) {
      showNotice('error', 'Enter a message to continue the roleplay.');
      return;
    }

    setAiTestStatus('loading');
    try {
      const response = await fetch(`/demo/session/${demoSessionId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          voice: selectedVoice,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatErrorDetail(data.detail) || 'Demo message failed');
      }
      setDemoMessages(Array.isArray(data.messages) ? data.messages : []);
      setDemoSummary(data.summary || '');
      setDemoAudioUrl(data.audio_url || '');
      setDemoInput('');
      setAiTestStatus('success');
    } catch (error) {
      setAiTestStatus('error');
      showNotice('error', error.message || 'Demo message failed');
    }
  }, [demoInput, demoSessionId, selectedVoice, showNotice]);

  const handleLoadDemoSession = useCallback(async () => {
    const lookupId = demoLookupSessionId.trim();
    if (!lookupId) {
      showNotice('error', 'Enter a session ID to load.');
      return;
    }
    setAiTestStatus('loading');
    try {
      const response = await fetch(`/demo/session/${lookupId}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatErrorDetail(data.detail) || 'Failed to load demo session');
      }
      setDemoSessionId(data.session_id || lookupId);
      setDemoMessages(Array.isArray(data.messages) ? data.messages : []);
      setDemoSummary(data.summary || '');
      setDemoAudioUrl(data.audio_url || '');
      setDemoRoleplayCompany(data.roleplay_company || '');
      setAiTestStatus('success');
      showNotice('success', 'Demo session loaded.');
    } catch (error) {
      setAiTestStatus('error');
      showNotice('error', error.message || 'Failed to load demo session');
    }
  }, [demoLookupSessionId, showNotice]);

  const handleEndDemoSession = useCallback(() => {
    if (!demoSessionId) {
      showNotice('error', 'No active demo session to end.');
      return;
    }
    setDemoSessionId('');
    setDemoInput('');
    setAiTestStatus('idle');
    showNotice('success', 'Demo session ended. Transcript and summary remain saved on the backend.');
  }, [demoSessionId, showNotice]);

  const loadUnifiedInbox = useCallback(async () => {
    if (!selectedClientId) return;
    
    try {
      setStatus(prev => ({ ...prev, inbox: 'loading' }));
      const messages = [];

      // Load WhatsApp conversations
      try {
        const waRes = await fetch(`/whatsapp/conversations?client_id=${selectedClientId}`);
        if (waRes.ok) {
          const waData = await parseJsonResponse(waRes, 'WhatsApp inbox returned invalid JSON.');
          const waConversations = Array.isArray(waData) ? waData : waData.conversations || [];
          waConversations.forEach(conv => {
            messages.push({
              type: 'whatsapp',
              id: `wa-${conv.id}`,
              from: conv.contact_name || conv.contact_phone,
              channel:'WhatsApp',
              preview: conv.last_message || '(No messages)',
              timestamp: conv.last_message_time || new Date().toISOString(),
              unread: !conv.is_read,
              object: conv
            });
          });
        }
      } catch (e) {
        console.warn('Failed to load WhatsApp:', e);
      }

      // Load Email conversations
      try {
        const emailRes = await fetch(`/email/conversations?client_id=${selectedClientId}`);
        if (emailRes.ok) {
          const emailData = await parseJsonResponse(emailRes, 'Email inbox returned invalid JSON.');
          const emailConversations = Array.isArray(emailData) ? emailData : emailData.conversations || [];
          emailConversations.forEach(conv => {
            messages.push({
              type: 'email',
              id: `email-${conv.id}`,
              from: conv.contact_email,
              channel: 'Email',
              preview: conv.subject || '(No subject)',
              timestamp: conv.last_message_time || new Date().toISOString(),
              unread: false,
              object: conv
            });
          });
        }
      } catch (e) {
        console.warn('Failed to load emails:', e);
      }

      // Sort by timestamp (newest first)
      messages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setUnifiedInbox(messages);
      setStatus(prev => ({ ...prev, inbox: 'idle' }));
      loadHandoffQueue();
    } catch (e) {
      console.error('Error loading unified inbox:', e);
      setStatus(prev => ({ ...prev, inbox: 'error' }));
    }
  }, [selectedClientId, loadHandoffQueue]);


  const handleAuthSubmit = useCallback(async (event) => {
    event.preventDefault();
    setAuthNotice('');

    const authEndpoint = authMode === 'signup' ? 'signup' : 'login';

    const payload = authMode === 'login'
      ? {
          email: authForm.email,
          password: authForm.password,
        }
      : {
          email: authForm.email,
          password: authForm.password,
          full_name: authForm.full_name,
          role: 'customer',
          ...(authForm.business_name ? { business_name: authForm.business_name } : {}),
          ...(authForm.referral_code ? { referral_code: authForm.referral_code } : {}),
        };

    try {
      const response = await fetch(`/auth/${authEndpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setAuthNoticeType('error');
        setAuthNotice(formatErrorDetail(errorData.detail) || 'Authentication failed');
        return;
      }

      // Handle both login and signup (signup now returns token too)
      const data = await response.json();
      if (rememberMe) {
        localStorage.setItem('oakdesk_token', data.access_token);
        sessionStorage.removeItem('oakdesk_token');
      } else {
        sessionStorage.setItem('oakdesk_token', data.access_token);
        localStorage.removeItem('oakdesk_token');
      }
      setAuthToken(data.access_token);
      const currentUser = await loadCurrentUser(data.access_token);
      // Lock non-admin users to their own client immediately (don't wait for loadClients)
      if (currentUser && !isStaffRole(normalizeRole(getForcedRole(currentUser)))) {
        setSelectedClientId(`${currentUser.client_id}`);
      }

      // Load enabled modules for this user
      let modules = [];
      try {
        const modResp = await fetch('/business/modules', {
          headers: { 'Authorization': `Bearer ${data.access_token}` },
        });
        if (modResp.ok) {
          const modData = await modResp.json();
          modules = modData.enabled_modules || [];
          setEnabledModules(modules);
        }
      } catch (_) {}

      // Fetch trial status for enforcement
      try {
        const trialResp = await fetch('/api/trial/status', {
          headers: { 'Authorization': `Bearer ${data.access_token}` },
        });
        if (trialResp.ok) setTrialStatus(await trialResp.json());
      } catch (_) {}

      // New customers who haven't completed onboarding go straight to module selection (if no modules) or the wizard
      if (data.onboarding_required && !postAuthView) {
        if (modules.length === 0) {
          setSelectedModules([]);
          setView('module-select');
        } else {
          setView('onboarding');
        }
        setPostAuthView('');
        return;
      }

      // Users start in demo mode, can upgrade when ready
      const role = normalizeRole(roleOverride || getForcedRole(currentUser));
      const desiredView = postAuthView || getDefaultViewForRole(role);
      const roleViews = getAllowedViewIds(role);
      const resolvedView = roleViews.has(desiredView)
        ? desiredView
        : getDefaultViewForRole(role);
      setView(resolvedView);
      setPostAuthView('');
    } catch (error) {
      setAuthNoticeType('error');
      setAuthNotice('Authentication failed: ' + error.message);
    }
  }, [authForm, authMode, loadCurrentUser, postAuthView, getForcedRole, roleOverride, rememberMe]);

  const handleSaveModules = useCallback(async () => {
    const token = localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token') || authToken;
    try {
      await fetch('/business/modules', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && token !== AUTH_DISABLED_TOKEN ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ modules: selectedModules }),
      });
    } catch (_) {}
    setEnabledModules(selectedModules);
    setView('onboarding');
  }, [selectedModules, authToken]);

  const handleLogout = useCallback(() => {
    // Clear all authentication data
    localStorage.removeItem('oakdesk_token');
    sessionStorage.removeItem('oakdesk_token');
    localStorage.removeItem('oakdesk_role');
    localStorage.removeItem('oakdesk_view_role');
    
    // Reset all auth state
    setAuthToken('');
    setAuthRole('');
    setAuthUser(null);
    setRoleOverride('');
    setPostAuthView('');
    
    // Close sidebar and reset view
    setSidebarOpen(false);
    setView('landing');
  }, []);

  const handleUpgradeAccount = useCallback(async () => {
    if (!upgradeBusinessName.trim()) {
      setUpgradeError('Please enter your business name');
      return;
    }

    setUpgradeStatus('loading');
    setUpgradeError('');

    try {
      const response = await fetch('/api/business/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ business_name: upgradeBusinessName })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Upgrade failed');
      }

      // Success! Reload user and redirect to pricing to select a plan
      await loadCurrentUser();
      setUpgradeDialogOpen(false);
      setUpgradeBusinessName('');
      setUpgradeStatus('idle');
      setView('pricing');
    } catch (error) {
      setUpgradeError(error.message);
      setUpgradeStatus('error');
    }
  }, [upgradeBusinessName, authToken, loadCurrentUser]);

  const isOnDemoAccount = useCallback(() => {
    if (!authUser || !clients.length) return false;
    
    const userClient = clients.find(c => c.id === authUser.client_id);
    if (!userClient) return false;
    
    // Check if this is the demo client (has demo phone number or "Demo" in name)
    return userClient.phone_number === '+15551230000' || 
           userClient.phone_number.startsWith('demo-') ||
           userClient.business_name.toLowerCase().includes('demo');
  }, [authUser, clients]);

  useEffect(() => {
    localStorage.setItem('oakdesk_density', tableDensity);
  }, [tableDensity]);

  useEffect(() => {
    localStorage.setItem('oakdesk_sidebar_open', sidebarOpen ? 'true' : 'false');
  }, [sidebarOpen]);

  useEffect(() => {
    localStorage.setItem('oakdesk_sidebar_width', String(sidebarWidth));
  }, [sidebarWidth]);

  useEffect(() => {
    localStorage.setItem('oakdesk_sidebar_collapsed', sidebarCollapsed ? 'true' : 'false');
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.removeItem('oakdesk_view_role');
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const key = event.key.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && key === 'k') {
        event.preventDefault();
        setCommandPaletteOpen(true);
        setCommandQuery('');
      }
      if (event.shiftKey && event.key === '?') {
        event.preventDefault();
        setShortcutsOpen(true);
      }
      if (event.key === 'Escape') {
        setCommandPaletteOpen(false);
        setShortcutsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const commandItems = useMemo(() => {
    const viewItems = views
      .filter((item) => allowedViewIds.has(item.id))
      .map((item) => ({
        id: `view-${item.id}`,
        label: item.label,
        description: 'Open view',
        viewId: item.id,
      }));

    const actions = [
      {
        id: 'action-shortcuts',
        label: 'Keyboard shortcuts',
        description: 'Open the shortcuts guide',
        actionId: 'shortcuts',
      },
    ];

    if (isAuthenticated) {
      actions.push({
        id: 'action-logout',
        label: 'Log out',
        description: 'Sign out of Oakdesk',
        actionId: 'logout',
      });
    }

    return [...actions, ...viewItems];
  }, [allowedViewIds, isAuthenticated]);

  const filteredCommands = useMemo(() => {
    const query = commandQuery.trim().toLowerCase();
    if (!query) return commandItems;
    return commandItems.filter((item) =>
      [item.label, item.description, item.viewId]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [commandItems, commandQuery]);

  const handleCommandSelect = useCallback((item) => {
    if (item.actionId === 'shortcuts') {
      setShortcutsOpen(true);
    }
    if (item.actionId === 'logout') {
      handleLogout();
    }
    if (item.viewId) {
      handleNavigate(item.viewId);
    }
    setCommandPaletteOpen(false);
    setCommandQuery('');
  }, [handleLogout, handleNavigate]);

  const stickyActions = useMemo(() => {
    if (isLandingLayout) return null;

    if (view === 'manager') {
      return {
        primary: { label: 'Manage clients', onClick: () => setView('clients-mgmt') },
        secondary: { label: 'Open inbox', onClick: () => setView('unified-inbox') },
      };
    }

    if (view === 'overview') {
      return {
        primary: { label: 'Create client', onClick: () => setView('clients-mgmt') },
        secondary: { label: 'Refresh data', onClick: () => { loadClients(); loadCalls(); } },
      };
    }

    if (view === 'clients-mgmt') {
      return {
        primary: {
          label: showClientForm ? 'Hide new client' : 'New client',
          onClick: () => setShowClientForm((prev) => !prev),
        },
        secondary: { label: 'Configure channels', onClick: () => setView('channels') },
      };
    }

    if (view === 'admin-users') {
      return {
        primary: { label: 'Scroll to top', onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
        secondary: { label: 'Open search', onClick: () => setCommandPaletteOpen(true) },
      };
    }

    if (view === 'unified-inbox') {
      return {
        primary: { label: 'Refresh inbox', onClick: loadUnifiedInbox },
        secondary: { label: 'Open search', onClick: () => setCommandPaletteOpen(true) },
      };
    }

    if (view === 'whatsapp') {
      return {
        primary: {
          label: 'Refresh chats',
          onClick: () => {
            loadWhatsappConversations();
            if (selectedWAConversation?.id) {
              loadWhatsappConversation(selectedWAConversation.id);
            }
          },
        },
        secondary: { label: waAutoRefreshEnabled ? 'Pause auto-refresh' : 'Resume auto-refresh', onClick: () => setWaAutoRefreshEnabled((prev) => !prev) },
      };
    }

    if (view === 'settings') {
      return {
        primary: { label: 'Open search', onClick: () => setCommandPaletteOpen(true) },
        secondary: { label: 'Shortcuts', onClick: () => setShortcutsOpen(true) },
      };
    }

    return null;
  }, [
    emailAutoRefreshEnabled,
    isLandingLayout,
    loadCalls,
    loadClients,
    loadEmailConversation,
    loadEmailConversations,
    loadUnifiedInbox,
    loadWhatsappConversation,
    loadWhatsappConversations,
    selectedEmailConversation,
    selectedWAConversation,
    setView,
    showClientForm,
    view,
    waAutoRefreshEnabled,
  ]);

  useEffect(() => {
    loadClients();
    loadCalls();
    checkSystemStatus();
    loadAiProviders();
  }, [checkSystemStatus, loadAiProviders, loadCalls, loadClients]);

  useEffect(() => {
    if (view === 'manager' && clients.length > 0) {
      loadWhatsappMetrics(clients);
    }
  }, [clients, loadWhatsappMetrics, view]);

  useEffect(() => {
    if (view === 'onboarding-wizard') {
      setView('onboarding');
    }
  }, [view]);

  useEffect(() => {
    if (authToken && authToken !== AUTH_DISABLED_TOKEN) {
      loadCurrentUser();
      fetch('/business/modules', {
        headers: { 'Authorization': `Bearer ${authToken}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => { if (data) setEnabledModules(data.enabled_modules || []); })
        .catch(() => {});
    }
  }, [authToken, loadCurrentUser]);


  useEffect(() => {
    // Handle public legal pages direct URL navigation
    const path = window.location.pathname;
    if (path === '/privacy') {
      setView('privacy');
      return;
    }
    if (path === '/terms') {
      setView('terms');
      return;
    }
    if (path === '/data-deletion') {
      setView('data-deletion');
      return;
    }
    if (path === '/cookies' || path === '/cookie-policy') {
      setView('cookie-policy');
      return;
    }

    const params = new URLSearchParams(window.location.search);

    // Direct view navigation via URL parameter (e.g., /app?view=legal-public)
    const viewParam = params.get('view');
    if (viewParam) {
      setView(viewParam);
      return;
    }

    // PWA home screen shortcuts
    const shortcut = params.get('shortcut');
    if (shortcut) {
      const shortcutMap = { inbox: 'unified-inbox', bookings: 'bookings', crm: 'crm', support: 'support' };
      if (shortcutMap[shortcut]) { window.history.replaceState({}, '', '/'); if (authToken) { setView(shortcutMap[shortcut]); return; } }
    }

    const inviteToken = params.get('invite');
    const resetToken = params.get('reset');
    const verifyToken = params.get('verify');

    const paymentSuccess = params.get('payment');

    if (inviteToken) {
      setAuthFlowToken(inviteToken);
      setAuthMode('invite');
      setView('auth');
    } else if (resetToken) {
      setAuthFlowToken(resetToken);
      setAuthMode('reset-confirm');
      setView('auth');
    } else if (verifyToken) {
      setAuthFlowToken(verifyToken);
      setAuthMode('login');
      setView('auth');
      verifyEmail(verifyToken);
    } else if (params.get('meta_error')) {
      const errCode = params.get('meta_error');
      const errMessages = {
        'access_denied': 'You denied the Meta permission request. Try again and accept the required permissions.',
        'invalid_state': 'Session expired. Please try connecting again.',
        'no_accounts': 'No Meta Business accounts found. Check your WhatsApp Business or Facebook Page setup.',
      };
      showNotice('error', errMessages[errCode] || `Meta connection failed: ${errCode}. Try again or enter credentials manually.`);
      window.history.replaceState({}, '', '/');
    } else if (params.get('meta_connected')) {
      // Meta OAuth success — show congratulations modal
      const platforms = (params.get('platforms') || '').split(',').filter(Boolean);
      setMetaConnectedPlatforms(platforms);
      window.history.replaceState({}, '', '/');
    } else if (paymentSuccess === 'success') {
      // Payment complete — refresh trial status and clear URL param
      const planName = params.get('plan') || 'your plan';
      window.history.replaceState({}, '', '/');
      // If already authenticated, refresh trial status immediately
      const token = localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token');
      if (token) {
        fetch('/api/trial/status', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setTrialStatus(data); })
          .catch(() => {});
        showNotice('success', `Payment successful! Your ${planName} plan is now active.`);
      }
    }
  }, [verifyEmail, showNotice]);

  useEffect(() => {
    const publicViews = new Set(['landing', 'auth', 'legal-public', 'privacy', 'terms', 'about-us', 'cookie-policy', 'data-deletion']);
    if (!isAuthenticated && !publicViews.has(view)) {
      setView('auth');
    }
    if (isAuthenticated && view !== 'landing' && view !== 'auth' && !allowedViewIds.has(view)) {
      setView(getDefaultViewForRole(resolvedRole));
    }
    if (isAuthenticated && view === 'auth' && !authFlowToken) {
      const role = normalizeRole(authUser?.role || authRole);
      const desiredView = postAuthView || getDefaultViewForRole(role);
      const roleViews = getAllowedViewIds(role);
      const resolvedView = roleViews.has(desiredView)
        ? desiredView
        : getDefaultViewForRole(role);
      setView(resolvedView);
    }
  }, [allowedViewIds, authFlowToken, authRole, authUser, isAuthenticated, postAuthView, resolvedRole, view]);

  useEffect(() => {
    if (view === 'unified-inbox') {
      loadUnifiedInbox();
    }
  }, [view, selectedClientId, loadUnifiedInbox]);

  useEffect(() => {
    if (view === 'admin-users') {
      loadAdminUsers();
      loadAdminInvites();
    }
  }, [view, loadAdminInvites, loadAdminUsers]);

  useEffect(() => {
    if (view === 'channels') {
      loadClientNumbers(selectedClientId);
      loadClientInboxes(selectedClientId);
      loadPoolDids();
    }
  }, [view, selectedClientId, loadClientInboxes, loadClientNumbers, loadPoolDids]);

  useEffect(() => {
    if (view === 'ai-insights') {
      loadAiInsights(selectedClientId);
    }
  }, [view, selectedClientId, loadAiInsights]);

  useEffect(() => {
    if (view === 'billing') {
      loadBillingUsage(selectedClientId, billingWindowDays);
    }
  }, [view, selectedClientId, billingWindowDays, loadBillingUsage]);

  useEffect(() => {
    if (view === 'sales-chat') {
      loadSalesChatConversations();
      if (isAdmin) {
        loadAdminUsers();
      }
    }
  }, [view, isAdmin, loadAdminUsers, loadSalesChatConversations]);

  useEffect(() => {
    if (view === 'sales-chat' && selectedSalesConversation?.id) {
      loadSalesChatMessages(selectedSalesConversation.id);
    }
  }, [view, selectedSalesConversation?.id, loadSalesChatMessages]);

  useEffect(() => {
    setInboxSummary(null);
    setInboxSummaryStatus('idle');
  }, [selectedInboxMessage]);

  // --- Sales dashboard loader ---
  const loadSalesDashboard = useCallback(async () => {
    setSalesDashboardLoading(true);
    try {
      const res = await apiFetch('/api/sales/my-dashboard');
      if (res.ok) {
        const data = await res.json();
        setSalesDashboard(data);
      }
    } catch (_) {}
    setSalesDashboardLoading(false);
  }, []);

  useEffect(() => {
    if (view === 'sales-dashboard') loadSalesDashboard();
  }, [view, loadSalesDashboard]);

  // --- Token balance loader ---
  const loadTokenBalance = useCallback(async () => {
    try {
      const res = await apiFetch('/api/tokens/balance');
      if (res.ok) setTokenBalance(await res.json());
      const txRes = await apiFetch('/api/tokens/transactions?limit=20');
      if (txRes.ok) setTokenTransactions(await txRes.json());
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (view === 'tokens') loadTokenBalance();
  }, [view, loadTokenBalance]);

  const handleTokenTopUp = useCallback(async (e) => {
    e.preventDefault();
    setTokenTopUpStatus('loading');
    try {
      const res = await apiFetch(`/api/tokens/topup/${tokenTopUpForm.client_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: Number(tokenTopUpForm.amount), notes: tokenTopUpForm.notes }),
      });
      if (res.ok) {
        setTokenTopUpStatus('success');
        setTokenTopUpForm({ client_id: '', amount: '', notes: '' });
        loadTokenBalance();
      } else {
        setTokenTopUpStatus('error');
      }
    } catch (_) {
      setTokenTopUpStatus('error');
    }
  }, [tokenTopUpForm, loadTokenBalance]);

  return (
    <ErrorBoundary>
    <div className={isLandingLayout ? 'page page--landing' : 'page page--app'}>
      <Layout
        currentView={view}
        setCurrentView={handleNavigate}
        user={authUser}
        onLogout={handleLogout}
        navigation={sidebarNavigation}
        hideSidebar={isLandingLayout}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        sidebarWidth={effectiveSidebarWidth}
        onResizeSidebar={handleSidebarResize}
        sidebarCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
        isAdmin={isAdmin}
        isDeveloper={isDeveloper}
        onNavigate={handleNavigate}
      >
        <div className={`content ${tableDensity === 'compact' ? 'density-compact' : 'density-comfortable'}`}>
          {!isLandingLayout && isAdmin && (
            <div className="status-strip">
              <div className="status-strip__left">
                <span className="status-chip">
                  {authUser?.full_name || authUser?.email || 'Guest'}
                </span>
                {(isAdmin || isDeveloper) && (
                  <>
                    <span className="status-chip">
                      Role: {roleOverride ? `${roleLabel(roleOverride)} preview` : roleLabel(resolvedRole)}
                    </span>
                    <span className={`status-chip status-chip--${systemStatus.backend === 'ok' ? 'ok' : systemStatus.backend === 'error' ? 'error' : 'warn'}`}>
                      API: {systemStatus.backend === 'ok' ? 'Online' : systemStatus.backend === 'error' ? 'Error' : 'Checking'}
                    </span>
                  </>
                )}
              </div>
              <div className="status-strip__right">
                {canSwitchViewRole && (
                  <label className="status-select">
                    <span>View</span>
                      <select
                        className="oakdesk-select"
                        value={roleOverride || 'actual'}
                        onChange={(event) => {
                          const value = event.target.value;
                          const next = value === 'actual' ? '' : value;
                          setRoleOverride(next);
                        }}
                      >
                        <option value="actual">Actual role</option>
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                        <option value="developer">Developer</option>
                      </select>
                  </label>
                )}
                <input
                  className="status-search"
                  type="search"
                  placeholder="Search views or actions..."
                  value={commandQuery}
                  onChange={(event) => {
                    setCommandQuery(event.target.value);
                    if (!commandPaletteOpen) {
                      setCommandPaletteOpen(true);
                    }
                  }}
                  onFocus={() => setCommandPaletteOpen(true)}
                />
                <button className="ghost" type="button" onClick={() => setCommandPaletteOpen(true)}>
                  Quick search
                </button>
                <button className="ghost" type="button" onClick={() => setShortcutsOpen(true)}>
                  Shortcuts
                </button>
              </div>
            </div>
          )}
          {/* Cookie consent — shows on landing for new visitors */}
          {isLanding && (
            <CookieConsent
              onPrivacyPolicy={() => window.open('/privacy', '_blank')}
            />
          )}

          {isLanding ? (
            <Suspense fallback={<LazyFallback />}>
              <LandingPage
                onGetStarted={handleLandingGetStarted}
                onLogin={() => { setPostAuthView(''); setAuthMode('login'); setView('auth'); }}
                onRequestCallback={handleRequestCallback}
              />
            </Suspense>
          ) : null}

          {/* Public legal pages — render without authentication */}
          {view === 'legal-public' && <Suspense fallback={<LazyFallback />}><PublicLegalPage /></Suspense>}
          {view === 'privacy' && <Suspense fallback={<LazyFallback />}><PrivacyPage onBack={() => setView('landing')} /></Suspense>}
          {view === 'terms' && <Suspense fallback={<LazyFallback />}><TermsPage onBack={() => setView('landing')} /></Suspense>}
          {view === 'about-us' && <Suspense fallback={<LazyFallback />}><AboutUs onNavigate={handleNavigate} /></Suspense>}
          {view === 'cookie-policy' && <Suspense fallback={<LazyFallback />}><CookiePolicyPage onBack={() => setView('landing')} /></Suspense>}
          {view === 'data-deletion' && <Suspense fallback={<LazyFallback />}><DataDeletionPage onBack={() => setView('landing')} /></Suspense>}

        {!isLanding && (view === 'auth' ? (
          <main className="auth">
                <section className="auth-card">
                  <div className="auth-header">
                    <div>
                      <h2>{authMode === 'login'
                        ? 'Log in'
                        : authMode === 'signup'
                          ? 'Create an account'
                          : authMode === 'reset-request'
                            ? 'Reset your password'
                            : authMode === 'reset-confirm'
                              ? 'Set a new password'
                              : 'Accept invite'}
                      </h2>
                      <p>{authMode === 'login'
                        ? 'Access your workspace and business dashboard.'
                        : authMode === 'signup'
                          ? 'Create a client user account. Admin access is assigned by ops.'
                          : authMode === 'reset-request'
                            ? 'We will email you a reset link.'
                            : authMode === 'reset-confirm'
                              ? 'Choose a strong password for your account.'
                              : 'Finish setting up your Oakdesk access.'}
                      </p>
                    </div>
                    <button
                      className="ghost"
                      type="button"
                      onClick={() => {
                        setPostAuthView('');
                        setView('landing');
                      }}
                    >
                      Back to Landing
                    </button>
                  </div>

                  <form
                    className="form"
                    onSubmit={authMode === 'login' || authMode === 'signup' ? handleAuthSubmit : (event) => event.preventDefault()}
                  >
                    {(authMode === 'login' || authMode === 'signup') && (
                      <>
                        {authMode === 'signup' && (
                          <>
                            <label>
                              Full name
                              <input
                                type="text"
                                value={authForm.full_name}
                                onChange={(event) => setAuthForm({ ...authForm, full_name: event.target.value })}
                                placeholder="Jordan Oakley"
                                required
                              />
                            </label>
                            <label>
                              Business name
                              <input
                                type="text"
                                value={authForm.business_name}
                                onChange={(event) => setAuthForm({ ...authForm, business_name: event.target.value })}
                                placeholder="Acme Co. (optional)"
                              />
                            </label>
                          </>
                        )}
                        <label>
                          Email
                          <input
                            type="email"
                            value={authForm.email}
                            onChange={(event) => setAuthForm({ ...authForm, email: event.target.value })}
                            placeholder="you@oakdesk.ai"
                            required
                          />
                        </label>
                        <label>
                          Password
                          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={authForm.password}
                              onChange={(event) => setAuthForm({ ...authForm, password: event.target.value })}
                              placeholder="Minimum 8 characters"
                              required
                              style={{ flex: 1, paddingRight: '4rem' }}
                            />
                            <button
                              type="button"
                              className="ghost"
                              onClick={() => setShowPassword((v) => !v)}
                              style={{ position: 'absolute', right: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem', minWidth: 0 }}
                            >
                              {showPassword ? 'Hide' : 'Show'}
                            </button>
                          </div>
                        </label>
                        {authMode === 'login' && (
                          <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                              style={{ width: 'auto', margin: 0 }}
                            />
                            Remember me
                          </label>
                        )}
                        {authMode === 'signup' && (
                          <>
                            <label>
                              Referral code
                              <input
                                type="text"
                                value={authForm.referral_code}
                                onChange={(event) => setAuthForm({ ...authForm, referral_code: event.target.value })}
                                placeholder="Optional — enter code from your salesperson"
                              />
                            </label>
                            <p className="auth-helper">
                              Simple monthly billing, no lock-in contract — cancel anytime.
                            </p>
                          </>
                        )}
                      </>
                    )}

                    {authMode === 'reset-request' && (
                      <>
                        <label>
                          Email
                          <input
                            type="email"
                            value={authResetEmail}
                            onChange={(event) => setAuthResetEmail(event.target.value)}
                            placeholder="you@oakdesk.ai"
                            required
                          />
                        </label>
                        <button className="primary" type="button" onClick={requestPasswordReset}>
                          Send reset link
                        </button>
                      </>
                    )}

                    {authMode === 'reset-confirm' && (
                      <>
                        <label>
                          Reset token
                          <input
                            type="text"
                            value={authFlowToken}
                            onChange={(event) => setAuthFlowToken(event.target.value)}
                            placeholder="Paste your reset token"
                            required
                          />
                        </label>
                        <label>
                          New password
                          <input
                            type="password"
                            value={authNewPassword}
                            onChange={(event) => setAuthNewPassword(event.target.value)}
                            placeholder="Minimum 8 characters"
                            required
                          />
                        </label>
                        <button className="primary" type="button" onClick={confirmPasswordReset}>
                          Update password
                        </button>
                      </>
                    )}

                    {authMode === 'invite' && (
                      <>
                        <label>
                          Invite token
                          <input
                            type="text"
                            value={authFlowToken}
                            onChange={(event) => setAuthFlowToken(event.target.value)}
                            placeholder="Paste your invite token"
                            required
                          />
                        </label>
                        <label>
                          Set password
                          <input
                            type="password"
                            value={authNewPassword}
                            onChange={(event) => setAuthNewPassword(event.target.value)}
                            placeholder="Minimum 8 characters"
                            required
                          />
                        </label>
                        <button className="primary" type="button" onClick={acceptInvite}>
                          Accept invite
                        </button>
                      </>
                    )}

                    {authNotice && (
                      <div className={`auth-notice${authNoticeType === 'success' ? ' auth-notice--success' : ''}`}>
                        {authNotice}
                      </div>
                    )}

                    {(authMode === 'login' || authMode === 'signup') && (
                      <button className="primary" type="submit">
                        {authMode === 'login' ? 'Log in' : 'Create account'}
                      </button>
                    )}
                  </form>

                  <div className="auth-footer">
                    {authMode === 'login' && (
                      <>
                        <span>Need an account?</span>
                        <button
                          className="ghost"
                          type="button"
                          onClick={() => {
                            setAuthNotice('');
                            setAuthMode('signup');
                          }}
                        >
                          Sign up
                        </button>
                        <button
                          className="ghost"
                          type="button"
                          onClick={() => {
                            setAuthNotice('');
                            setAuthMode('reset-request');
                          }}
                        >
                          Forgot password?
                        </button>
                      </>
                    )}
                    {authMode === 'signup' && (
                      <>
                        <span>Already have an account?</span>
                        <button
                          className="ghost"
                          type="button"
                          onClick={() => {
                            setAuthNotice('');
                            setAuthMode('login');
                          }}
                        >
                          Log in
                        </button>
                      </>
                    )}
                    {authMode !== 'login' && authMode !== 'signup' && (
                      <button
                        className="ghost"
                        type="button"
                        onClick={() => {
                          setAuthNotice('');
                          setAuthMode('login');
                        }}
                      >
                        Back to login
                      </button>
                    )}
                  </div>
                </section>
              </main>
            ) : (
              <> {/* app views */}
                {/* Email verification banner */}
                {!isAdmin && !isDeveloper && authUser && !authUser.email_verified && (
                  <EmailVerificationBanner user={authUser} />
                )}

                {/* Survey modal — shown to trial users around day 3-5 */}
                {!isAdmin && !isDeveloper && authUser && (
                  <SurveyModal
                    user={authUser}
                    createdAt={authUser.created_at}
                  />
                )}
                {/* Upgrade banner — non-blocking. Unpaid clients can browse the full
                    dashboard; specific paid actions (sending, campaigns, AI) are
                    gated individually rather than locking out the whole app. */}
                {!isAdmin && !isDeveloper && trialStatus?.access_blocked && (
                  <UpgradeBanner onUpgrade={() => setView('pricing')} />
                )}
                {/* Meta OAuth success modal */}
                {metaConnectedPlatforms !== null && (
                  <MetaConnectedModal
                    platforms={metaConnectedPlatforms}
                    onDismiss={() => setMetaConnectedPlatforms(null)}
                    onNavigate={(v) => { setMetaConnectedPlatforms(null); setView(v); }}
                  />
                )}
                {/* Bookings available banner */}
                {authUser && (
                  <BookingsAvailableBanner />
                )}
                <div className="breadcrumb-bar">
                  <span>{primaryAreaLabel}</span>
                  <span className="breadcrumb-separator">&gt;</span>
                  <span>{currentViewLabel}</span>
                </div>
                {/* Clean page header — hidden on home (HomeDashboard has its own greeting) */}
                {view !== 'home' && (
                  <header className="app-page-header">
                    <div className="app-page-header__left">
                      <h1 className="app-page-header__title">{currentViewLabel}</h1>
                      {isAdmin && (
                        <span className="app-page-header__pill">
                          {healthSummary ? '● Online' : '○ Checking'}
                        </span>
                      )}
                    </div>
                    {isStaffRole(resolvedRole) && (
                      <div className="app-page-header__stats">
                        <span>{clients.length} clients</span>
                        <span>{calls.length} calls</span>
                      </div>
                    )}
                  </header>
                )}

                {notice && (
                  <div className={`notice notice--${notice.type}`}>
                    <span>{notice.message}</span>
                  </div>
                )}

        {view === 'manager' && (
          <main className="grid">
            {/* System Status */}
            <section className="card span-2">
              <div className="card-header">
                <h2>System Status</h2>
                <p className="text-secondary text-sm">Real-time platform health</p>
              </div>
              <div className="flex gap flex-wrap">
                <div className="card-metric card-metric--success" style={{ flex: '1', minWidth: '150px' }}>
                  <p className="metric-label">Backend</p>
                  <p className="metric-value" style={{ color: systemStatus.backend === 'ok' ? 'var(--success)' : 'var(--error)' }}>
                    {systemStatus.backend === 'ok' ? '● Online' : '● Offline'}
                  </p>
                </div>
                <div className="card-metric card-metric--success" style={{ flex: '1', minWidth: '150px' }}>
                  <p className="metric-label">Frontend</p>
                  <p className="metric-value" style={{ color: 'var(--success)' }}>● Online</p>
                </div>
                <div className="card-metric card-metric--success" style={{ flex: '1', minWidth: '150px' }}>
                  <p className="metric-label">Database</p>
                  <p className="metric-value" style={{ color: 'var(--success)' }}>● Connected</p>
                </div>
              </div>
            </section>

            {/* Key Metrics */}
            <section className="card">
              <div className="card-header flex items-center justify-between">
                <div>
                  <h2>WhatsApp Metrics</h2>
                </div>
                <button className="btn btn-ghost" type="button" onClick={() => loadWhatsappMetrics(clients)}>↻ Refresh</button>
              </div>
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="card-metric card-metric--primary">
                  <p className="metric-label">Total Conversations</p>
                  <p className="metric-value" style={{ fontSize: '2rem' }}>{whatsappMetrics.totalConversations}</p>
                </div>
                <div className="card-metric card-metric--primary">
                  <p className="metric-label">Active Now</p>
                  <p className="metric-value" style={{ fontSize: '2rem' }}>{whatsappMetrics.activeConversations}</p>
                </div>
                <div className="card-metric card-metric--primary" style={{ gridColumn: '1 / -1' }}>
                  <p className="metric-label">Total Messages</p>
                  <p className="metric-value" style={{ fontSize: '2rem' }}>{whatsappMetrics.totalMessages}</p>
                </div>
              </div>
            </section>

            {/* Quick Actions */}
            <section className="card">
              <div className="card-header">
                <h2>Quick Actions</h2>
                <p>Manage everything from here</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button className="primary" style={{ width: '100%', padding: '12px' }} onClick={() => setView('clients-mgmt')}>
                  Manage Clients
                </button>
                <button className="ghost" style={{ width: '100%', padding: '12px' }} onClick={() => setView('whatsapp')}>
                  View Conversations
                </button>
                <button className="ghost" style={{ width: '100%', padding: '12px' }} onClick={() => setView('crm')}>
                  Manage Contacts
                </button>
                <button className="ghost" style={{ width: '100%', padding: '12px' }} onClick={() => setView('analytics')}>
                  View Analytics
                </button>
                <button className="ghost" style={{ width: '100%', padding: '12px' }} onClick={() => setView('settings')}>
                  Settings
                </button>
              </div>
            </section>

            {/* Recent Activity */}
            <section className="card span-2">
              <div className="card-header">
                <h2>Active Clients</h2>
                <p>{clients.length} total</p>
              </div>
              <div className="list">
                {status.clients === 'loading' ? (
                  <SkeletonList rows={3} />
                ) : clients.length === 0 ? (
                  <EmptyStateCard
                    title="No clients yet"
                    detail="Create a client to start routing calls and messages."
                    actionLabel="Create client"
                    onAction={() => setView('clients-mgmt')}
                  />
                ) : (
                  clients.map((client) => (
                    <div key={client.id} className="list-item" style={{ paddingTop: '12px', paddingBottom: '12px' }}>
                      <div>
                        <strong>{client.business_name}</strong>
                        <span style={{ display: 'block', fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                          Voice: {client.phone_number} • WhatsApp: {client.whatsapp_number || 'Not set'}
                        </span>
                      </div>
                      <button className="micro-btn" style={{ padding: '4px 8px', fontSize: '0.75rem' }} onClick={() => { setSelectedClientId(client.id); setView('whatsapp'); }}>
                        View
                      </button>
                    </div>
                  ))
                )}
              </div>
            </section>
          </main>
        )}

        {view === 'clients-mgmt' && (
          <main className="grid">
            {/* Stats Overview */}
            <section className="card">
              <div className="p-24">
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>
                  Total Clients
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--success)', fontFamily: 'Poppins' }}>
                  {clients.length}
                </div>
              </div>
            </section>

            <section className="card">
              <div className="p-24">
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>
                  Active Clients
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--accent-blue)', fontFamily: 'Poppins' }}>
                  {clients.filter(c => !c.phone_number.startsWith('pending-')).length}
                </div>
              </div>
            </section>

            <section className="card">
              <div className="p-24">
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>
                  Setup Pending
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--warning)', fontFamily: 'Poppins' }}>
                  {clients.filter(c => c.phone_number.startsWith('pending-')).length}
                </div>
              </div>
            </section>

            <section className="card span-3">
              <div className="card-header">
                <h2>Manage Clients</h2>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="search"
                    placeholder="Search clients..."
                    value={clientSearchQuery}
                    onChange={(e) => setClientSearchQuery(e.target.value)}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      minWidth: '250px'
                    }}
                  />
                  <button className="primary" type="button" onClick={() => setShowClientForm(!showClientForm)}>
                    {showClientForm ? 'Cancel' : '+ New Client'}
                  </button>
                </div>
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <h2>Create New Client</h2>
                <button className="ghost" type="button" onClick={() => setShowClientForm(!showClientForm)}>
                  {showClientForm ? '✕ Close' : '+ New'}
                </button>
              </div>
              {showClientForm && (
                <div className="form p-24">
                  <div style={{ 
                    padding: '16px', 
                    background: 'rgba(124, 255, 79, 0.1)', 
                    border: '1px solid rgba(124, 255, 79, 0.3)',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    fontSize: '0.9rem'
                  }}>
                    💡 <strong>Tip:</strong> You can leave the phone number as "pending-" to set it up later via the onboarding wizard.
                  </div>
                  
                  <label>
                    Business Name *
                    <input
                      type="text"
                      placeholder="e.g., Acme Plumbing Corp"
                      value={clientForm.business_name}
                      onChange={(e) => setClientForm({ ...clientForm, business_name: e.target.value })}
                      required
                      style={{
                        borderColor: clientForm.business_name.length > 0 ? 'rgba(124, 255, 79, 0.5)' : 'var(--border)'
                      }}
                    />
                    <small className="field-help">The name of the business using Oakdesk</small>
                  </label>
                  <label>
                    Voice Phone Number *
                    <input
                      type="text"
                      placeholder="+1234567890 or pending-setup"
                      value={clientForm.phone_number}
                      onChange={(e) => setClientForm({ ...clientForm, phone_number: e.target.value })}
                      required
                      style={{
                        borderColor: clientForm.phone_number.length > 0 ? 'rgba(124, 255, 79, 0.5)' : 'var(--border)'
                      }}
                    />
                    <small className="field-help">Primary phone number for voice calls</small>
                  </label>
                  <label>
                    WhatsApp Number
                    <input
                      type="text"
                      placeholder="+1234567890 (optional)"
                      value={clientForm.whatsapp_number || ''}
                      onChange={(e) => setClientForm({ ...clientForm, whatsapp_number: e.target.value })}
                    />
                    <small className="field-help">WhatsApp Business number (optional)</small>
                  </label>
                  <label>
                    AI Prompt *
                    <textarea
                      placeholder="You are a friendly AI receptionist for [Business]. Help customers with inquiries, schedule appointments, and provide information..."
                      value={clientForm.ai_prompt}
                      onChange={(e) => setClientForm({ ...clientForm, ai_prompt: e.target.value })}
                      style={{ 
                        minHeight: '120px',
                        borderColor: clientForm.ai_prompt.length > 20 ? 'rgba(124, 255, 79, 0.5)' : 'var(--border)'
                      }}
                      required
                    />
                    <small className="field-help">
                      System prompt for the AI. Be specific about your business and desired tone.
                      {clientForm.ai_prompt.length > 0 && (
                        <span style={{ float: 'right', color: clientForm.ai_prompt.length > 500 ? 'var(--warning)' : 'var(--success)' }}>
                          {clientForm.ai_prompt.length} chars
                        </span>
                      )}
                    </small>
                  </label>
                  <label>
                    Voice Preference
                    <select
                       className="oakdesk-select"
                       value={clientForm.voice_preference}
                       onChange={(e) => setClientForm({ ...clientForm, voice_preference: e.target.value })}
                       style={{ borderColor: 'rgba(124, 255, 79, 0.5)' }}
                    >
                      <option value="alloy">Alloy (Neutral, balanced)</option>
                      <option value="echo">Echo (Male, clear)</option>
                      <option value="fable">Fable (Warm, engaging)</option>
                      <option value="onyx">Onyx (Deep, authoritative)</option>
                      <option value="nova">Nova (Female, friendly)</option>
                      <option value="shimmer">Shimmer (Energetic, upbeat)</option>
                    </select>
                    <small className="field-help">Choose the AI voice that matches your brand</small>
                  </label>
                  <label>
                    Notification Email
                    <input
                      type="email"
                      placeholder="admin@example.com"
                      value={clientForm.notification_email}
                      onChange={(e) => setClientForm({ ...clientForm, notification_email: e.target.value })}
                    />
                    <small className="field-help">Receive alerts and important notifications here</small>
                  </label>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                    <button 
                      className="primary" 
                      type="button"
                      onClick={async () => {
                        if (!clientForm.business_name || !clientForm.phone_number || !clientForm.ai_prompt) {
                          showNotice('error', 'Please fill in all required fields');
                          return;
                        }
                        try {
                          const response = await fetch('/clients', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(clientForm),
                          });
                          if (!response.ok) {
                            const error = await response.json();
                            throw new Error(error.detail || 'Failed to create client');
                          }
                          setClientForm(emptyClient);
                          setShowClientForm(false);
                          loadClients();
                          showNotice('success', '✓ Client created successfully');
                        } catch (error) {
                          showNotice('error', error.message);
                        }
                      }}
                      disabled={!clientForm.business_name || !clientForm.phone_number || !clientForm.ai_prompt}
                      style={{
                        flex: 1,
                        opacity: (!clientForm.business_name || !clientForm.phone_number || !clientForm.ai_prompt) ? 0.5 : 1,
                        cursor: (!clientForm.business_name || !clientForm.phone_number || !clientForm.ai_prompt) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ✓ Create Client
                    </button>
                    <button 
                      className="ghost"
                      type="button"
                      onClick={() => {
                        setClientForm(emptyClient);
                        setShowClientForm(false);
                      }}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </section>

            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>Invite teammates</h2>
                  <p>Send email invites for new users.</p>
                </div>
                <button className="ghost" type="button" onClick={loadAdminInvites}>
                  Refresh invites
                </button>
              </div>

              <form className="form form-row" onSubmit={handleInviteCreate}>
                <label>
                  Full name
                  <input
                    type="text"
                    value={inviteForm.full_name}
                    onChange={(event) => setInviteForm({ ...inviteForm, full_name: event.target.value })}
                    placeholder="Jordan Oakley"
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(event) => setInviteForm({ ...inviteForm, email: event.target.value })}
                    placeholder="jordan@oakdesk.ai"
                  />
                </label>
                <label>
                  Role
                  <select
                      className="oakdesk-select"
                      value={inviteForm.role}
                      onChange={(event) => setInviteForm({ ...inviteForm, role: event.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    {isDeveloper && <option value="developer">Developer</option>}
                  </select>
                </label>
                <label>
                  Organisation Name
                  <input
                    type="text"
                    value={inviteForm.organisation_name}
                    onChange={(event) => setInviteForm({ ...inviteForm, organisation_name: event.target.value })}
                    placeholder="Acme Plumbing Co."
                  />
                </label>
                <div className="form-actions">
                  <button className="primary" type="submit">
                    {status.inviteCreate === 'loading' ? 'Sending...' : 'Send invite'}
                  </button>
                </div>
              </form>

              <div className="list">
                {status.adminInvites === 'loading' ? (
                  <p className="empty">Loading invites...</p>
                ) : adminInvites.length === 0 ? (
                  <p className="empty">No active invites.</p>
                ) : (
                  adminInvites.map((invite) => (
                    <div key={invite.id} className="list-item">
                      <div>
                        <strong>{invite.full_name || invite.email}</strong>
                        <span>{invite.email} · {invite.organisation_name || '-'}</span>
                      </div>
                      <div className="inline-actions">
                        <span className="pill">{roleLabel(invite.role)}</span>
                        <button
                          className="ghost"
                          type="button"
                          onClick={() => handleInviteRevoke(invite.id)}
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="card span-3">
              <div className="card-header">
                <h2>All Clients</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={clientFilterStatus}
                    onChange={(e) => setClientFilterStatus(e.target.value)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="all">All Clients</option>
                    <option value="active">Active</option>
                    <option value="pending">Setup Pending</option>
                    <option value="demo">Demo</option>
                  </select>
                  <button className="ghost" type="button" onClick={loadClients}>↻ Refresh</button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px', padding: '16px' }}>
                {(() => {
                  const filteredClients = clients.filter(client => {
                    // Apply search filter
                    const matchesSearch = !clientSearchQuery || 
                      client.business_name.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                      client.phone_number.includes(clientSearchQuery);
                    
                    // Apply status filter
                    let matchesStatus = true;
                    if (clientFilterStatus === 'active') {
                      matchesStatus = !client.phone_number.startsWith('pending-') && !client.business_name.toLowerCase().includes('demo');
                    } else if (clientFilterStatus === 'pending') {
                      matchesStatus = client.phone_number.startsWith('pending-');
                    } else if (clientFilterStatus === 'demo') {
                      matchesStatus = client.business_name.toLowerCase().includes('demo');
                    }
                    
                    return matchesSearch && matchesStatus;
                  });

                  if (clients.length === 0) {
                    return (
                      <div style={{ gridColumn: '1/-1', padding: '80px 24px', textAlign: 'center' }}>
                        <div style={{ fontSize: '4rem', marginBottom: '16px', opacity: '0.3' }}>🏢</div>
                        <h3 style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>No clients yet</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                          Create your first client to get started
                        </p>
                        <button className="primary" onClick={() => setShowClientForm(true)}>
                          + Create Client
                        </button>
                      </div>
                    );
                  }

                  if (filteredClients.length === 0) {
                    return (
                      <div style={{ gridColumn: '1/-1', padding: '80px 24px', textAlign: 'center' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '16px', opacity: '0.3' }}>🔍</div>
                        <h3 style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>No clients found</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                          Try adjusting your search or filter criteria
                        </p>
                        <button className="ghost" onClick={() => {
                          setClientSearchQuery('');
                          setClientFilterStatus('all');
                        }}>
                          Clear Filters
                        </button>
                      </div>
                    );
                  }

                  return filteredClients.map((client) => {
                      const isPending = client.phone_number.startsWith('pending-');
                      const isDemo = client.business_name.toLowerCase().includes('demo');
                      
                      return (
                        <div 
                          key={client.id} 
                          style={{
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--border)',
                            borderRadius: '12px',
                            padding: '20px',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#7CFF4F';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(124, 255, 79, 0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'var(--border)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          {/* Status Badge */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            background: isPending ? 'rgba(255, 165, 0, 0.15)' : isDemo ? 'rgba(124, 255, 79, 0.15)' : 'rgba(0, 217, 255, 0.15)',
                            color: isPending ? '#FFA500' : isDemo ? '#7CFF4F' : '#00D9FF',
                            border: `1px solid ${isPending ? '#FFA500' : isDemo ? '#7CFF4F' : '#00D9FF'}`
                          }}>
                            {isPending ? 'Pending' : isDemo ? 'Demo' : 'Active'}
                          </div>

                          {/* Client Info */}
                          <div style={{ marginBottom: '16px' }}>
                            <h3 style={{ 
                              margin: '0 0 8px 0', 
                              fontSize: '1.2rem', 
                              fontWeight: '600',
                              color: 'var(--text-primary)',
                              paddingRight: '80px'
                            }}>
                              {client.business_name}
                            </h3>
                            <div style={{ 
                              fontSize: '0.8rem', 
                              color: 'var(--text-secondary)',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px'
                            }}>
                              <div>📞 {client.phone_number}</div>
                              {client.whatsapp_number && (
                                <div>💬 {client.whatsapp_number}</div>
                              )}
                              {client.notification_email && (
                                <div>✉️ {client.notification_email}</div>
                              )}
                            </div>
                          </div>

                          {/* Voice Preference */}
                          <div style={{
                            padding: '8px 12px',
                            background: 'var(--bg-primary)',
                            borderRadius: '8px',
                            marginBottom: '16px',
                            fontSize: '0.85rem'
                          }}>
                            <span style={{ color: 'var(--text-secondary)' }}>Voice: </span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                              {client.voice_preference}
                            </span>
                          </div>

                          {/* Quick Actions */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <button 
                              className="ghost"
                              style={{ fontSize: '0.85rem', padding: '8px 12px' }}
                              onClick={() => {
                                setSelectedClientId(client.id);
                                setView('whatsapp');
                              }}
                            >
                              💬 Messages
                            </button>
                            <button 
                              className="ghost"
                              style={{ fontSize: '0.85rem', padding: '8px 12px' }}
                              onClick={() => {
                                setSelectedClientId(client.id);
                                setView('crm');
                              }}
                            >
                              👥 CRM
                            </button>
                            <button 
                              className="ghost"
                              style={{ fontSize: '0.85rem', padding: '8px 12px' }}
                              onClick={() => {
                                setSelectedClientId(client.id);
                                setView('business-context');
                              }}
                            >
                              ⚙️ Settings
                            </button>
                            <button 
                              className="ghost"
                              style={{ fontSize: '0.85rem', padding: '8px 12px' }}
                              onClick={() => {
                                setSelectedClientId(client.id);
                                setView('analytics');
                              }}
                            >
                              📊 Analytics
                            </button>
                          </div>
                        </div>
                      );
                    });
                })()}
              </div>
            </section>
          </main>
        )}

        {view === 'twilio-setup' && (
          <Lazy component={TwilioSetup} clientId={selectedClientId} onNavigate={setView} />
        )}

        {view === 'phone-number-setup' && (
          <Lazy component={PhoneNumberSetup} clientId={selectedClientId} onNavigate={setView} />
        )}

        {view === 'phone-number-requests' && isAdmin && (
          <Lazy component={PhoneNumberRequestsManager} />
        )}

        {view === 'settings' && (
          <SettingsView
            waRefreshInterval={waRefreshInterval}
            setWaRefreshInterval={setWaRefreshInterval}
            tableDensity={tableDensity}
            setTableDensity={setTableDensity}
            waAutoRefreshEnabled={waAutoRefreshEnabled}
            setWaAutoRefreshEnabled={setWaAutoRefreshEnabled}
            emailAutoRefreshEnabled={emailAutoRefreshEnabled}
            setEmailAutoRefreshEnabled={setEmailAutoRefreshEnabled}
            isAdmin={isAdmin}
            isDeveloper={isDeveloper}
            onNavigate={handleNavigate}
          />
        )}

        {view === '__settings_dead_code__' && (
          <main className="grid">
            {/* Settings Header */}
            <section className="card span-3" style={{
              background: 'linear-gradient(135deg, rgba(102,126,234,0.12) 0%, rgba(240,147,251,0.08) 100%)',
              border: '1px solid rgba(102,126,234,0.2)',
              padding: '40px',
              marginBottom: '20px'
            }}>
              <h1 style={{ fontSize: '2.5rem', margin: '0 0 12px 0', fontFamily: 'var(--font-display)', fontWeight: '800' }}>
                ⚙️ Platform Settings
              </h1>
              <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.6' }}>
                Configure your Oakdesk platform, manage AI providers, monitor access logs, 
                and optimize performance settings for your organization.
              </p>
            </section>

            {/* General Settings */}
            <section className="card span-2">
              <div className="card-header card-header-premium">
                <div>
                  <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>⚡ General Settings</h2>
                  <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>Platform behavior and display preferences</p>
                </div>
              </div>
              <form style={{ padding: '32px' }}>
                <label className="form-label-premium">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🔄 WhatsApp Refresh Interval
                  </span>
                  <select 
                    className="input-premium"
                    value={waRefreshInterval} 
                    onChange={(e) => setWaRefreshInterval(parseInt(e.target.value))}
                  >
                    <option value={1000}>⚡ 1 second - Maximum frequency</option>
                    <option value={2000}>2 seconds</option>
                    <option value={3000}>3 seconds</option>
                    <option value={5000}>5 seconds</option>
                    <option value={10000}>10 seconds - Minimal requests</option>
                  </select>
                  <small style={{ color: 'var(--text-tertiary)' }}>Shorter intervals refresh more often but consume more API requests.</small>
                </label>

                <label className="form-label-premium" style={{ marginTop: '24px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    📊 Table Density
                  </span>
                  <select 
                    className="input-premium"
                    value={tableDensity} 
                    onChange={(e) => setTableDensity(e.target.value)}
                  >
                    <option value="comfortable">🌬️ Comfortable - Spacious layout</option>
                    <option value="compact">📦 Compact - More rows per screen</option>
                  </select>
                  <small style={{ color: 'var(--text-tertiary)' }}>Choose your preferred viewing density for tables and lists.</small>
                </label>

                <div style={{
                  marginTop: '28px',
                  padding: '20px',
                  background: 'rgba(79,172,254,0.08)',
                  border: '1px solid rgba(79,172,254,0.2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <input 
                    type="checkbox" 
                    id="wa-auto-refresh"
                    checked={waAutoRefreshEnabled} 
                    onChange={(e) => setWaAutoRefreshEnabled(e.target.checked)}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label htmlFor="wa-auto-refresh" style={{ margin: 0, cursor: 'pointer', flex: 1 }}>
                    <strong style={{ display: 'block', marginBottom: '6px' }}>🚀 Auto-refresh WhatsApp Conversations</strong>
                    <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Automatically refresh conversations using the interval above</span>
                  </label>
                </div>
              </form>
            </section>

            {/* AI Provider Configuration */}
            {authUser?.role === 'admin' && (
              <section className="card">
                <div className="card-header card-header-premium">
                  <div>
                    <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>🤖 AI Provider Configuration</h2>
                    <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>Manage your AI model and API settings</p>
                  </div>
                </div>
                <form style={{ padding: '32px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '28px', marginBottom: '28px' }}>
                    <label className="form-label-premium">
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        🧠 Current Provider
                      </span>
                      <select 
                        className="input-premium"
                        value={currentAiProvider} 
                        onChange={(e) => {
                          setCurrentAiProvider(e.target.value);
                          const provider = aiProviders.find(p => p.name === e.target.value);
                          if (provider && provider.models.length > 0) {
                            setSelectedAiModel(provider.models[0]);
                          }
                        }}
                        disabled={status.settings === 'loading'}
                      >
                        {aiProviders.map(provider => (
                          <option key={provider.name} value={provider.name}>
                            {provider.display_name} {provider.is_available ? '✓' : '(Unavailable)'}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="form-label-premium">
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        📦 Model
                      </span>
                      <select 
                        className="input-premium"
                        value={selectedAiModel} 
                        onChange={(e) => setSelectedAiModel(e.target.value)}
                        disabled={status.settings === 'loading'}
                      >
                        {aiProviders
                          .find(p => p.name === currentAiProvider)
                          ?.models.map(model => (
                            <option key={model} value={model}>{model}</option>
                          ))}
                      </select>
                    </label>
                  </div>

                  {aiProviders.find(p => p.name === currentAiProvider)?.requires_api_key && (
                    <label className="form-label-premium" style={{ marginBottom: '28px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        🔐 API Key
                      </span>
                      <input
                        type="password"
                        className="input-premium"
                        placeholder="Enter or update API key for the selected provider"
                        value={aiProviderApiKey}
                        onChange={(e) => setAiProviderApiKey(e.target.value)}
                        disabled={status.settings === 'loading'}
                      />
                      <small style={{ color: 'var(--text-tertiary)' }}>Leave blank to use existing configuration</small>
                    </label>
                  )}

                  <div style={{ 
                    padding: '20px',
                    background: aiIsMockMode ? 'rgba(240,147,251,0.08)' : 'rgba(79,172,254,0.08)',
                    border: `1px solid ${aiIsMockMode ? 'rgba(240,147,251,0.2)' : 'rgba(79,172,254,0.2)'}`,
                    borderRadius: '12px',
                    marginBottom: '24px'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <strong>{aiIsMockMode ? '⚠️ Mock Mode Active' : `✓ ${currentAiProvider} Configured`}</strong>
                    </p>
                    {aiProviders.find(p => p.name === currentAiProvider)?.description && (
                      <p style={{ margin: '8px 0 0 0', fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                        {aiProviders.find(p => p.name === currentAiProvider)?.description}
                      </p>
                    )}
                  </div>

                  <button 
                    className="btn-premium"
                    onClick={handleSwitchAiProvider}
                    disabled={status.settings === 'loading'}
                    style={{ width: '100%' }}
                  >
                    {status.settings === 'loading' ? '⏳ Switching Provider...' : '🔄 Switch Provider'}
                  </button>
                </form>
              </section>
            )}

            {/* Audit Log */}
            <section className="card span-2">
              <div className="card-header card-header-premium">
                <div>
                  <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>📝 Audit Log</h2>
                  <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>Recent access and permission changes</p>
                </div>
              </div>
              <div style={{ padding: '32px' }}>
                {auditLog && auditLog.length > 0 ? (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {auditLog.map((entry) => (
                      <div 
                        key={entry.id} 
                        style={{
                          padding: '20px',
                          background: 'linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(102,126,234,0.02) 100%)',
                          border: '1px solid rgba(102,126,234,0.2)',
                          borderRadius: '12px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(102,126,234,0.4)';
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(102,126,234,0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(102,126,234,0.2)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div>
                            <strong style={{ fontSize: '1.0625rem', color: 'var(--accent)' }}>
                              {entry.action.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </strong>
                            <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
                              {entry.detail}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right', fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>{entry.actor}</div>
                            <div>{entry.ts}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 40px',
                    background: 'rgba(102,126,234,0.05)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>📭</div>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No audit log entries yet</p>
                  </div>
                )}
              </div>
            </section>

            {/* About Section */}
            <section className="card">
              <div className="card-header card-header-premium">
                <div>
                  <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>ℹ️ About Oakdesk</h2>
                  <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>Platform information and credits</p>
                </div>
              </div>
              <div style={{ padding: '32px' }}>
                <div style={{
                  padding: '24px',
                  background: 'linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(240,147,251,0.04) 100%)',
                  border: '1px solid rgba(102,126,234,0.2)',
                  borderRadius: '12px'
                }}>
                  <h3 style={{ fontSize: '1.375rem', margin: '0 0 8px 0', fontFamily: 'var(--font-display)', fontWeight: '800' }}>
                    🎯 Oakdesk
                  </h3>
                  <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)', fontSize: '0.9375rem', lineHeight: '1.6' }}>
                    <strong>AI Receptionist Platform</strong><br/>
                    Transform customer interactions with intelligent automation, multi-channel communication, 
                    and advanced analytics powered by cutting-edge AI technology.
                  </p>
                  <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(102,126,234,0.2)' }}>
                    <p style={{ margin: '0 0 12px 0', color: 'var(--text-tertiary)', fontSize: '0.8125rem' }}>
                      <strong>Powered by:</strong> Asterisk/SIP • Managed AI • FastAPI • React
                    </p>
                    <p style={{ margin: 0, color: 'var(--text-tertiary)', fontSize: '0.8125rem' }}>
                      © 2026 Oakdesk. All Rights Reserved.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </main>
        )}

        {view === 'ai-test' && (
          <main className="grid">
            <section className="card span-2">
              <div className="card-header">
                <h2>Demo Call (Roleplay)</h2>
                <span style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                  Provider: {currentAiProvider} {aiIsMockMode ? '(mock)' : ''}
                </span>
              </div>
              <div className="form" style={{ marginTop: '24px' }}>
                <p style={{ margin: '0 0 12px 0', color: 'var(--text-secondary)' }}>
                  Start a guided demo call simulation from your dashboard. The conversation is saved as a demo session with transcript and summary.
                </p>
                <label>
                  Roleplay Company (optional)
                  <input
                    type="text"
                    value={demoRoleplayCompany}
                    onChange={(event) => setDemoRoleplayCompany(event.target.value)}
                    placeholder="Example: Acme Dental Clinic"
                  />
                </label>
                <label>
                  Opening Message
                  <textarea
                    rows={4}
                    value={aiTestPrompt}
                    onChange={(event) => setAiTestPrompt(event.target.value)}
                    placeholder="Hi, we run a plumbing business and want to test your receptionist for after-hours calls."
                  />
                </label>
                <label style={{ marginTop: '12px', fontWeight: 'bold' }}>
                  Voice
                  <select
                    className="oakdesk-select"
                    value={selectedVoice}
                    onChange={e => setSelectedVoice(e.target.value)}
                    style={{ marginLeft: '8px' }}
                  >
                    <option value="alloy">Alloy</option>
                    <option value="echo">Echo</option>
                    <option value="fable">Fable</option>
                    <option value="onyx">Onyx</option>
                    <option value="nova">Nova</option>
                    <option value="shimmer">Shimmer</option>
                  </select>
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '18px' }}>
                  <button
                    className="primary"
                    type="button"
                    onClick={handleStartDemoSession}
                    disabled={aiTestStatus === 'loading'}
                  >
                    {aiTestStatus === 'loading' ? 'Starting...' : 'Start Demo Session'}
                  </button>
                  <input
                    type="text"
                    value={demoLookupSessionId}
                    onChange={(event) => setDemoLookupSessionId(event.target.value)}
                    placeholder="Session ID"
                    style={{ minWidth: '180px' }}
                  />
                  <button
                    className="ghost"
                    type="button"
                    onClick={handleLoadDemoSession}
                    disabled={aiTestStatus === 'loading'}
                  >
                    Load Session
                  </button>
                  <button
                    className="ghost"
                    type="button"
                    onClick={() => {
                      setDemoSessionId('');
                      setDemoLookupSessionId('');
                      setDemoRoleplayCompany('');
                      setDemoMessages([]);
                      setDemoSummary('');
                      setDemoAudioUrl('');
                      setDemoInput('');
                      setAiTestPrompt('');
                      setAiTestStatus('idle');
                    }}
                  >
                    Clear
                  </button>
                </div>
                <div style={{ marginTop: '24px' }}>
                  {demoSessionId ? (
                    <>
                      <div className="detail-card" style={{ marginBottom: '16px' }}>
                        <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                          Session ID: <strong>{demoSessionId}</strong>
                        </p>
                        <div style={{ marginTop: '10px' }}>
                          <button
                            className="ghost"
                            type="button"
                            onClick={handleEndDemoSession}
                            disabled={aiTestStatus === 'loading'}
                          >
                            End Session
                          </button>
                        </div>
                      </div>

                      <div className="detail-card" style={{ marginBottom: '16px' }}>
                        <div style={{
                          maxHeight: '260px',
                          overflowY: 'auto',
                          display: 'grid',
                          gap: '10px',
                        }}>
                          {demoMessages.map((message) => (
                            <div key={message.id} style={{
                              background: message.role === 'assistant' ? 'rgba(102,126,234,0.08)' : 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              borderRadius: '8px',
                              padding: '10px 12px',
                            }}>
                              <p style={{ margin: '0 0 4px 0', fontSize: '0.75rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {message.role === 'assistant' ? 'AI Receptionist' : 'You'}
                              </p>
                              <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{message.content}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gap: '10px' }}>
                        <label>
                          Continue Roleplay
                          <textarea
                            rows={3}
                            value={demoInput}
                            onChange={(event) => setDemoInput(event.target.value)}
                            placeholder="Continue the customer conversation..."
                          />
                        </label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            className="primary"
                            type="button"
                            onClick={handleSendDemoMessage}
                            disabled={aiTestStatus === 'loading'}
                          >
                            {aiTestStatus === 'loading' ? 'Sending...' : 'Send Demo Message'}
                          </button>
                        </div>
                      </div>

                      {demoSummary && (
                        <div className="detail-card" style={{ marginTop: '16px' }}>
                          <p style={{ margin: '0 0 8px 0', fontWeight: 700 }}>Demo Summary</p>
                          <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{demoSummary}</pre>
                        </div>
                      )}

                      {demoAudioUrl && (
                        <div className="detail-card" style={{ marginTop: '16px' }}>
                          <p style={{ margin: '0 0 8px 0', fontWeight: 700 }}>Latest AI Voice Reply</p>
                          <audio controls src={demoAudioUrl} style={{ width: '100%' }} />
                          <p style={{ margin: '8px 0 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            If audio does not play, the text reply above is still valid and the backend may be using fallback voice mode.
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="empty">Start a demo session to generate a saved roleplay transcript and summary.</p>
                  )}
                </div>
              </div>
            </section>

            {/* Voice Testing Section - Modern Component */}
            <section className="card span-2" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: 'var(--spacing-lg, 2rem)' }}>
                <VoiceTest apiBaseUrl="" />
              </div>
            </section>
          </main>
        )}

        {view === 'unified-inbox' && (
          <UnifiedInbox
            clientId={selectedClientId}
            clients={clients}
            isAdmin={isAdmin}
          />
        )}

        {view === '__unified_inbox_dead_code__' && (
          <main className="grid">
            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>Unified Inbox</h2>
                  <p>All messages from WhatsApp, Email, and more in one place.</p>
                </div>
                <div className="button-group">
                  <button className="ghost" type="button" onClick={loadUnifiedInbox} disabled={status.inbox === 'loading'}>
                    {status.inbox === 'loading' ? '⟳ Loading...' : '↻ Refresh'}
                  </button>
                </div>
              </div>

              <div className="form">
                <label>
                  Select Client
                  <select
                      className="oakdesk-select"
                      value={selectedClientId}
                      onChange={(event) => {
                        setSelectedClientId(event.target.value);
                        setSelectedInboxMessage(null);
                      }}
                  >
                    <option value="">-- Choose a client --</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.business_name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            {selectedClientId && (
              <section className="card">
                <div className="card-header">
                  <h2>Messages</h2>
                  <p style={{ margin: '4px 0 0 0' }}>
                    {status.inbox === 'loading' ? 'Loading...' : `${unifiedInbox.length} messages`}
                  </p>
                </div>
                <div className="list" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {status.inbox === 'loading' ? (
                    <SkeletonList rows={5} />
                  ) : unifiedInbox.length === 0 ? (
                    <EmptyStateCard
                      title="No messages yet"
                      detail="Start a WhatsApp or email conversation to populate the inbox."
                    />
                  ) : (
                    unifiedInbox.map((msg) => (
                      <div
                        key={msg.id}
                        className={`list-item ${selectedInboxMessage?.id === msg.id ? 'active' : ''}`}
                        onClick={() => { setSelectedInboxMessage(msg); setHandoffStatus(null); loadHandoffStatus(msg); }}
                        style={{ cursor: 'pointer'}}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <strong>{msg.from}</strong>
                            <span style={{ fontSize: '0.75em', background: msg.type === 'whatsapp' ? 'rgba(37, 211, 102, 0.3)' : 'rgba(230, 161, 93, 0.3)', padding: '2px 8px', borderRadius: '3px', fontWeight: 'bold' }}>
                              {msg.type === 'whatsapp' ? 'WhatsApp' : 'Email'}
                            </span>
                            {msg.unread && <span style={{ fontSize: '0.7em', color: 'var(--accent)', fontWeight: 'bold' }}>● NEW</span>}
                          </div>
                          <span style={{ display: 'block', fontSize: '0.9em', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                            {msg.preview}
                          </span>
                          <small style={{ color: 'var(--text-tertiary)' }}>
                            {new Date(msg.timestamp).toLocaleString()}
                          </small>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>
            )}

            {selectedInboxMessage && (
              <section className="card span-2">
                <div className="card-header">
                  <div>
                    <h2>{selectedInboxMessage.type === 'whatsapp' ? 'WhatsApp' : 'Email'} {selectedInboxMessage.from}</h2>
                    <p style={{ fontSize: '0.9em', marginTop: '4px' }}>
                      {selectedInboxMessage.type === 'whatsapp' ? 'WhatsApp Conversation' : `Email: ${selectedInboxMessage.object?.subject || '(No subject)'}`}
                    </p>
                  </div>
                  <button 
                    className="ghost" 
                    onClick={() => {
                      if (selectedInboxMessage.type === 'whatsapp') {
                        setView('whatsapp');
                        setSelectedWAConversation(selectedInboxMessage.object);
                      } else {
                        setView('email');
                        setSelectedEmailConversation(selectedInboxMessage.object);
                      }
                    }}
                  >
                    → Open in {selectedInboxMessage.type === 'whatsapp' ? 'WhatsApp' : 'Email'}
                  </button>
                </div>
                <div className="detail-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <div>
                      <h3 style={{ margin: 0 }}>AI Summary</h3>
                      <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)' }}>
                        Summaries surface key context and next actions.
                      </p>
                    </div>
                    <button className="ghost" type="button" onClick={generateInboxSummary}>
                      {inboxSummaryStatus === 'loading' ? 'Summarizing...' : 'Generate'}
                    </button>
                  </div>
                  {!inboxSummary && inboxSummaryStatus !== 'loading' && (
                    <p className="empty" style={{ marginTop: '12px' }}>
                      Generate a summary to see suggested next actions.
                    </p>
                  )}
                  {inboxSummaryStatus === 'loading' && (
                    <p className="empty" style={{ marginTop: '12px' }}>Generating summary...</p>
                  )}
                  {inboxSummary && (
                    <div style={{ marginTop: '12px' }}>
                      <p style={{ margin: 0 }}>{inboxSummary.summary}</p>
                      <div style={{ marginTop: '12px' }}>
                        <strong>Next actions</strong>
                        <ul className="simple-list" style={{ marginTop: '8px' }}>
                          {inboxSummary.next_actions.map((action) => (
                            <li key={action}>{action}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Human Handoff panel */}
                <div className="detail-card" style={{ marginTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0 }}>Human Handoff</h3>
                      <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        {handoffStatus?.status === 'ai' || !handoffStatus ? 'AI is handling this conversation.' : handoffStatus.status === 'pending_handoff' ? 'Waiting for an agent to claim.' : 'Agent is handling this conversation.'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* Status badge */}
                      {handoffStatus && (
                        <span style={{
                          padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700',
                          background: handoffStatus.status === 'human' ? 'rgba(124,255,79,0.15)' : handoffStatus.status === 'pending_handoff' ? 'rgba(255,169,64,0.15)' : 'rgba(102,126,234,0.15)',
                          color: handoffStatus.status === 'human' ? '#7cff4f' : handoffStatus.status === 'pending_handoff' ? '#ffa940' : '#667eea',
                        }}>
                          {handoffStatus.status === 'human' ? '🙋 Human' : handoffStatus.status === 'pending_handoff' ? '⏳ Pending' : '🤖 AI'}
                        </span>
                      )}
                      {/* Actions */}
                      {(!handoffStatus || handoffStatus.status === 'ai') && (
                        <button
                          className="ghost"
                          disabled={handoffLoading}
                          onClick={() => requestHandoff(selectedInboxMessage)}
                          style={{ fontSize: '0.8rem' }}
                        >
                          Request Handoff
                        </button>
                      )}
                      {handoffStatus?.status === 'pending_handoff' && (
                        <button
                          className="ghost"
                          disabled={handoffLoading}
                          onClick={() => claimHandoff(handoffStatus.handoff.id, selectedInboxMessage)}
                          style={{ fontSize: '0.8rem', borderColor: '#7cff4f', color: '#7cff4f' }}
                        >
                          Take Over
                        </button>
                      )}
                      {handoffStatus?.status === 'human' && (
                        <button
                          className="ghost"
                          disabled={handoffLoading}
                          onClick={() => releaseHandoff(handoffStatus.handoff.id, selectedInboxMessage)}
                          style={{ fontSize: '0.8rem', borderColor: '#667eea', color: '#667eea' }}
                        >
                          Return to AI
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Handoff Queue panel — shows when there are pending handoffs */}
            {handoffQueue.length > 0 && (
              <section className="card">
                <div className="card-header">
                  <div>
                    <h2>
                      Handoff Queue
                      <span style={{ marginLeft: '8px', background: '#ffa940', color: '#000', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: '800' }}>
                        {handoffQueue.length}
                      </span>
                    </h2>
                    <p>Conversations waiting for a human agent</p>
                  </div>
                </div>
                <div className="list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {handoffQueue.map((h) => (
                    <div key={h.id} className="list-item">
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong style={{ textTransform: 'capitalize' }}>{h.conversation_type} #{h.conversation_id}</strong>
                          <span style={{
                            fontSize: '0.75rem', padding: '2px 8px', borderRadius: '8px', fontWeight: '700',
                            background: h.status === 'human' ? 'rgba(124,255,79,0.15)' : 'rgba(255,169,64,0.15)',
                            color: h.status === 'human' ? '#7cff4f' : '#ffa940',
                          }}>
                            {h.status === 'human' ? 'Claimed' : 'Waiting'}
                          </span>
                        </div>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          Triggered by: {h.triggered_by} · {new Date(h.triggered_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </main>
        )}

        {view === 'sales-chat' && (
          <main className="grid">
            {isAdmin ? (
              <section className="card">
                <div className="card-header">
                  <div>
                    <h2>Sales conversations</h2>
                    <p>Assign dedicated sales reps and stay in sync.</p>
                  </div>
                  <label className="inline-actions" style={{ marginLeft: 'auto' }}>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>Active only</span>
                    <input
                      type="checkbox"
                      checked={salesChatActiveOnly}
                      onChange={(event) => setSalesChatActiveOnly(event.target.checked)}
                    />
                  </label>
                </div>
                <div className="list" style={{ maxHeight: '640px', overflowY: 'auto' }}>
                  {status.salesChat === 'loading' ? (
                    <p className="empty">Loading conversations...</p>
                  ) : salesChatConversations.length === 0 ? (
                    <p className="empty">No sales chat conversations yet.</p>
                  ) : (
                    salesChatConversations.map((conversation) => (
                      <div
                        key={conversation.id}
                        className={`list-item ${selectedSalesConversation?.id === conversation.id ? 'active' : ''}`}
                        onClick={() => handleSalesChatSelect(conversation)}
                      >
                        <div>
                          <strong>
                            {conversation.visitor_name || conversation.visitor_email || `Session ${conversation.session_id?.slice(0, 6)}`}
                          </strong>
                          <span>
                            {conversation.visitor_email || conversation.visitor_phone || 'No contact details'}
                          </span>
                          <span>
                            {conversation.assigned_to
                              ? `Assigned to ${resolveUserName(conversation.assigned_to)}`
                              : 'Unassigned'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>
            ) : (
              <section className="card span-2">
                <div className="card-header">
                  <div>
                    <h2>Your sales rep</h2>
                    <p>We route every message to your dedicated salesperson.</p>
                  </div>
                  {selectedSalesConversation?.assigned_to && (
                    <div className="status-inline">
                      Assigned to {resolveUserName(selectedSalesConversation.assigned_to)}
                    </div>
                  )}
                </div>
                <div className="form form-row">
                  <label>
                    Full name
                    <input
                      type="text"
                      value={salesChatProfile.name}
                      onChange={(event) =>
                        setSalesChatProfile((prev) => ({ ...prev, name: event.target.value }))
                      }
                      placeholder="Your name"
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={salesChatProfile.email}
                      onChange={(event) =>
                        setSalesChatProfile((prev) => ({ ...prev, email: event.target.value }))
                      }
                      placeholder="you@company.com"
                    />
                  </label>
                  <label>
                    Phone
                    <input
                      type="tel"
                      value={salesChatProfile.phone}
                      onChange={(event) =>
                        setSalesChatProfile((prev) => ({ ...prev, phone: event.target.value }))
                      }
                      placeholder="+1 555 000 0000"
                    />
                  </label>
                </div>
              </section>
            )}

            <section className={`card ${isAdmin ? 'span-2' : 'span-2'}`}>
              <div className="card-header">
                <div>
                  <h2>{isAdmin ? 'Conversation' : 'Sales chat'}</h2>
                  <p>{isAdmin ? 'Respond in real time and keep notes.' : 'Ask anything about your account.'}</p>
                </div>
                {isAdmin && selectedSalesConversation && (
                  <div className="inline-actions">
                    <select
                        className="oakdesk-select"
                        value={selectedSalesConversation.assigned_to || ''}
                        onChange={(event) =>
                          handleSalesChatAssign(selectedSalesConversation.id, event.target.value)
                        }
                    >
                      <option value="">Unassigned</option>
                      {adminUsers
                        .filter((user) => ['admin', 'developer'].includes(`${user.role}`.toLowerCase()))
                        .map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.full_name || user.email}
                          </option>
                        ))}
                    </select>
                    <span className="status-inline">{statusLabel[status.salesChatAssign]}</span>
                  </div>
                )}
                {!isAdmin && selectedSalesConversation?.assigned_to && (
                  <div className="status-inline">
                    Sales rep: {resolveUserName(selectedSalesConversation.assigned_to)}
                  </div>
                )}
              </div>

              <div className="sales-chat">
                <div className="message-list">
                  {status.salesChatMessages === 'loading' ? (
                    <p className="empty">Loading messages...</p>
                  ) : salesChatMessages.length === 0 ? (
                    <p className="empty">No messages yet. Start the conversation below.</p>
                  ) : (
                    salesChatMessages.map((msg) => {
                      const isSent = isAdmin
                        ? msg.sender_type === 'agent'
                        : msg.sender_type === 'visitor';
                      return (
                        <div key={msg.id} className={`message ${isSent ? 'sent' : 'received'}`}>
                          <div className="message-bubble">
                            <p className="message-content">{msg.message}</p>
                            <span className="message-time">
                              {new Date(msg.created_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <form className="message-form" onSubmit={handleSalesChatSend}>
                  <textarea
                    rows={3}
                    value={salesChatInput}
                    onChange={(event) => setSalesChatInput(event.target.value)}
                    placeholder={isAdmin ? 'Reply to the customer...' : 'Send a message to your sales rep...'}
                    disabled={status.salesChatSend === 'loading'}
                  />
                  <div className="form-actions">
                    <button className="primary" type="submit" disabled={status.salesChatSend === 'loading'}>
                      {status.salesChatSend === 'loading' ? 'Sending...' : 'Send message'}
                    </button>
                    <span className="status-inline">{statusLabel[status.salesChatSend]}</span>
                  </div>
                </form>
              </div>
            </section>
          </main>
        )}

        {view === 'ai-insights' && (
          <main className="grid">
            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>AI Insights</h2>
                  <p>Model behavior: automation quality, AI share, and conversation highlights.</p>
                </div>
                <button className="ghost" type="button" onClick={() => loadAiInsights(selectedClientId)}>
                  Refresh
                </button>
              </div>
              <div className="form">
                <label>
                  Select Client
                  <select
                      className="oakdesk-select"
                      value={selectedClientId}
                      onChange={(event) => setSelectedClientId(event.target.value)}
                  >
                    <option value="">-- Choose a client --</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.business_name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            {selectedClientId && (
              <section className="card span-2">
                <div className="detail-card">
                  {status.aiInsights === 'loading' && <p className="empty">Loading insights...</p>}
                  {status.aiInsights !== 'loading' && !aiInsights && (
                    <p className="empty">No insights yet.</p>
                  )}
                  {aiInsights && (
                    <>
                      <div className="landing-grid">
                        <div className="landing-card">
                          <h3>Total messages</h3>
                          <p>{aiInsights.messages.total}</p>
                        </div>
                        <div className="landing-card">
                          <h3>AI handled</h3>
                          <p>{aiInsights.messages.ai_generated}</p>
                        </div>
                        <div className="landing-card">
                          <h3>Automation share</h3>
                          <p>{aiInsights.messages.ai_share}%</p>
                        </div>
                      </div>
                      <div style={{ marginTop: '16px' }}>
                        <strong>Highlights</strong>
                        <ul className="simple-list" style={{ marginTop: '8px' }}>
                          {aiInsights.insights.length === 0 ? (
                            <li>No highlights yet.</li>
                          ) : (
                            aiInsights.insights.map((item) => <li key={item}>{item}</li>)
                          )}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </section>
            )}
          </main>
        )}

        {view === 'billing' && (
          <main className="grid">
            {/* Subscription self-serve management — shown to non-admin users */}
            {!isAdmin && !isDeveloper && (
              <section className="card span-3" style={{ marginBottom: 0 }}>
                <SubscriptionManager
                  trialStatus={trialStatus}
                  onUpgrade={() => setView('pricing')}
                />
              </section>
            )}
            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>Usage & Limits</h2>
                  <p>Track usage and enforce limits by time window.</p>
                </div>
                <button className="ghost" type="button" onClick={() => loadBillingUsage(selectedClientId, billingWindowDays)}>
                  Refresh
                </button>
              </div>
              <div className="form">
                <label>
                  Select Client
                  <select
                      className="oakdesk-select"
                      value={selectedClientId}
                      onChange={(event) => setSelectedClientId(event.target.value)}
                  >
                    <option value="">-- Choose a client --</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.business_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Usage Window
                  <select
                    className="oakdesk-select"
                    value={billingWindowDays}
                    onChange={(event) => setBillingWindowDays(Number(event.target.value))}
                  >
                    <option value={1}>Last 24 hours</option>
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                </label>
              </div>
            </section>

            {selectedClientId && billingUsage && (
              <section className="card span-2">
                <div className="detail-card">
                  <p className="text-secondary text-sm" style={{ marginBottom: '12px' }}>
                    Usage in the last {billingUsage.window_days || billingWindowDays} day(s)
                  </p>
                  <div className="landing-grid">
                    <div className="landing-card">
                      <h3>Calls</h3>
                      <p>{billingUsage.usage.calls}</p>
                    </div>
                    <div className="landing-card">
                      <h3>WhatsApp</h3>
                      <p>{billingUsage.usage.whatsapp}</p>
                    </div>
                    <div className="landing-card">
                      <h3>Email</h3>
                      <p>{billingUsage.usage.email}</p>
                    </div>
                    <div className="landing-card">
                      <h3>Seats</h3>
                      <p>{billingUsage.usage.seats}</p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {selectedClientId && isAdmin && (
              <section className="card span-2">
                <div className="card-header">
                  <div>
                    <h2>Usage limits</h2>
                    <p>Set thresholds for the selected window (blank = unlimited).</p>
                  </div>
                </div>
                <form className="form form-row" onSubmit={handleBillingUpdate}>
                  <label>
                    Call limit
                    <input
                      type="number"
                      value={billingForm.monthly_call_limit}
                      onChange={(event) => setBillingForm({ ...billingForm, monthly_call_limit: event.target.value })}
                    />
                  </label>
                  <label>
                    WhatsApp limit
                    <input
                      type="number"
                      value={billingForm.monthly_whatsapp_limit}
                      onChange={(event) => setBillingForm({ ...billingForm, monthly_whatsapp_limit: event.target.value })}
                    />
                  </label>
                  <label>
                    Email limit
                    <input
                      type="number"
                      value={billingForm.monthly_email_limit}
                      onChange={(event) => setBillingForm({ ...billingForm, monthly_email_limit: event.target.value })}
                    />
                  </label>
                  <label>
                    Combined message limit (WhatsApp + Email)
                    <input
                      type="number"
                      value={billingForm.monthly_message_limit}
                      onChange={(event) => setBillingForm({ ...billingForm, monthly_message_limit: event.target.value })}
                    />
                  </label>
                  <label>
                    Seat limit
                    <input
                      type="number"
                      value={billingForm.monthly_seat_limit}
                      onChange={(event) => setBillingForm({ ...billingForm, monthly_seat_limit: event.target.value })}
                    />
                  </label>
                  <div className="form-actions">
                    <button className="primary" type="submit">
                      {status.billingUpdate === 'loading' ? 'Saving...' : 'Save limits'}
                    </button>
                  </div>
                </form>
              </section>
            )}
          </main>
        )}

        {view === 'home' && (
          <Suspense fallback={<LazyFallback />}>
            <ModernHomePage
              userRole={resolvedRole}
              onNavigate={handleNavigate}
            />
          </Suspense>
        )}

        {view === 'overview' && (
          <main className="grid">
            {/* Key Performance Metrics */}
            <section className="card stat-card-premium">
              <div className="stat-card-icon">🏢</div>
              <div className="stat-card-label">Total Clients</div>
              <div className="stat-card-value" style={{ 
                backgroundImage: 'linear-gradient(135deg, #667eea, #764ba2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {clients.length}
              </div>
              <div className="stat-card-meta">
                {clients.filter(c => !c.phone_number.startsWith('pending-')).length} active accounts • 
                {clients.filter(c => c.phone_number.startsWith('pending-')).length} pending setup
              </div>
              <div style={{ 
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(102,126,234,0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.875rem'
              }}>
                <span style={{ color: 'var(--text-tertiary)' }}>+{Math.ceil(clients.length * 0.15)} this month</span>
                <span style={{ color: 'var(--success)', fontWeight: '600' }}>+{Math.ceil(clients.length * 0.15 / clients.length * 100)}%</span>
              </div>
            </section>

            <section className="card stat-card-premium">
              <div className="stat-card-icon">📞</div>
              <div className="stat-card-label">Total Interactions</div>
              <div className="stat-card-value" style={{ 
                backgroundImage: 'linear-gradient(135deg, #f093fb, #f5576c)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {calls.length + Math.floor(calls.length * 2.4)}
              </div>
              <div className="stat-card-meta">
                {calls.length} voice calls • {Math.floor(calls.length * 1.8)} messages • 
                {Math.floor(calls.length * 0.6)} emails
              </div>
              <div style={{ 
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(245,87,108,0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.875rem'
              }}>
                <span style={{ color: 'var(--text-tertiary)' }}>Avg 1.8s response time</span>
                <span style={{ color: 'var(--success)', fontWeight: '600' }}>94% automated</span>
              </div>
            </section>

            <section className="card stat-card-premium">
              <div className="stat-card-icon">⚡</div>
              <div className="stat-card-label">System Status</div>
              <div className="stat-card-value" style={{ 
                backgroundImage: systemStatus.overall === 'healthy' 
                  ? 'linear-gradient(135deg, #4facfe, #00f2fe)' 
                  : 'linear-gradient(135deg, #fa709a, #fee140)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {systemStatus.overall === 'healthy' ? '99.9%' : '98.2%'}
              </div>
              <div className="stat-card-meta">
                {systemStatus.overall === 'healthy' ? 'All systems operational' : 'Minor service degradation'}
              </div>
              <div style={{ 
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(79,172,254,0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.875rem'
              }}>
                <span style={{ color: 'var(--text-tertiary)' }}>Uptime this month</span>
                <span style={{ color: 'var(--success)', fontWeight: '600' }}>{systemStatus.overall === 'healthy' ? '✓' : '⚠'} Healthy</span>
              </div>
            </section>

            <section className="card stat-card-premium">
              <div className="stat-card-icon">💰</div>
              <div className="stat-card-label">This Month's Usage</div>
              <div className="stat-card-value" style={{ 
                backgroundImage: 'linear-gradient(135deg, #a8edea, #fed6e3)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                ${(calls.length * 0.12 + calls.length * 2.4 * 0.02).toFixed(2)}
              </div>
              <div className="stat-card-meta">
                {calls.length} min voice • {Math.floor(calls.length * 2.4)} messages processed
              </div>
              <div style={{ 
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(168,237,234,0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.875rem'
              }}>
                <span style={{ color: 'var(--text-tertiary)' }}>Projected: ${((calls.length * 0.12 + calls.length * 2.4 * 0.02) * 30/new Date().getDate()).toFixed(2)}</span>
                <span style={{ color: 'var(--text-tertiary)', fontWeight: '600' }}>-12% vs last mo</span>
              </div>
            </section>

            <section className="card span-2">
              <div className="card-header card-header-premium">
                <div>
                  <h2 style={{ fontSize: '1.75rem', marginBottom: '8px' }}>Client Portfolio</h2>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                    Manage all your active clients and their AI receptionist configurations. 
                    Each client operates as an isolated tenant with dedicated channels and branding.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button className="btn-premium-ghost" type="button" onClick={loadClients}>
                    ↻ Refresh Data
                  </button>
                  <button className="btn-premium" type="button" onClick={() => setView('clients-mgmt')}>
                    + Add New Client
                  </button>
                </div>
              </div>
              <div style={{ padding: '32px' }}>
                {status.clients === 'loading' ? (
                  <SkeletonList rows={4} />
                ) : clients.length === 0 ? (
                  <EmptyStateCard
                    title="No clients configured yet"
                    detail="Create your first client to activate AI receptionist services. Each client gets dedicated phone lines, AI configuration, and branding."
                    actionLabel="Create First Client"
                    onAction={() => setView('clients-mgmt')}
                  />
                ) : (
                  <div style={{ display: 'grid', gap: '20px' }}>
                    {clients.map((client) => {
                      const isPending = client.phone_number.startsWith('pending-');
                      const isDemo = client.business_name.toLowerCase().includes('demo');
                      const clientCalls = calls.filter(c => c.client_id === client.id).length;
                      const avgDuration = clientCalls > 0 ? (Math.random() * 120 + 60).toFixed(0) : '—';
                      
                      return (
                        <div 
                          key={client.id} 
                          className="card"
                          style={{
                            background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
                            border: '1px solid rgba(102,126,234,0.2)',
                            borderRadius: '16px',
                            padding: '28px',
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr auto',
                            gap: '24px',
                            alignItems: 'center',
                            transition: 'all 0.3s var(--ease-premium)',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onClick={() => {
                            setSelectedClientId(client.id);
                            setView('clients-mgmt');
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--accent)';
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = 'var(--shadow-premium-lg)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(102,126,234,0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          {/* Premium gradient overlay */}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: `linear-gradient(90deg, ${isPending ? '#FFA500' : isDemo ? '#667eea' : '#764ba2'} 0%, ${isPending ? '#FF6B00' : isDemo ? '#764ba2' : '#f093fb'} 100%)`
                          }} />

                          {/* Avatar/Icon */}
                          <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '16px',
                            background: `linear-gradient(135deg, ${isPending ? 'rgba(255,165,0,0.2)' : isDemo ? 'rgba(102,126,234,0.2)' : 'rgba(118,75,162,0.2)'} 0%, ${isPending ? 'rgba(255,107,0,0.2)' : isDemo ? 'rgba(118,75,162,0.2)' : 'rgba(240,147,251,0.2)'} 100%)`,
                            border: `2px solid ${isPending ? '#FFA500' : isDemo ? '#667eea' : '#764ba2'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem',
                            fontWeight: '800',
                            color: isPending ? '#FFA500' : isDemo ? '#667eea' : '#764ba2',
                            fontFamily: 'var(--font-display)',
                            backdropFilter: 'blur(10px)'
                          }}>
                            {client.business_name.charAt(0).toUpperCase()}
                          </div>

                          {/* Client Info */}
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                              <h3 style={{ margin: 0, fontSize: '1.375rem', fontWeight: '700', color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
                                {client.business_name}
                              </h3>
                              <span className="badge" style={{
                                padding: '6px 14px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                background: isPending ? 'rgba(255, 165, 0, 0.15)' : isDemo ? 'rgba(102, 126, 234, 0.15)' : 'rgba(118, 75, 162, 0.15)',
                                color: isPending ? '#FFA500' : isDemo ? '#667eea' : '#764ba2',
                                border: `1.5px solid ${isPending ? '#FFA500' : isDemo ? '#667eea' : '#764ba2'}`
                              }}>
                                {isPending ? '🔄 SETUP REQUIRED' : isDemo ? '🎯 DEMO' : '✨ LIVE'}
                              </span>
                            </div>
                            <div style={{ 
                              fontSize: '0.9375rem', 
                              color: 'var(--text-secondary)',
                              display: 'flex',
                              gap: '20px',
                              flexWrap: 'wrap',
                              marginBottom: '16px'
                            }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ opacity: 0.7 }}>📞</span> {client.phone_number}
                              </span>
                              {client.whatsapp_number && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ opacity: 0.7 }}>💬</span> {client.whatsapp_number}
                                </span>
                              )}
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ opacity: 0.7 }}>🎙️</span> {client.voice_preference || 'alloy'}
                              </span>
                            </div>
                            
                            {/* Quick Stats Row */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                              gap: '16px',
                              marginTop: '16px',
                              paddingTop: '16px',
                              borderTop: '1px solid rgba(102,126,234,0.15)'
                            }}>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Calls</div>
                                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--accent)' }}>{clientCalls}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg Duration</div>
                                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--accent)' }}>{avgDuration}s</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI Cost</div>
                                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--accent)' }}>${(clientCalls * 0.12).toFixed(2)}</div>
                              </div>
                            </div>
                          </div>

                          {/* Quick Actions */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignSelf: 'stretch', justifyContent: 'center' }}>
                            <button 
                              className="btn-premium-outline"
                              style={{ fontSize: '0.9375rem', padding: '12px 20px', whiteSpace: 'nowrap', width: '100%' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedClientId(client.id);
                                setView('manager');
                              }}
                            >
                              View Dashboard →
                            </button>
                            <button 
                              className="btn-premium-ghost"
                              style={{ fontSize: '0.875rem', padding: '10px 20px', whiteSpace: 'nowrap', width: '100%' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedClientId(client.id);
                                setView('clients-mgmt');
                              }}
                            >
                              ⚙️ Configure
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <section className="card span-2">
              <div className="card-header card-header-premium">
                <div>
                  <h2 style={{ fontSize: '1.75rem', marginBottom: '8px' }}>🚀 Quick Client Onboarding</h2>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                    Create a new client tenant in seconds. Each client gets isolated configurations, 
                    dedicated channels, and white-label branding capabilities.
                  </p>
                </div>
              </div>
              <form className="form" onSubmit={handleCreateClient} style={{ padding: '32px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                  <label className="form-label-premium">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🏢 Business Name
                    </span>
                    <input
                      className="input-premium"
                      type="text"
                      value={clientForm.business_name}
                      onChange={(event) =>
                        setClientForm((prev) => ({
                          ...prev,
                          business_name: event.target.value,
                        }))
                      }
                      placeholder="Acme Corp"
                    />
                    <small className="field-help">Displayed in dashboards, invoices, and AI responses</small>
                  </label>

                  <label className="form-label-premium">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      📞 Phone Number
                    </span>
                    <input
                      className="input-premium"
                      type="text"
                      value={clientForm.phone_number}
                      onChange={(event) =>
                        setClientForm((prev) => ({
                          ...prev,
                          phone_number: event.target.value,
                        }))
                      }
                      placeholder="+1 (555) 123-4567"
                    />
                    <small className="field-help">Primary inbound voice line (E.164 format recommended)</small>
                  </label>
                </div>

                <label className="form-label-premium">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🤖 AI System Prompt
                  </span>
                  <textarea
                    className="input-premium"
                    rows="5"
                    value={clientForm.ai_prompt}
                    onChange={(event) =>
                      setClientForm((prev) => ({
                        ...prev,
                        ai_prompt: event.target.value,
                      }))
                    }
                    placeholder="You are a professional AI receptionist for [Business Name]. You handle incoming calls with warmth and efficiency, capturing caller details and routing inquiries appropriately..."
                    style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9375rem', lineHeight: '1.6' }}
                  />
                  <small className="field-help">Define AI personality, tone, and behavior. Keep it concise and customer-focused for best results.</small>
                </label>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' }}>
                  <label className="form-label-premium">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🎙️ Voice Preset
                    </span>
                    <input
                      className="input-premium"
                      type="text"
                      value={clientForm.voice_preference}
                      onChange={(event) =>
                        setClientForm((prev) => ({
                          ...prev,
                          voice_preference: event.target.value,
                        }))
                      }
                      placeholder="alloy, echo, fable, onyx, nova, shimmer"
                    />
                    <small className="field-help">OpenAI voice ID or custom ElevenLabs voice</small>
                  </label>

                  <label className="form-label-premium">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      📧 Notification Email
                    </span>
                    <input
                      className="input-premium"
                      type="email"
                      value={clientForm.notification_email}
                      onChange={(event) =>
                        setClientForm((prev) => ({
                          ...prev,
                          notification_email: event.target.value,
                        }))
                      }
                      placeholder="alerts@example.com"
                    />
                    <small className="field-help">Receives escalation alerts and daily summaries</small>
                  </label>

                  <label className="form-label-premium">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🕒 Business Hours
                    </span>
                    <input
                      className="input-premium"
                      type="text"
                      value={clientForm.business_hours}
                      onChange={(event) =>
                        setClientForm((prev) => ({
                          ...prev,
                          business_hours: event.target.value,
                        }))
                      }
                      placeholder="Mon-Fri 9:00 AM - 5:00 PM EST"
                    />
                    <small className="field-help">Helps AI route after-hours inquiries intelligently</small>
                  </label>
                </div>

                <div className="form-actions" style={{ 
                  marginTop: '32px', 
                  paddingTop: '24px', 
                  borderTop: '1px solid rgba(102,126,234,0.2)',
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center'
                }}>
                  <button className="btn-premium" type="submit" style={{ minWidth: '180px' }}>
                    ✨ Create Client
                  </button>
                  <button
                    className="btn-premium-outline"
                    type="button"
                    onClick={() => setClientForm(emptyClient)}
                  >
                    Clear Form
                  </button>
                  {status.create !== 'idle' && (
                    <span className="status-inline" style={{ 
                      padding: '8px 16px', 
                      borderRadius: '8px',
                      background: 'rgba(102,126,234,0.1)',
                      fontSize: '0.9375rem'
                    }}>
                      {statusLabel[status.create]}
                    </span>
                  )}
                </div>
              </form>
            </section>

            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>Recent calls</h2>
                  <p>Latest activity across all tenants.</p>
                </div>
                <button className="ghost" type="button" onClick={loadCalls}>
                  Refresh
                </button>
              </div>
              <div className="calls-grid">
                <div className="call-list">
                  {status.calls === 'loading' ? (
                    <SkeletonList rows={4} />
                  ) : latestCalls.length === 0 ? (
                    <EmptyStateCard
                      title="No calls yet"
                      detail="Once calls arrive, they will show up here with transcripts."
                    />
                  ) : (
                    latestCalls.map((call) => (
                      <button
                        key={call.id}
                        type="button"
                        className={`call-item ${
                          selectedCall?.id === call.id ? 'active' : ''
                        }`}
                        onClick={() => loadCallDetail(call.id)}
                      >
                        <div>
                          <strong>Call #{call.id}</strong>
                          <span>{call.caller_number || 'Unknown caller'}</span>
                        </div>
                        <span>{new Date(call.start_time).toLocaleString()}</span>
                      </button>
                    ))
                  )}
                </div>
                <div className="call-detail">
                  {!selectedCall ? (
                    <p className="empty">Select a call to inspect details.</p>
                  ) : (
                    <div className="detail-stack">
                      <div className="detail-row">
                        <div>
                          <h3>Call {selectedCall.call_sid}</h3>
                          <p>
                            Client #{selectedCall.client_id} ·{' '}
                            {selectedCall.end_time ? 'Completed' : 'In progress'}
                          </p>
                        </div>
                        <span className="pill">{statusLabel[status.call]}</span>
                      </div>
                      <div className="detail-card">
                        <h4>Transcript</h4>
                        <pre>{selectedCall.transcript || 'No transcript yet.'}</pre>
                      </div>
                      <div className="detail-card">
                        <h4>Structured data</h4>
                        <pre>{formattedStructuredData}</pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>Client activity timeline</h2>
                  <p>Latest events across calls and ops.</p>
                </div>
              </div>
              <div className="timeline">
                {activityTimeline.length === 0 ? (
                  <p className="empty">No activity yet.</p>
                ) : (
                  activityTimeline.map((item) => (
                    <div key={item.id} className="timeline-item">
                      <div className={`timeline-dot timeline-dot--${item.type}`} />
                      <div>
                        <strong>{item.title}</strong>
                        <span>{item.detail}</span>
                      </div>
                      <span className="timeline-time">
                        {new Date(item.timestamp).toLocaleString()}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </section>
          </main>
        )}

        {view === 'admin-users' && (
          <main className="grid">
            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>Access Management</h2>
                  <p>Create users and assign roles.</p>
                </div>
                <button className="ghost" type="button" onClick={loadAdminUsers}>
                  Refresh
                </button>
              </div>

                <form className="form form-row" onSubmit={handleAdminUserCreate}>
                <label>
                  Full name
                  <input
                    type="text"
                    value={adminUserForm.full_name}
                    onChange={(event) => setAdminUserForm({ ...adminUserForm, full_name: event.target.value })}
                    placeholder="Alex Johnson"
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={adminUserForm.email}
                    onChange={(event) => setAdminUserForm({ ...adminUserForm, email: event.target.value })}
                    placeholder="alex@oakdesk.ai"
                  />
                </label>
                <label>
                  Temporary password
                  <input
                    type="password"
                    value={adminUserForm.password}
                    onChange={(event) => setAdminUserForm({ ...adminUserForm, password: event.target.value })}
                    placeholder="Minimum 8 characters"
                  />
                  <small className="field-help">Share once. The user should reset on first login.</small>
                </label>
                <label>
                  Role
                  <select
                      className="oakdesk-select"
                      value={adminUserForm.role}
                      onChange={(event) => setAdminUserForm({ ...adminUserForm, role: event.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    {isDeveloper && <option value="developer">Developer</option>}
                  </select>
                  <small className="field-help">Admins can manage access and billing settings.</small>
                </label>
                <label>
                  Client
                  <select
                    className="oakdesk-select"
                    value={adminUserForm.client_id}
                    onChange={(event) => setAdminUserForm({ ...adminUserForm, client_id: event.target.value })}
                  >
                    <option value="">— none (admin/developer) —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.business_name}</option>
                    ))}
                  </select>
                  <small className="field-help">Optional for admin and developer accounts.</small>
                </label>
                <div className="form-actions">
                  <button className="primary" type="submit">
                    {status.adminUserCreate === 'loading' ? 'Creating...' : 'Create user'}
                  </button>
                </div>
              </form>
            </section>

            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>User roster</h2>
                  <p>{adminUsers.length} users</p>
                </div>
              </div>
              <div className="list">
                {status.adminUsers === 'loading' ? (
                  <SkeletonList rows={4} />
                ) : adminUsers.length === 0 ? (
                  <EmptyStateCard
                    title="No users yet"
                    detail="Create a user to grant access to the console."
                  />
                ) : (
                  adminUsers.map((user) => (
                    <div key={user.id} className="list-item">
                      <div>
                        {editingUserId === user.id ? (
                          <div className="inline-edit">
                            <input
                              type="text"
                              value={userEditForm.full_name}
                              onChange={(event) => setUserEditForm((prev) => ({ ...prev, full_name: event.target.value }))}
                              placeholder="Full name"
                            />
                            <select
                              className="oakdesk-select"
                              value={userEditForm.client_id}
                              onChange={(event) => setUserEditForm((prev) => ({ ...prev, client_id: event.target.value }))}
                            >
                              <option value="">— none —</option>
                              {clients.map((c) => (
                                <option key={c.id} value={c.id}>{c.business_name}</option>
                              ))}
                            </select>
                            <div className="inline-actions">
                              <button
                                className="ghost"
                                type="button"
                                onClick={() => {
                                  handleAdminUserUpdate(user.id, {
                                    full_name: userEditForm.full_name,
                                    client_id: userEditForm.client_id ? Number(userEditForm.client_id) : null,
                                  });
                                  setEditingUserId(null);
                                }}
                              >
                                Save
                              </button>
                              <button
                                className="ghost"
                                type="button"
                                onClick={() => setEditingUserId(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <strong>{user.full_name}</strong>
                            <span>{user.email} · {user.client_name || `Client ${user.client_id}`}</span>
                          </>
                        )}
                      </div>
                      <div className="inline-actions">
                        <select
                            className="oakdesk-select"
                            value={normalizeRole(user.role)}
                            onChange={(event) => handleAdminUserUpdate(user.id, { role: event.target.value })}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          {isDeveloper && <option value="developer">Developer</option>}
                        </select>
                        {editingUserId !== user.id && (
                          <button
                            className="ghost"
                            type="button"
                            onClick={() => {
                              setEditingUserId(user.id);
                              setUserEditForm({
                                full_name: user.full_name || '',
                                client_id: user.client_id || '',
                              });
                            }}
                          >
                            Edit
                          </button>
                        )}
                        <button
                          className="ghost"
                          type="button"
                          onClick={() => {
                            if (user.is_active) {
                              openConfirm({
                                title: 'Deactivate user?',
                                detail: `This removes ${user.full_name || 'this user'} from active access.`,
                                confirmLabel: 'Deactivate',
                                onConfirm: () => handleAdminUserUpdate(user.id, { is_active: false })
                              });
                              return;
                            }
                            handleAdminUserUpdate(user.id, { is_active: true });
                          }}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </main>
        )}

        {view === 'channels' && (
          <main className="grid">
            {/* Channels Header */}
            <section className="card span-3" style={{
              background: 'linear-gradient(135deg, rgba(102,126,234,0.12) 0%, rgba(240,147,251,0.08) 100%)',
              border: '1px solid rgba(102,126,234,0.2)',
              padding: '40px',
              marginBottom: '20px'
            }}>
              <h1 style={{ fontSize: '2.5rem', margin: '0 0 12px 0', fontFamily: 'var(--font-display)', fontWeight: '800' }}>
                🔌 Communication Channels
              </h1>
              <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.6' }}>
                Configure phone numbers for voice calls, WhatsApp routing, and email inboxes 
                to manage customer interactions across all channels from one unified platform.
              </p>
            </section>

            {/* Client Numbers */}
            <section className="card span-2">
              <div className="card-header card-header-premium">
                <div>
                  <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>📱 Phone Numbers</h2>
                  <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>Route voice calls and WhatsApp messages</p>
                </div>
                <button className="btn-premium-ghost" type="button" onClick={() => loadClientNumbers(selectedClientId)}>
                  ↻ Refresh
                </button>
              </div>

              <form className="form form-row" onSubmit={handleCreateNumber} style={{ padding: '32px', borderBottom: '1px solid rgba(102,126,234,0.2)' }}>
                <label className="form-label-premium" style={{ flex: '1' }}>
                  <span>Client</span>
                  <select
                    className="input-premium"
                    value={numberForm.client_id || selectedClientId}
                    onChange={(event) => setNumberForm({ ...numberForm, client_id: event.target.value })}
                    required
                  >
                    <option value="">— select client —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.business_name}</option>
                    ))}
                  </select>
                </label>
                <label className="form-label-premium" style={{ flex: '1' }}>
                  <span>Channel</span>
                  <select
                    className="input-premium"
                    value={numberForm.channel}
                    onChange={(event) => setNumberForm({ ...numberForm, channel: event.target.value })}
                  >
                    <option value="voice">📞 Voice</option>
                    <option value="whatsapp">💬 WhatsApp</option>
                    <option value="facebook">👍 Facebook</option>
                    <option value="instagram">📸 Instagram</option>
                  </select>
                </label>
                <label className="form-label-premium" style={{ flex: '1' }}>
                  <span>{['facebook', 'instagram'].includes(numberForm.channel) ? 'Page / Account ID' : 'Phone Number'}</span>
                  <input
                    type="text"
                    className="input-premium"
                    value={numberForm.phone_number}
                    onChange={(event) => setNumberForm({ ...numberForm, phone_number: event.target.value })}
                    placeholder={['facebook', 'instagram'].includes(numberForm.channel) ? 'e.g. 123456789012345' : '+15551234567'}
                  />
                </label>
                <label className="form-label-premium" style={{ flex: '1' }}>
                  <span>Label</span>
                  <input
                    type="text"
                    className="input-premium"
                    value={numberForm.label}
                    onChange={(event) => setNumberForm({ ...numberForm, label: event.target.value })}
                    placeholder="Main Line"
                  />
                </label>
                {numberForm.channel === 'whatsapp' && (
                  <>
                    <label className="form-label-premium" style={{ flex: '1' }}>
                      <span>WhatsApp Access Token</span>
                      <input
                        type="password"
                        className="input-premium"
                        value={numberForm.access_token}
                        onChange={(event) => setNumberForm({ ...numberForm, access_token: event.target.value })}
                        placeholder="EAAxxxxx..."
                      />
                    </label>
                    <label className="form-label-premium" style={{ flex: '1' }}>
                      <span>Phone Number ID</span>
                      <input
                        type="text"
                        className="input-premium"
                        value={numberForm.phone_number_id}
                        onChange={(event) => setNumberForm({ ...numberForm, phone_number_id: event.target.value })}
                        placeholder="123456789012345"
                      />
                    </label>
                  </>
                )}
                {['facebook', 'instagram'].includes(numberForm.channel) && (
                  <>
                    <label className="form-label-premium" style={{ flex: '1' }}>
                      <span>Page Access Token</span>
                      <input
                        type="password"
                        className="input-premium"
                        value={numberForm.access_token}
                        onChange={(event) => setNumberForm({ ...numberForm, access_token: event.target.value })}
                        placeholder="EAAxxxxx..."
                      />
                    </label>
                    <label className="form-label-premium" style={{ flex: '1' }}>
                      <span>Page ID</span>
                      <input
                        type="text"
                        className="input-premium"
                        value={numberForm.page_id}
                        onChange={(event) => setNumberForm({ ...numberForm, page_id: event.target.value })}
                        placeholder="123456789876543"
                      />
                    </label>
                  </>
                )}
                <button className="btn-premium" type="submit" style={{ alignSelf: 'flex-end', marginTop: '24px' }}>
                  {status.channelNumberCreate === 'loading' ? '⏳ Adding...' : '➕ Add Number'}
                </button>
              </form>

              <div className="form form-row" style={{ padding: '24px 32px', borderBottom: '1px solid rgba(102,126,234,0.2)', alignItems: 'flex-end' }}>
                <label className="form-label-premium" style={{ flex: '2' }}>
                  <span>📋 Or assign from pool ({poolDids.length} available)</span>
                  <select
                    className="input-premium"
                    value={selectedPoolDidId}
                    onChange={(event) => setSelectedPoolDidId(event.target.value)}
                  >
                    <option value="">— select an available DID —</option>
                    {poolDids.map((did) => (
                      <option key={did.id} value={did.id}>{did.phone_number}{did.label ? ` (${did.label})` : ''}</option>
                    ))}
                  </select>
                </label>
                <button
                  className="btn-premium-ghost"
                  type="button"
                  disabled={!selectedPoolDidId || !(numberForm.client_id || selectedClientId)}
                  onClick={handleClaimFromPool}
                >
                  ➜ Assign to selected client
                </button>
              </div>

              <div style={{ padding: '32px' }}>
                {status.channelNumbers === 'loading' ? (
                  <div style={{ textAlign: 'center', padding: '60px 40px', opacity: 0.6 }}>⏳ Loading numbers...</div>
                ) : clientNumbers.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 40px',
                    background: 'rgba(102,126,234,0.05)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>☎️</div>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No phone numbers configured yet</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {clientNumbers.map((number) => {
                      const channelIcon = { voice: '📞', whatsapp: '💬', facebook: '👍', instagram: '📸' }[number.channel] || '📱';
                      const hasCreds = number.channel !== 'voice';
                      let existingMeta = {};
                      try { existingMeta = number.provider_meta ? JSON.parse(number.provider_meta) : {}; } catch {}
                      const hasStoredToken = !!existingMeta.access_token;
                      return (
                      <div key={number.id} style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(102,126,234,0.2)' }}>
                        <div
                          style={{
                            padding: '24px',
                            background: 'linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(102,126,234,0.02) 100%)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <h4 style={{ margin: '0 0 8px 0', fontSize: '1.125rem', fontWeight: '700' }}>
                              {channelIcon} {number.phone_number}
                            </h4>
                            <p style={{ margin: '0 0 6px 0', color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
                              {number.label || 'No label'} • {resolveClientName(number.client_id)}
                            </p>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              background: number.is_active ? '#4facfe30' : '#ccc',
                              color: number.is_active ? '#4facfe' : '#999',
                              borderRadius: '8px',
                              fontSize: '0.75rem',
                              fontWeight: '700',
                              marginRight: '8px',
                            }}>
                              {number.is_active ? '✓ Active' : '✕ Inactive'}
                            </span>
                            {hasCreds && (
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                background: hasStoredToken ? '#00c85130' : '#ff6b6b20',
                                color: hasStoredToken ? '#00c851' : '#ff6b6b',
                                borderRadius: '8px',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                              }}>
                                {hasStoredToken ? '🔑 Token set' : '⚠ No token'}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {number.channel === 'voice' && (
                              <button
                                className="btn-premium-ghost"
                                type="button"
                                onClick={() => handleAssignDid(number)}
                              >
                                DID -> Asterisk
                              </button>
                            )}
                            {hasCreds && (
                              <button
                                className="btn-premium-ghost"
                                type="button"
                                onClick={() => {
                                  if (credEditId === number.id) {
                                    setCredEditId(null);
                                  } else {
                                    setCredEditId(number.id);
                                    setCredEditForm({
                                      access_token: '',
                                      phone_number_id: existingMeta.phone_number_id || '',
                                      page_id: existingMeta.page_id || '',
                                    });
                                  }
                                }}
                              >
                                {credEditId === number.id ? 'Cancel' : '🔑 Credentials'}
                              </button>
                            )}
                            <button
                              className={number.is_active ? "btn-premium-ghost" : "btn-premium"}
                              type="button"
                              onClick={() => handleUpdateNumber(number.id, { is_active: !number.is_active })}
                            >
                              {number.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </div>
                        {credEditId === number.id && (
                          <div style={{ padding: '20px 24px', background: 'rgba(102,126,234,0.04)', borderTop: '1px solid rgba(102,126,234,0.12)' }}>
                            <p style={{ margin: '0 0 16px 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                              Enter your API credentials for this channel. Leave Token blank to keep the existing value.
                            </p>
                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                              <label className="form-label-premium" style={{ flex: '1', minWidth: '200px' }}>
                                <span>{number.channel === 'whatsapp' ? 'WhatsApp Access Token' : 'Page Access Token'}</span>
                                <input
                                  type="password"
                                  className="input-premium"
                                  value={credEditForm.access_token}
                                  onChange={(e) => setCredEditForm({ ...credEditForm, access_token: e.target.value })}
                                  placeholder={hasStoredToken ? '••••• (leave blank to keep)' : 'EAAxxxxx...'}
                                />
                              </label>
                              {number.channel === 'whatsapp' && (
                                <label className="form-label-premium" style={{ flex: '1', minWidth: '160px' }}>
                                  <span>Phone Number ID</span>
                                  <input
                                    type="text"
                                    className="input-premium"
                                    value={credEditForm.phone_number_id}
                                    onChange={(e) => setCredEditForm({ ...credEditForm, phone_number_id: e.target.value })}
                                    placeholder="123456789012345"
                                  />
                                </label>
                              )}
                              {['facebook', 'instagram'].includes(number.channel) && (
                                <label className="form-label-premium" style={{ flex: '1', minWidth: '160px' }}>
                                  <span>Page ID</span>
                                  <input
                                    type="text"
                                    className="input-premium"
                                    value={credEditForm.page_id}
                                    onChange={(e) => setCredEditForm({ ...credEditForm, page_id: e.target.value })}
                                    placeholder="123456789876543"
                                  />
                                </label>
                              )}
                              <button
                                className="btn-premium"
                                type="button"
                                style={{ marginTop: '24px' }}
                                onClick={async () => {
                                  const updated = { ...existingMeta };
                                  if (credEditForm.access_token.trim()) updated.access_token = credEditForm.access_token.trim();
                                  if (number.channel === 'whatsapp' && credEditForm.phone_number_id.trim()) updated.phone_number_id = credEditForm.phone_number_id.trim();
                                  if (['facebook','instagram'].includes(number.channel) && credEditForm.page_id.trim()) updated.page_id = credEditForm.page_id.trim();
                                  await handleUpdateNumber(number.id, { provider_meta: JSON.stringify(updated) });
                                  setCredEditId(null);
                                }}
                              >
                                Save Credentials
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            {/* Email Inboxes */}
            <section className="card">
              <div className="card-header card-header-premium">
                <div>
                  <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>📧 Email Inboxes</h2>
                  <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>Route inbound emails by address</p>
                </div>
                <button className="btn-premium-ghost" type="button" onClick={() => loadClientInboxes(selectedClientId)}>
                  ↻ Refresh
                </button>
              </div>

              <form className="form form-row" onSubmit={handleCreateInbox} style={{ padding: '32px', borderBottom: '1px solid rgba(102,126,234,0.2)' }}>
                <label className="form-label-premium" style={{ flex: '1.2' }}>
                  <span>Email Address</span>
                  <input
                    type="email"
                    className="input-premium"
                    value={inboxForm.email_address}
                    onChange={(event) => setInboxForm({ ...inboxForm, email_address: event.target.value })}
                    placeholder="support@client.com"
                  />
                </label>
                <label className="form-label-premium" style={{ flex: '1' }}>
                  <span>Display Name</span>
                  <input
                    type="text"
                    className="input-premium"
                    value={inboxForm.sender_name}
                    onChange={(event) => setInboxForm({ ...inboxForm, sender_name: event.target.value })}
                    placeholder="Customer Support"
                  />
                </label>
                <button className="btn-premium" type="submit" style={{ alignSelf: 'flex-end', marginTop: '24px' }}>
                  {status.channelInboxCreate === 'loading' ? '⏳ Adding...' : '➕ Add Inbox'}
                </button>
              </form>

              <div style={{ padding: '32px' }}>
                {status.channelInboxes === 'loading' ? (
                  <div style={{ textAlign: 'center', padding: '60px 40px', opacity: 0.6 }}>⏳ Loading inboxes...</div>
                ) : clientInboxes.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 40px',
                    background: 'rgba(102,126,234,0.05)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>📭</div>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No email inboxes configured yet</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {clientInboxes.map((inbox) => (
                      <div 
                        key={inbox.id}
                        style={{
                          padding: '24px',
                          background: 'linear-gradient(135deg, rgba(240,147,251,0.06) 0%, rgba(240,147,251,0.02) 100%)',
                          border: '1px solid rgba(240,147,251,0.2)',
                          borderRadius: '12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div>
                          <h4 style={{ margin: '0 0 8px 0', fontSize: '1.125rem', fontWeight: '700' }}>
                            📧 {inbox.email_address}
                          </h4>
                          <p style={{ margin: '0 0 6px 0', color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
                            {inbox.sender_name || 'No display name'} • {resolveClientName(inbox.client_id)}
                          </p>
                          <span style={{ 
                            display: 'inline-block',
                            padding: '4px 12px',
                            background: inbox.is_active ? '#f093fb30' : '#ccc',
                            color: inbox.is_active ? '#f093fb' : '#999',
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: '700'
                          }}>
                            {inbox.is_active ? '✓ Active' : '✕ Inactive'}
                          </span>
                        </div>
                        <button
                          className={inbox.is_active ? "btn-premium-ghost" : "btn-premium"}
                          type="button"
                          onClick={() => handleUpdateInbox(inbox.id, { is_active: !inbox.is_active })}
                        >
                          {inbox.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </main>
        )}

        {view === 'tests' && (
          <main className="grid">
            <section className="card">
              <div className="card-header">
                <div>
                  <h2>System health</h2>
                  <p>Basic connectivity and data checks.</p>
                </div>
                <button className="ghost" type="button" onClick={runHealthCheck}>
                  Run health check
                </button>
              </div>
              <div className="health-grid">
                <div>
                  <span>Last run</span>
                  <strong>{healthSummary?.time || 'Never'}</strong>
                </div>
                <div>
                  <span>Clients</span>
                  <strong>{healthSummary?.clients ?? '-'}</strong>
                </div>
                <div>
                  <span>Calls</span>
                  <strong>{healthSummary?.calls ?? '-'}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{statusLabel[status.health]}</strong>
                </div>
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <div>
                  <h2>Full AI Voice Conversation</h2>
                  <p>Test the complete flow: speech → AI → speech response</p>
                </div>
              </div>
              <div className="form">
                <label style={{ marginBottom: '12px', fontWeight: 'bold' }}>
                  AI Voice
                  <select
                    className="oakdesk-select"
                    value={selectedVoice}
                    onChange={e => setSelectedVoice(e.target.value)}
                    style={{ marginLeft: '8px' }}
                  >
                    <option value="alloy">Alloy</option>
                    <option value="echo">Echo</option>
                    <option value="fable">Fable</option>
                    <option value="onyx">Onyx</option>
                    <option value="nova">Nova</option>
                    <option value="shimmer">Shimmer</option>
                  </select>
                </label>
                <div className="form-actions">
                  <button
                    className="primary"
                    type="button"
                    onClick={startVoiceRecording}
                    disabled={voiceRecording}
                  >
                    {voiceRecording ? '🔴 Recording...' : '🎤 Start Recording'}
                  </button>
                  <button
                    className="ghost"
                    type="button"
                    onClick={stopVoiceRecording}
                    disabled={!voiceRecording}
                  >
                    Stop
                  </button>
                  <span className="status-inline">
                    {voiceRecording ? 'Listening...' : 'Ready'}
                  </span>
                </div>
                {voiceRecordingUrl && (
                  <div className="detail-card" style={{ marginTop: '12px' }}>
                    <p style={{ margin: '0 0 8px 0', color: 'var(--text-secondary)', fontWeight: '600' }}>
                      📼 Your Recording:
                    </p>
                    <audio controls src={voiceRecordingUrl} style={{ width: '100%' }} />
                  </div>
                )}
              </div>
              <form className="form" onSubmit={handleVoiceTestSubmit}>
                <label>
                  Or upload audio file (.mp3, .wav, .m4a, .ogg, .flac, .webm)
                  <input
                    type="file"
                    accept=".mp3,.wav,.m4a,.ogg,.flac,.webm,audio/*"
                    onChange={(event) => setVoiceTestFile(event.target.files?.[0] || null)}
                  />
                </label>
                <div className="form-actions">
                  <button className="primary" type="submit" disabled={status.voiceTest === 'loading'}>
                    {status.voiceTest === 'loading' ? '⏳ Processing...' : '🚀 Send to AI'}
                  </button>
                  <span className="status-inline">{statusLabel[status.voiceTest]}</span>
                </div>
              </form>
              {status.voiceTest === 'loading' && (
                <div className="detail-card" style={{ marginTop: '16px', textAlign: 'center', padding: '20px' }}>
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    🎧 Transcribing → 🤖 AI thinking → 🔊 Generating speech...
                  </p>
                </div>
              )}
              {voiceTestResult?.transcription && status.voiceTest !== 'loading' && (
                <div className="detail-card" style={{ marginTop: '16px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ margin: '0 0 8px 0', color: 'var(--text-secondary)', fontWeight: '600' }}>
                      📝 You said:
                    </p>
                    <pre style={{ 
                      whiteSpace: 'pre-wrap', 
                      margin: 0, 
                      padding: '12px', 
                      background: 'var(--card-bg)',
                      borderRadius: '4px',
                      borderLeft: '3px solid var(--accent)'
                    }}>{voiceTestResult.transcription}</pre>
                  </div>
                  {voiceTestResult.ai_reply && (
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ margin: '0 0 8px 0', color: 'var(--text-secondary)', fontWeight: '600' }}>
                        🤖 AI replied:
                      </p>
                      <pre style={{ 
                        whiteSpace: 'pre-wrap', 
                        margin: 0, 
                        padding: '12px', 
                        background: 'var(--card-bg)',
                        borderRadius: '4px',
                        borderLeft: '3px solid #4caf50'
                      }}>{voiceTestResult.ai_reply}</pre>
                    </div>
                  )}
                  {voiceTestResult.audio_url && (
                    <div>
                      <p style={{ margin: '0 0 8px 0', color: 'var(--text-secondary)', fontWeight: '600' }}>
                        🔊 AI Voice Response:
                      </p>
                      <audio 
                        controls 
                        src={voiceTestResult.audio_url} 
                        autoPlay
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                </div>
              )}
              {!voiceTestResult && status.voiceTest !== 'loading' && (
                <div className="detail-card" style={{ marginTop: '16px' }}>
                  <p className="empty">Record or upload audio to start a conversation with AI</p>
                </div>
              )}
            </section>

          </main>
        )}

        {view === 'company' && (
          <main className="grid">
            <section className="card">
              <div className="card-header">
                <div>
                  <h2>Company selection</h2>
                  <p>Admin view: switch tenants and inspect operational call details.</p>
                </div>
              </div>
              <div className="form">
                <label>
                  Active company
                  <select
                    value={selectedClientId}
                    onChange={(event) => setSelectedClientId(event.target.value)}
                  >
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.business_name}
                      </option>
                    ))}
                  </select>
                </label>
                {selectedClient && (
                  <div className="company-card">
                    <h3>{selectedClient.business_name}</h3>
                    <p>{selectedClient.phone_number}</p>
                    <span>{selectedClient.notification_email || 'No notification email'}</span>
                  </div>
                )}
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <div>
                  <h2>Company calls</h2>
                  <p>Calls routed to this tenant.</p>
                </div>
                <button className="ghost" type="button" onClick={loadCalls}>
                  Refresh
                </button>
              </div>
              <div className="list">
                {callsByClient.length === 0 ? (
                  <p className="empty">No calls for this company yet.</p>
                ) : (
                  callsByClient.map((call) => (
                    <div key={call.id} className="list-item">
                      <div>
                        <strong>Call #{call.id}</strong>
                        <span>{call.caller_number || 'Unknown caller'}</span>
                      </div>
                      <span>{new Date(call.start_time).toLocaleString()}</span>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>Call insight</h2>
                  <p>Inspect the last call in detail.</p>
                </div>
              </div>
              <div className="detail-card">
                {callsByClient.length === 0 ? (
                  <p className="empty">No call data yet.</p>
                ) : (
                  <div className="detail-stack">
                    <div className="detail-row">
                      <div>
                        <h3>{callsByClient[0].call_sid}</h3>
                        <p>
                          {callsByClient[0].end_time ? 'Completed' : 'In progress'} ·{' '}
                          {callsByClient[0].caller_number || 'Unknown'}
                        </p>
                      </div>
                      <button
                        className="ghost"
                        type="button"
                        onClick={() => loadCallDetail(callsByClient[0].id)}
                      >
                        Load full detail
                      </button>
                    </div>
                    {selectedCall && selectedCall.id === callsByClient[0].id ? (
                      <pre>{selectedCall.transcript || 'No transcript yet.'}</pre>
                    ) : (
                      <p className="empty">Load details to view transcript.</p>
                    )}
                  </div>
                )}
              </div>
            </section>
          </main>
        )}

        {/* portal = legacy alias → merged into client-workspace / ops-console */}
        {view === 'portal' && (
          (isAdmin || isDeveloper)
            ? <OpsConsole clients={clients} onNavigate={setView} systemStatus={systemStatus} />
            : <ClientWorkspace user={authUser} clientId={selectedClientId} onNavigate={setView} trialStatus={trialStatus} />
        )}

        {view === 'ceo-dashboard' && <CEODashboard token={authToken} onNavigate={setView} currentUser={authUser} />}
        {view === 'sales-dashboard' && <SalesDashboard selectedClient={selectedClientId} />}
        {view === 'data-capturer-dashboard' && <DataCapturerDashboard selectedClient={selectedClientId} />}

        {/* ── CRM Module ── */}
        {view === 'crm-dashboard' && <CRMDashboard clientId={selectedClientId} onNavigate={setView} />}
        {view === 'leads-view' && <LeadsView clientId={selectedClientId} onNavigate={setView} />}
        {view === 'opportunities-view' && <OpportunitiesView clientId={selectedClientId} onNavigate={setView} />}
        {view === 'accounts-view' && <AccountsView clientId={selectedClientId} onNavigate={setView} />}
        {view === 'contacts-view' && <ContactsView clientId={selectedClientId} onNavigate={setView} />}
        {view === 'quotations-view' && <QuotationsView clientId={selectedClientId} onNavigate={setView} />}

        {/* ── Help Desk Module ── */}
        {view === 'helpdesk-dashboard' && <HelpDeskDashboard clientId={selectedClientId} isAdmin={isAdmin} clients={clients} onNavigate={setView} />}
        {view === 'tickets-view' && <TicketsView clientId={selectedClientId} isAdmin={isAdmin} clients={clients} onNavigate={setView} onSelectTicket={(ticketId) => { setSelectedTicketId(ticketId); setView('ticket-detail-view'); }} />}
        {view === 'ticket-detail-view' && <TicketDetailView ticketId={selectedTicketId} onClose={() => { setSelectedTicketId(null); setView('tickets-view'); }} />}
        {view === 'knowledge-base-view' && <KnowledgeBaseView clientId={selectedClientId} isAdmin={isAdmin} clients={clients} onNavigate={setView} />}
        {view === 'assets-view' && <AssetsView clientId={selectedClientId} isAdmin={isAdmin} clients={clients} onNavigate={setView} />}
        {view === 'sla-management' && <SLAManagementView clientId={selectedClientId} isAdmin={isAdmin} clients={clients} onNavigate={setView} />}

        {/* ── Project Management Module ── */}
        {view === 'projects-dashboard' && <ProjectDashboard clientId={selectedClientId} token={authToken} onNavigate={setView} />}
        {view === 'projects-view' && <ProjectsView clientId={selectedClientId} token={authToken} onNavigate={setView} onSelectProject={(projectId) => { setSelectedProjectId(projectId); setView('project-detail-view'); }} />}
        {view === 'project-detail-view' && <ProjectDetailView projectId={selectedProjectId} clientId={selectedClientId} token={authToken} onNavigate={setView} onClose={() => { setSelectedProjectId(null); setView('projects-view'); }} />}
        {view === 'tasks-view' && <TasksView clientId={selectedClientId} token={authToken} onNavigate={setView} />}
        {view === 'milestones-view' && <MilestonesView clientId={selectedClientId} token={authToken} onNavigate={setView} />}
        {view === 'time-logging-view' && <TimeLoggingView clientId={selectedClientId} token={authToken} onNavigate={setView} />}

        {view === 'whatsapp' && (
          <ChannelInbox channel="whatsapp" clientId={selectedClientId} clients={clients} isAdmin={isAdmin} />
        )}

        {view === '__whatsapp_dead_code__' && (
          <main className="grid">
            {/* WhatsApp Header */}
            <section className="card span-3" style={{
              background: 'linear-gradient(135deg, rgba(76,175,80,0.12) 0%, rgba(76,175,80,0.08) 100%)',
              border: '1px solid rgba(76,175,80,0.2)',
              padding: '40px',
              marginBottom: '20px'
            }}>
              <h1 style={{ fontSize: '2.5rem', margin: '0 0 12px 0', fontFamily: 'var(--font-display)', fontWeight: '800' }}>
                💬 WhatsApp Conversations
              </h1>
              <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.6' }}>
                Manage customer messaging and replies in real-time with AI-powered assistance and instant notifications.
              </p>
            </section>

            <section className="card span-2">
              <div className="card-header card-header-premium">
                <div>
                  <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>🔄 Conversation Management</h2>
                  <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>Select a client to view active WhatsApp chats</p>
                </div>
                <div className="button-group" style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className={waAutoRefreshEnabled ? "btn-premium" : "btn-premium-ghost"}
                    type="button" 
                    title="Auto-refresh conversations every 3 seconds"
                    onClick={() => setWaAutoRefreshEnabled(!waAutoRefreshEnabled)}
                  >
                    {waAutoRefreshEnabled ? '◆' : '◇'} Auto-Refresh
                  </button>
                  <button 
                    className="btn-premium-ghost"
                    type="button" 
                    onClick={() => {
                      loadWhatsappConversations();
                      if (selectedWAConversation?.id) {
                        loadWhatsappConversation(selectedWAConversation.id);
                      }
                    }}
                    disabled={status.whatsapp === 'loading'}
                  >
                    {status.whatsapp === 'loading' ? '⟳ Refreshing...' : '↻ Refresh'}
                  </button>
                </div>
              </div>
              
              <form className="p-24">
                <label className="form-label-premium">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🏢 Select Client
                  </span>
                  <select
                    className="input-premium"
                    value={selectedClientId}
                    onChange={(event) => {
                      const newClientId = event.target.value;
                      setSelectedClientId(newClientId);
                      if (newClientId) {
                        setSelectedWAConversation(null);
                        setWhatsappConversations([]);
                        loadWhatsappConversations(newClientId);
                      }
                    }}
                  >
                    <option value="">-- Choose a client --</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.business_name} ({client.whatsapp_number || 'No WhatsApp'})
                      </option>
                    ))}
                  </select>
                </label>
              </form>
            </section>

            {selectedClientId && (
              <>
                <section className="card">
                  <div className="card-header card-header-premium">
                    <div>
                      <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>📬 Recent Conversations</h2>
                      <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>
                        {whatsappConversations.length} {whatsappConversations.length === 1 ? 'chat' : 'chats'}
                        {status.whatsapp === 'loading' && ' • Loading...'}
                      </p>
                    </div>
                  </div>
                  <div style={{ padding: '32px' }}>
                    {status.whatsapp === 'loading' && whatsappConversations.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '60px 40px', opacity: 0.6 }}>⏳ Loading conversations...</div>
                    ) : whatsappConversations.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '60px 40px',
                        background: 'rgba(76,175,80,0.05)',
                        borderRadius: '12px'
                      }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>💭</div>
                        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No conversations yet. Messages will appear once received.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {whatsappConversations.map((conv) => (
                          <div
                            key={conv.id}
                            onClick={() => loadWhatsappConversation(conv.id)}
                            style={{
                              padding: '20px',
                              background: selectedWAConversation?.id === conv.id 
                                ? 'linear-gradient(135deg, rgba(76,175,80,0.15) 0%, rgba(76,175,80,0.08) 100%)'
                                : 'linear-gradient(135deg, rgba(76,175,80,0.06) 0%, rgba(76,175,80,0.02) 100%)',
                              border: `2px solid ${selectedWAConversation?.id === conv.id ? 'rgba(76,175,80,0.4)' : 'rgba(76,175,80,0.2)'}`,
                              borderRadius: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedWAConversation?.id !== conv.id) {
                                e.currentTarget.style.borderColor = 'rgba(76,175,80,0.4)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedWAConversation?.id !== conv.id) {
                                e.currentTarget.style.borderColor = 'rgba(76,175,80,0.2)';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <div style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '1.0625rem', fontWeight: '700' }}>
                                  💬 {conv.contact_name || 'Unknown'}
                                </h4>
                                <p style={{ margin: '0 0 6px 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                  {conv.contact_phone}
                                </p>
                                <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                                  {new Date(conv.last_message_time).toLocaleString()}
                                </span>
                              </div>
                              <div style={{ 
                                padding: '8px 16px',
                                background: conv.is_active ? '#4caf5030' : '#ccc',
                                color: conv.is_active ? '#4caf50' : '#999',
                                borderRadius: '8px',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                                marginLeft: '16px'
                              }}>
                                {conv.is_active ? '● Active' : '○ Inactive'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>

                {selectedWAConversation && (
                  <section className="card span-2">
                    <div className="card-header card-header-premium">
                      <div>
                        <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>💬 Chat: {selectedWAConversation.contact_name || selectedWAConversation.contact_phone}</h2>
                        <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>
                          {selectedWAConversation.contact_phone} • 
                          {selectedWAConversation.is_active ? ' Active' : ' Inactive'}
                          {status.whatsappLoad === 'loading' && ' • Updating...'}
                        </p>
                      </div>
                      <button
                        className="btn-premium-ghost"
                        type="button"
                        onClick={() => loadWhatsappConversation(selectedWAConversation.id)}
                        disabled={status.whatsappLoad === 'loading'}
                      >
                        {status.whatsappLoad === 'loading' ? '⟳' : '↻'}
                      </button>
                    </div>

                    <div style={{ padding: '32px' }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '1px solid rgba(76,175,80,0.2)'
                      }}>
                        {!selectedWAConversation.messages || selectedWAConversation.messages.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '40px 20px', opacity: 0.6 }}>
                            No messages yet. Start a conversation!
                          </div>
                        ) : (
                          selectedWAConversation.messages.map((msg, idx) => (
                            <div 
                              key={msg.id || idx}
                              style={{
                                display: 'flex',
                                justifyContent: msg.direction === 'outbound' ? 'flex-end' : 'flex-start'
                              }}
                            >
                              <div style={{
                                maxWidth: '70%',
                                padding: '12px 16px',
                                borderRadius: '12px',
                                background: msg.direction === 'outbound' 
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, rgba(76,175,80,0.2) 0%, rgba(76,175,80,0.1) 100%)',
                                color: msg.direction === 'outbound' ? 'white' : 'var(--text-primary)',
                                wordWrap: 'break-word'
                              }}>
                                {msg.is_ai_generated && msg.direction === 'outbound' && (
                                  <span style={{ 
                                    display: 'inline-block',
                                    background: 'rgba(255,255,255,0.2)',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: '700',
                                    marginRight: '8px',
                                    marginBottom: '4px'
                                  }}>
                                    🤖 AI
                                  </span>
                                )}
                                <p style={{ margin: '0 0 6px 0', wordBreak: 'break-word' }}>
                                  {msg.content}
                                </p>
                                <small style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                  {new Date(msg.timestamp).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                  {msg.direction === 'outbound' && (
                                    <span style={{ marginLeft: '8px' }}>
                                      {msg.status === 'sent' && '✓'}
                                      {msg.status === 'delivered' && '✓✓'}
                                      {msg.status === 'read' && '✓✓ (read)'}
                                      {msg.status === 'pending' && '◊'}
                                      {msg.status === 'failed' && '✕'}
                                    </span>
                                  )}
                                </small>
                                {msg.is_ai_generated && msg.direction === 'outbound' && (
                                  <div style={{ marginTop: '4px' }}>
                                    <AIFeedbackWidget
                                      sourceType="whatsapp"
                                      sourceId={selectedWAConversation?.id}
                                      aiText={msg.content}
                                      compact
                                    />
                                  </div>
                                )}
                              </div>
                              {msg.status === 'failed' && waFailedMessages.has(msg.id) && (
                                <button
                                  style={{
                                    marginLeft: '8px',
                                    padding: '4px 12px',
                                    fontSize: '0.75rem',
                                    background: 'var(--error)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => handleRetryWhatsappMessage(msg.id, selectedWAConversation.id, msg.content)}
                                  disabled={Object.values(waMessageSending).some(v => v)}
                                  title="Retry sending this message"
                                >
                                  Retry
                                </button>
                              )}
                            </div>
                          ))
                        )}
                      </div>

                      <form onSubmit={handleSendWhatsappMessage} style={{ display: 'flex', gap: '12px' }}>
                        <textarea
                          placeholder="Type your message... (max 4096 characters)"
                          value={waMessageInput}
                          onChange={(e) => {
                            if (e.target.value.length <= 4096) {
                              setWaMessageInput(e.target.value);
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendWhatsappMessage(e);
                            }
                          }}
                          disabled={Object.values(waMessageSending).some(v => v)}
                          style={{ 
                            flex: 1,
                            padding: '12px',
                            borderRadius: '12px',
                            border: '2px solid rgba(76,175,80,0.2)',
                            fontFamily: 'inherit',
                            fontSize: '0.9375rem',
                            resize: 'none', 
                            minHeight: '60px',
                            maxHeight: '120px',
                            transition: 'border-color 0.3s'
                          }}
                          onFocus={(e) => e.target.style.borderColor = 'rgba(76,175,80,0.4)'}
                          onBlur={(e) => e.target.style.borderColor = 'rgba(76,175,80,0.2)'}
                        />
                        <button
                          type="submit"
                          className="btn-premium"
                          disabled={!waMessageInput.trim() || Object.values(waMessageSending).some(v => v)}
                          style={{ alignSelf: 'flex-end', minWidth: '100px' }}
                        >
                          {Object.values(waMessageSending).some(v => v) ? '⏳ Sending...' : '📤 Send'}
                        </button>
                      </form>
                      <small style={{ marginTop: '8px', display: 'block', color: 'var(--text-tertiary)' }}>
                        {waMessageInput.length}/4096
                      </small>
                    </div>
                  </section>
                )}
              </>
            )}
          </main>
        )}

        {view === 'email' && (
          <ComingSoon icon="📧" title="Email — coming soon" description="We're putting the finishing touches on email integration. Check back soon." />
        )}
        {view === '__email_dead__' && (
          <main className="grid">
            {/* Email dead code */}
            <section className="card span-3" style={{
              background: 'linear-gradient(135deg, rgba(33,150,243,0.12) 0%, rgba(33,150,243,0.08) 100%)',
              border: '1px solid rgba(33,150,243,0.2)',
              padding: '40px',
              marginBottom: '20px'
            }}>
              <h1 style={{ fontSize: '2.5rem', margin: '0 0 12px 0', fontFamily: 'var(--font-display)', fontWeight: '800' }}>
                📧 Email Management
              </h1>
              <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.6' }}>
                Manage customer email threads with AI-powered auto-responses and intelligent message routing across your organization.
              </p>
            </section>

            <section className="card span-2">
              <div className="card-header card-header-premium">
                <div>
                  <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>🔄 Email Management</h2>
                  <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>Select a client to view email threads</p>
                </div>
                <div className="button-group" style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className={emailAutoRefreshEnabled ? "btn-premium" : "btn-premium-ghost"}
                    type="button" 
                    title="Auto-refresh emails every 5 seconds"
                    onClick={() => setEmailAutoRefreshEnabled(!emailAutoRefreshEnabled)}
                  >
                    {emailAutoRefreshEnabled ? '◆' : '◇'} Auto-Refresh
                  </button>
                  <button 
                    className="btn-premium-ghost"
                    type="button" 
                    onClick={() => {
                      loadEmailConversations();
                      if (selectedEmailConversation?.id) {
                        loadEmailConversation(selectedEmailConversation.id);
                      }
                    }}
                    disabled={status.email === 'loading'}
                  >
                    {status.email === 'loading' ? '⟳ Refreshing...' : '↻ Refresh'}
                  </button>
                </div>
              </div>
              
              <form className="p-24">
                <label className="form-label-premium">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🏢 Select Client
                  </span>
                  <select
                    className="input-premium"
                    value={selectedClientId}
                    onChange={(event) => {
                      const newClientId = event.target.value;
                      setSelectedClientId(newClientId);
                      if (newClientId) {
                        setSelectedEmailConversation(null);
                        setEmailConversations([]);
                        loadEmailConversations(newClientId);
                      }
                    }}
                  >
                    <option value="">-- Choose a client --</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.business_name} ({client.notification_email || 'No Email'})
                      </option>
                    ))}
                  </select>
                </label>
              </form>
            </section>

            {selectedClientId && (
              <>
                <section className="card">
                  <div className="card-header card-header-premium">
                    <div>
                      <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>📬 Inbox</h2>
                      <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>
                        {emailConversations.length} {emailConversations.length === 1 ? 'thread' : 'threads'}
                        {status.email === 'loading' && ' • Loading...'}
                      </p>
                    </div>
                  </div>
                  <div style={{ padding: '32px' }}>
                    {status.email === 'loading' && emailConversations.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '60px 40px', opacity: 0.6 }}>⏳ Loading emails...</div>
                    ) : emailConversations.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '60px 40px',
                        background: 'rgba(33,150,243,0.05)',
                        borderRadius: '12px'
                      }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>📭</div>
                        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No emails yet. Inbox threads will appear when email routing is active.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {emailConversations.map((conv) => (
                          <div
                            key={conv.id}
                            onClick={() => loadEmailConversation(conv.id)}
                            style={{
                              padding: '20px',
                              background: selectedEmailConversation?.id === conv.id 
                                ? 'linear-gradient(135deg, rgba(33,150,243,0.15) 0%, rgba(33,150,243,0.08) 100%)'
                                : 'linear-gradient(135deg, rgba(33,150,243,0.06) 0%, rgba(33,150,243,0.02) 100%)',
                              border: `2px solid ${selectedEmailConversation?.id === conv.id ? 'rgba(33,150,243,0.4)' : 'rgba(33,150,243,0.2)'}`,
                              borderRadius: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedEmailConversation?.id !== conv.id) {
                                e.currentTarget.style.borderColor = 'rgba(33,150,243,0.4)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedEmailConversation?.id !== conv.id) {
                                e.currentTarget.style.borderColor = 'rgba(33,150,243,0.2)';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <div style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '1.0625rem', fontWeight: '700' }}>
                                  📨 {conv.contact_name || conv.contact_email}
                                </h4>
                                <p style={{ margin: '0 0 6px 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                  {conv.subject}
                                </p>
                                <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                                  {conv.contact_email} • {new Date(conv.last_message_time).toLocaleString()}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: '16px' }}>
                                {conv.message_count > 0 && (
                                  <span style={{ 
                                    padding: '6px 12px',
                                    background: '#2196f330',
                                    color: '#2196f3',
                                    borderRadius: '8px',
                                    fontSize: '0.8125rem',
                                    fontWeight: '700'
                                  }}>
                                    {conv.message_count} messages
                                  </span>
                                )}
                                <span style={{ 
                                  padding: '8px 16px',
                                  background: conv.is_active ? '#2196f330' : '#ccc',
                                  color: conv.is_active ? '#2196f3' : '#999',
                                  borderRadius: '8px',
                                  fontSize: '0.75rem',
                                  fontWeight: '700'
                                }}>
                                  {conv.is_active ? '● Active' : '○ Archived'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>

                {selectedEmailConversation && (
                  <section className="card span-2">
                    <div className="card-header card-header-premium">
                      <div>
                        <h2 style={{ fontSize: '1.375rem', marginBottom: '6px' }}>📨 {selectedEmailConversation.subject}</h2>
                        <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>
                          {selectedEmailConversation.contact_email} • 
                          {selectedEmailConversation.is_active ? ' Active' : ' Archived'}
                          {status.emailLoad === 'loading' && ' • Updating...'}
                        </p>
                      </div>
                      <button
                        className="btn-premium-ghost"
                        type="button"
                        onClick={() => loadEmailConversation(selectedEmailConversation.id)}
                        disabled={status.emailLoad === 'loading'}
                      >
                        {status.emailLoad === 'loading' ? '⟳' : '↻'}
                      </button>
                    </div>

                    <div style={{ padding: '32px' }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        marginBottom: '32px',
                        paddingBottom: '24px',
                        borderBottom: '1px solid rgba(33,150,243,0.2)'
                      }}>
                        {!selectedEmailConversation.messages || selectedEmailConversation.messages.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '40px 20px', opacity: 0.6 }}>
                            No messages in this thread.
                          </div>
                        ) : (
                          selectedEmailConversation.messages.map((msg, idx) => (
                            <div 
                              key={msg.id || idx}
                              style={{
                                padding: '20px',
                                background: msg.direction === 'outbound'
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, rgba(33,150,243,0.1) 0%, rgba(33,150,243,0.05) 100%)',
                                border: msg.direction === 'outbound' ? 'none' : '1px solid rgba(33,150,243,0.2)',
                                borderRadius: '12px',
                                color: msg.direction === 'outbound' ? 'white' : 'var(--text-primary)'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                                <div>
                                  <strong style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {msg.direction === 'outbound' ? msg.sender_name : msg.sender_email}
                                    {msg.is_ai_generated && msg.direction === 'outbound' && (
                                      <span style={{ 
                                        background: 'rgba(255,255,255,0.2)',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.7rem',
                                        fontWeight: '700'
                                      }}>
                                        🤖 AI
                                      </span>
                                    )}
                                  </strong>
                                </div>
                                <small style={{ opacity: 0.8 }}>
                                  {new Date(msg.timestamp).toLocaleString([], { 
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </small>
                              </div>
                              <p style={{ margin: '0 0 12px 0', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                {msg.body_text}
                              </p>
                              {msg.direction === 'outbound' && (
                                <small style={{ opacity: 0.8, display: 'block' }}>
                                  {msg.status === 'sent' && '✓ Sent'}
                                  {msg.status === 'delivered' && '✓✓ Delivered'}
                                  {msg.status === 'pending' && '◊ Sending...'}
                                  {msg.status === 'failed' && '✕ Failed'}
                                </small>
                              )}
                              {msg.is_ai_generated && msg.direction === 'outbound' && (
                                <div style={{ marginTop: '8px' }}>
                                  <AIFeedbackWidget
                                    sourceType="email"
                                    sourceId={selectedEmailConversation?.id}
                                    aiText={msg.body_text}
                                    compact
                                  />
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>

                      <div style={{
                        padding: '24px',
                        background: 'rgba(33,150,243,0.05)',
                        border: '1px solid rgba(33,150,243,0.2)',
                        borderRadius: '12px'
                      }}>
                        <h4 style={{ margin: '0 0 20px 0', fontSize: '1.0625rem', fontWeight: '700' }}>✉️ Compose Reply</h4>
                        <form onSubmit={handleSendEmail} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <label className="form-label-premium">
                            <span>Subject</span>
                            <input
                              type="text"
                              className="input-premium"
                              placeholder="Re: Email subject..."
                              value={emailSubject}
                              onChange={(e) => setEmailSubject(e.target.value)}
                              disabled={status.emailSend === 'loading'}
                            />
                          </label>
                          <label className="form-label-premium">
                            <span>Message</span>
                            <textarea
                              className="input-premium"
                              placeholder="Type your email message..."
                              value={emailBody}
                              onChange={(e) => setEmailBody(e.target.value)}
                              disabled={status.emailSend === 'loading'}
                              style={{ 
                                resize: 'vertical', 
                                minHeight: '120px',
                                maxHeight: '300px'
                              }}
                            />
                            <small style={{ color: 'var(--text-tertiary)' }}>
                              {emailBody.length}/100000
                            </small>
                          </label>
                          <button
                            type="submit"
                            className="btn-premium"
                            disabled={!emailBody.trim() || !emailSubject.trim() || status.emailSend === 'loading'}
                            style={{ width: '100%' }}
                          >
                            {status.emailSend === 'loading' ? '⏳ Sending...' : '📤 Send Email'}
                          </button>
                        </form>
                      </div>
                    </div>
                  </section>
                )}
              </>
            )}
          </main>
        )}

        {view === 'facebook' && (
          <ComingSoon icon="📘" title="Facebook Messenger — coming soon" description="We're putting the finishing touches on Facebook integration. Check back soon." />
        )}

        {view === 'instagram' && (
          <ComingSoon icon="📷" title="Instagram DMs — coming soon" description="We're putting the finishing touches on Instagram integration. Check back soon." />
        )}

        {view === 'crm' && (
          <CrmView clientId={selectedClientId} clients={clients} isAdmin={isAdmin} />
        )}

        {view === 'client-lookup' && (
          <Lazy component={ClientLookup} clientId={selectedClientId} token={authToken} onNavigate={handleNavigate} />
        )}

        {/* analytics legacy route — renders the same AnalyticsDashboard */}
        {view === 'analytics' && (
          <AnalyticsDashboard />
        )}


        {view === 'sentiment' && (
          <main className="grid">
            <section className="card span-3" style={{ background: 'linear-gradient(135deg, rgba(240,147,251,0.1) 0%, rgba(245,87,108,0.06) 100%)', border: '1px solid rgba(240,147,251,0.2)' }}>
              <div className="card-header" style={{ padding: '28px 32px' }}>
                <div>
                  <h2 style={{ fontSize: '1.75rem', marginBottom: '8px' }}>😊 Sentiment Analysis</h2>
                  <p style={{ color: 'var(--text-secondary)' }}>Analyze message tone, track escalations, and monitor customer sentiment across all channels.</p>
                </div>
              </div>
            </section>

            <section className="card span-2">
              <div className="card-header"><h3>Analyze Text</h3></div>
              <div className="p-24" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <textarea
                  rows={5}
                  placeholder="Paste a customer message to analyze sentiment..."
                  value={sentimentAnalyzeText}
                  onChange={(e) => setSentimentAnalyzeText(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
                <button
                  className="primary"
                  disabled={!sentimentAnalyzeText.trim() || status.sentimentAnalyze === 'loading'}
                  onClick={async () => {
                    setStatus((s) => ({ ...s, sentimentAnalyze: 'loading' }));
                    try {
                      const res = await fetch('/api/sentiment/analyze', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ text: sentimentAnalyzeText, channel: 'general' }),
                      });
                      const data = await res.json();
                      setSentimentResult(data);
                      setStatus((s) => ({ ...s, sentimentAnalyze: 'success' }));
                    } catch (err) {
                      setStatus((s) => ({ ...s, sentimentAnalyze: 'error' }));
                    }
                  }}
                  type="button"
                >
                  {status.sentimentAnalyze === 'loading' ? 'Analyzing…' : 'Analyze Sentiment'}
                </button>

                {sentimentResult && (
                  <div style={{ padding: '20px', borderRadius: '12px', background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <span style={{ fontSize: '2rem' }}>
                        {(sentimentResult.label || '').toUpperCase() === 'POSITIVE' ? '😊' : (sentimentResult.label || '').toUpperCase() === 'NEGATIVE' ? '😞' : '😐'}
                      </span>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '1.1rem', textTransform: 'capitalize' }}>{(sentimentResult.label || 'neutral').toLowerCase()}</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Score: {(sentimentResult.score || 0).toFixed(3)} · Intent: {sentimentResult.intent || 'general'}</div>
                      </div>
                      {(sentimentResult.should_escalate || sentimentResult.requires_escalation) && (
                        <span style={{ marginLeft: 'auto', background: '#ff4d4d22', color: '#ff4d4d', padding: '4px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '700' }}>⚠ Escalate</span>
                      )}
                    </div>
                    {sentimentResult.emotions && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {Object.entries(sentimentResult.emotions).map(([emotion, score]) => (
                          <span key={emotion} style={{ padding: '4px 10px', borderRadius: '20px', background: 'var(--bg-primary)', border: '1px solid var(--border)', fontSize: '0.8rem' }}>
                            {emotion}: {(score * 100).toFixed(0)}%
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>

            <section className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Escalations</h3>
                <button className="ghost" type="button" onClick={async () => {
                  setStatus((s) => ({ ...s, sentiment: 'loading' }));
                  try {
                    const r = await fetch('/api/sentiment/escalations?client_id=1');
                    const d = await r.json();
                    setSentimentEscalations(Array.isArray(d) ? d : d.recommendations || d.escalations || []);
                    setStatus((s) => ({ ...s, sentiment: 'success' }));
                  } catch { setStatus((s) => ({ ...s, sentiment: 'error' })); }
                }}>Refresh</button>
              </div>
              <div style={{ padding: '16px' }}>
                {sentimentEscalations.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>
                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✅</div>
                    <p>No escalations pending</p>
                  </div>
                ) : sentimentEscalations.map((esc, idx) => (
                  <div key={idx} style={{ padding: '12px', borderRadius: '8px', background: '#ff4d4d11', border: '1px solid #ff4d4d33', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>{esc.reason || esc.message || esc.text || 'Escalation required'}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Score: {(esc.sentiment_score || esc.score || 0).toFixed(2)} · Priority: {esc.priority || 'medium'} · Conv: {esc.conversation_id}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="card span-3">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Sentiment Dashboard</h3>
                <button className="ghost" type="button" onClick={async () => {
                  try {
                    const r = await fetch('/api/sentiment/dashboard?client_id=1');
                    setSentimentDashboard(await r.json());
                  } catch { /* ignore */ }
                }}>Load Dashboard</button>
              </div>
              <div style={{ padding: '24px' }}>
                {sentimentDashboard ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
                    {[
                      { label: 'Total Analyzed', value: sentimentDashboard.total_messages || 0, color: '#667eea' },
                      { label: 'Positive', value: sentimentDashboard.positive_count || 0, color: '#43e97b' },
                      { label: 'Negative', value: sentimentDashboard.negative_count || 0, color: '#ff4d4d' },
                      { label: 'Neutral', value: sentimentDashboard.neutral_count || 0, color: '#ffa500' },
                      { label: 'Avg Score', value: (sentimentDashboard.average_score || 0).toFixed(2), color: '#4facfe' },
                      { label: 'Escalations', value: sentimentDashboard.escalation_count || 0, color: '#f093fb' },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ textAlign: 'center', padding: '20px', borderRadius: '12px', background: `${color}15`, border: `1px solid ${color}33` }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary)', marginBottom: '8px' }}>{label}</div>
                        <div style={{ fontSize: '2rem', fontWeight: '800', color }}>{value}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', opacity: 0.5, padding: '40px' }}>Click "Load Dashboard" to view sentiment metrics</div>
                )}
              </div>
            </section>
          </main>
        )}

        {view === 'ab-testing' && (
          <main className="grid">
            <section className="card span-3" style={{ background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(79,172,254,0.06) 100%)', border: '1px solid rgba(102,126,234,0.2)' }}>
              <div className="card-header" style={{ padding: '28px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontSize: '1.75rem', marginBottom: '8px' }}>🧪 A/B Testing</h2>
                  <p style={{ color: 'var(--text-secondary)' }}>Create experiments, assign variants, and measure which AI responses or workflows perform best.</p>
                </div>
                <button className="primary" type="button" onClick={async () => {
                  setStatus((s) => ({ ...s, abTests: 'loading' }));
                  try {
                    const r = await fetch('/api/ab-tests/?client_id=1');
                    const d = await r.json();
                    setAbTests(Array.isArray(d) ? d : d.tests || []);
                    setStatus((s) => ({ ...s, abTests: 'success' }));
                  } catch { setStatus((s) => ({ ...s, abTests: 'error' })); }
                }}>Load Tests</button>
              </div>
            </section>

            <section className="card">
              <div className="card-header"><h3>Create A/B Test</h3></div>
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input className="input" placeholder="Test name" value={abTestForm.name} onChange={(e) => setAbTestForm((f) => ({ ...f, name: e.target.value }))} />
                <input className="input" placeholder="Description" value={abTestForm.description} onChange={(e) => setAbTestForm((f) => ({ ...f, description: e.target.value }))} />
                <select className="input" value={abTestForm.metric} onChange={(e) => setAbTestForm((f) => ({ ...f, metric: e.target.value }))}>
                  <option value="conversion_rate">Conversion Rate</option>
                  <option value="response_time">Response Time</option>
                  <option value="satisfaction_score">Satisfaction Score</option>
                  <option value="click_through_rate">Click-through Rate</option>
                </select>
                <button className="primary" type="button" disabled={!abTestForm.name || status.abTestCreate === 'loading'}
                  onClick={async () => {
                    setStatus((s) => ({ ...s, abTestCreate: 'loading' }));
                    try {
                      const r = await fetch('/api/ab-tests/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ...abTestForm, client_id: 1 }),
                      });
                      const created = await r.json();
                      setAbTests((prev) => [created, ...prev]);
                      setAbTestForm({ name: '', description: '', metric: 'conversion_rate' });
                      setStatus((s) => ({ ...s, abTestCreate: 'success' }));
                    } catch { setStatus((s) => ({ ...s, abTestCreate: 'error' })); }
                  }}>
                  {status.abTestCreate === 'loading' ? 'Creating…' : 'Create Test'}
                </button>
              </div>
            </section>

            <section className="card span-2">
              <div className="card-header"><h3>Tests ({abTests.length})</h3></div>
              <div style={{ padding: '16px', maxHeight: '400px', overflowY: 'auto' }}>
                {abTests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>No tests yet — create one to get started.</div>
                ) : abTests.map((test) => (
                  <div
                    key={test.id}
                    onClick={() => setSelectedAbTest(test)}
                    style={{ padding: '16px', borderRadius: '10px', border: `2px solid ${selectedAbTest?.id === test.id ? '#667eea' : 'var(--border)'}`, marginBottom: '10px', cursor: 'pointer', background: selectedAbTest?.id === test.id ? 'rgba(102,126,234,0.08)' : 'transparent', transition: 'all 0.2s' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: '700', marginBottom: '4px' }}>{test.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{test.description}</div>
                      </div>
                      <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', background: test.status === 'active' ? '#43e97b22' : '#66666622', color: test.status === 'active' ? '#43e97b' : 'var(--text-secondary)' }}>
                        {test.status || 'draft'}
                      </span>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>Metric: {test.metric} · Variants: {test.variants?.length || 0}</div>
                  </div>
                ))}
              </div>
            </section>

            {selectedAbTest && (
              <section className="card span-3">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>Test: {selectedAbTest.name}</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="ghost" type="button" onClick={async () => {
                      try {
                        const r = await fetch(`/api/ab-tests/${selectedAbTest.id}/results`);
                        setAbResults(await r.json());
                      } catch { /* ignore */ }
                    }}>Load Results</button>
                  </div>
                </div>
                <div className="p-24" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <div>
                    <h4 style={{ marginBottom: '12px' }}>Add Variant</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <input className="input" placeholder="Variant name" value={abVariantForm.name} onChange={(e) => setAbVariantForm((f) => ({ ...f, name: e.target.value }))} />
                      <textarea className="input" rows={3} placeholder='Config JSON (e.g. {"prompt": "..."})' value={abVariantForm.config} onChange={(e) => setAbVariantForm((f) => ({ ...f, config: e.target.value }))} style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }} />
                      <button className="primary" type="button" disabled={!abVariantForm.name} onClick={async () => {
                        let cfg = {};
                        try { cfg = JSON.parse(abVariantForm.config); } catch { cfg = {}; }
                        const r = await fetch(`/api/ab-tests/${selectedAbTest.id}/variants`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ name: abVariantForm.name, config: cfg }),
                        });
                        if (r.ok) {
                          const updated = await r.json();
                          setSelectedAbTest(updated);
                          setAbVariantForm({ name: '', config: '{}' });
                        }
                      }}>Add Variant</button>
                    </div>
                  </div>
                  <div>
                    <h4 style={{ marginBottom: '12px' }}>Results</h4>
                    {abResults ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {(abResults.variants || []).map((v) => (
                          <div key={v.id} style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>{v.name}</div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                              Impressions: {v.impressions || 0} · Conversions: {v.conversions || 0} · Rate: {((v.conversion_rate || 0) * 100).toFixed(1)}%
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ opacity: 0.5, padding: '20px', textAlign: 'center' }}>Click "Load Results" to fetch test data</div>
                    )}
                  </div>
                </div>
              </section>
            )}
          </main>
        )}

        {view === 'knowledge-base' && (
          <main className="grid">
            <section className="card span-3" style={{ background: 'linear-gradient(135deg, rgba(168,237,234,0.1) 0%, rgba(254,214,227,0.06) 100%)', border: '1px solid rgba(168,237,234,0.2)' }}>
              <div className="card-header" style={{ padding: '28px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontSize: '1.75rem', marginBottom: '8px' }}>📚 Knowledge Base</h2>
                  <p style={{ color: 'var(--text-secondary)' }}>Manage AI knowledge bases, add documents, and search indexed content to power your chatbot responses.</p>
                </div>
                <button className="primary" type="button" onClick={async () => {
                  setStatus((s) => ({ ...s, knowledgeBase: 'loading' }));
                  try {
                    const r = await fetch('/api/knowledge-base/?client_id=1');
                    const d = await r.json();
                    setKnowledgeBases(Array.isArray(d) ? d : d.kbs || []);
                    setStatus((s) => ({ ...s, knowledgeBase: 'success' }));
                  } catch { setStatus((s) => ({ ...s, knowledgeBase: 'error' })); }
                }}>Load Knowledge Bases</button>
              </div>
            </section>

            <section className="card">
              <div className="card-header"><h3>Create Knowledge Base</h3></div>
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input className="input" placeholder="Name" value={kbForm.name} onChange={(e) => setKbForm((f) => ({ ...f, name: e.target.value }))} />
                <input className="input" placeholder="Description" value={kbForm.description} onChange={(e) => setKbForm((f) => ({ ...f, description: e.target.value }))} />
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
                  <input type="checkbox" checked={kbForm.auto_sync} onChange={(e) => setKbForm((f) => ({ ...f, auto_sync: e.target.checked }))} />
                  Auto-sync documents
                </label>
                <button className="primary" type="button" disabled={!kbForm.name || status.kbCreate === 'loading'}
                  onClick={async () => {
                    setStatus((s) => ({ ...s, kbCreate: 'loading' }));
                    try {
                      const r = await fetch('/api/knowledge-base/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ...kbForm, client_id: 1 }),
                      });
                      const created = await r.json();
                      setKnowledgeBases((prev) => [created, ...prev]);
                      setKbForm({ name: '', description: '', auto_sync: false });
                      setStatus((s) => ({ ...s, kbCreate: 'success' }));
                    } catch { setStatus((s) => ({ ...s, kbCreate: 'error' })); }
                  }}>
                  {status.kbCreate === 'loading' ? 'Creating…' : 'Create KB'}
                </button>
              </div>
            </section>

            <section className="card span-2">
              <div className="card-header"><h3>Knowledge Bases ({knowledgeBases.length})</h3></div>
              <div style={{ padding: '16px', maxHeight: '420px', overflowY: 'auto' }}>
                {knowledgeBases.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>No knowledge bases — create one to get started.</div>
                ) : knowledgeBases.map((kb) => (
                  <div
                    key={kb.id}
                    onClick={() => { setSelectedKb(kb); setKbDocuments([]); setKbStats(null); }}
                    style={{ padding: '14px 16px', borderRadius: '10px', border: `2px solid ${selectedKb?.id === kb.id ? '#a8edea' : 'var(--border)'}`, marginBottom: '8px', cursor: 'pointer', background: selectedKb?.id === kb.id ? 'rgba(168,237,234,0.08)' : 'transparent', transition: 'all 0.2s' }}
                  >
                    <div style={{ fontWeight: '700', marginBottom: '4px' }}>{kb.name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>{kb.description || 'No description'}</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: '4px', background: kb.auto_sync ? '#43e97b22' : 'transparent', border: '1px solid var(--border)', fontSize: '0.75rem' }}>{kb.auto_sync ? '🔄 Auto-sync' : '⏸ Manual'}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{kb.document_count || 0} docs</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {selectedKb && (
              <>
                <section className="card span-2">
                  <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Documents — {selectedKb.name}</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="ghost" type="button" onClick={async () => {
                        const r = await fetch(`/api/knowledge-base/${selectedKb.id}/documents`);
                        const d = await r.json();
                        setKbDocuments(Array.isArray(d) ? d : d.documents || []);
                      }}>Load Docs</button>
                      <button className="ghost" type="button" onClick={async () => {
                        await fetch(`/api/knowledge-base/${selectedKb.id}/sync`, { method: 'POST' });
                      }}>Sync KB</button>
                    </div>
                  </div>
                  <div style={{ padding: '16px', maxHeight: '360px', overflowY: 'auto' }}>
                    {kbDocuments.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>Click "Load Docs" to view documents</div>
                    ) : kbDocuments.map((doc) => (
                      <div key={doc.id} style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '8px', background: 'var(--bg-secondary)' }}>
                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>{doc.title}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', WebkitLineClamp: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{doc.content}</div>
                        {doc.source_url && <div style={{ marginTop: '6px', fontSize: '0.75rem', color: '#4facfe' }}>🔗 {doc.source_url}</div>}
                      </div>
                    ))}
                  </div>
                </section>

                <section className="card">
                  <div className="card-header"><h3>Add Document</h3></div>
                  <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <input className="input" placeholder="Title" value={kbDocForm.title} onChange={(e) => setKbDocForm((f) => ({ ...f, title: e.target.value }))} />
                    <textarea className="input" rows={6} placeholder="Document content..." value={kbDocForm.content} onChange={(e) => setKbDocForm((f) => ({ ...f, content: e.target.value }))} style={{ resize: 'vertical' }} />
                    <input className="input" placeholder="Source URL (optional)" value={kbDocForm.source_url} onChange={(e) => setKbDocForm((f) => ({ ...f, source_url: e.target.value }))} />
                    <button className="primary" type="button" disabled={!kbDocForm.title || !kbDocForm.content} onClick={async () => {
                      const r = await fetch(`/api/knowledge-base/${selectedKb.id}/documents`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(kbDocForm),
                      });
                      if (r.ok) {
                        const doc = await r.json();
                        setKbDocuments((prev) => [...prev, doc]);
                        setKbDocForm({ title: '', content: '', source_url: '' });
                      }
                    }}>Add Document</button>
                  </div>
                </section>

                <section className="card span-3">
                  <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Search Knowledge Base</h3>
                  </div>
                  <div style={{ padding: '20px', display: 'flex', gap: '12px' }}>
                    <input className="input" style={{ flex: 1 }} placeholder="Search query…" value={kbSearchQuery} onChange={(e) => setKbSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && kbSearchQuery.trim()) {
                          fetch(`/api/knowledge-base/${selectedKb.id}/search?query=${encodeURIComponent(kbSearchQuery)}`).then((r) => r.json()).then(setKbSearchResults);
                        }
                      }}
                    />
                    <button className="primary" type="button" onClick={async () => {
                      if (!kbSearchQuery.trim()) return;
                      const r = await fetch(`/api/knowledge-base/${selectedKb.id}/search?query=${encodeURIComponent(kbSearchQuery)}`);
                      setKbSearchResults(await r.json());
                    }}>Search</button>
                  </div>
                  {kbSearchResults.length > 0 && (
                    <div style={{ padding: '0 20px 20px' }}>
                      {kbSearchResults.map((result, idx) => (
                        <div key={idx} style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '8px', background: 'var(--bg-secondary)' }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{result.title}</div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{result.content}</div>
                          {result.relevance_score != null && (
                            <div style={{ marginTop: '6px', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Relevance: {(result.relevance_score * 100).toFixed(0)}%</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}
          </main>
        )}

        {view === 'compliance' && (
          <main className="grid">
            <section className="card span-3" style={{ background: 'linear-gradient(135deg, rgba(245,87,108,0.1) 0%, rgba(255,77,77,0.06) 100%)', border: '1px solid rgba(245,87,108,0.2)' }}>
              <div className="card-header" style={{ padding: '28px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontSize: '1.75rem', marginBottom: '8px' }}>🔒 Compliance & Recordings</h2>
                  <p style={{ color: 'var(--text-secondary)' }}>Manage call recordings, track data access, and maintain audit trails for regulatory compliance.</p>
                </div>
                <button className="primary" type="button" onClick={async () => {
                  setStatus((s) => ({ ...s, compliance: 'loading' }));
                  try {
                    const r = await fetch('/api/recordings?client_id=1');
                    const d = await r.json();
                    setRecordings(Array.isArray(d) ? d : d.recordings || []);
                    setStatus((s) => ({ ...s, compliance: 'success' }));
                  } catch { setStatus((s) => ({ ...s, compliance: 'error' })); }
                }}>Load Recordings</button>
              </div>
            </section>

            <section className="card">
              <div className="card-header"><h3>Log Recording</h3></div>
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input className="input" placeholder="Call ID" value={recordingForm.call_id} onChange={(e) => setRecordingForm((f) => ({ ...f, call_id: e.target.value }))} />
                <input className="input" type="number" placeholder="Duration (seconds)" value={recordingForm.duration_seconds} onChange={(e) => setRecordingForm((f) => ({ ...f, duration_seconds: parseInt(e.target.value) || 0 }))} />
                <input className="input" placeholder="File URL" value={recordingForm.file_url} onChange={(e) => setRecordingForm((f) => ({ ...f, file_url: e.target.value }))} />
                <button className="primary" type="button" disabled={!recordingForm.call_id || status.recordingCreate === 'loading'}
                  onClick={async () => {
                    setStatus((s) => ({ ...s, recordingCreate: 'loading' }));
                    try {
                      const r = await fetch('/api/recordings', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ...recordingForm, client_id: 1 }),
                      });
                      const created = await r.json();
                      setRecordings((prev) => [created, ...prev]);
                      setRecordingForm({ call_id: '', duration_seconds: 0, file_url: '' });
                      setStatus((s) => ({ ...s, recordingCreate: 'success' }));
                    } catch { setStatus((s) => ({ ...s, recordingCreate: 'error' })); }
                  }}>
                  {status.recordingCreate === 'loading' ? 'Saving…' : 'Log Recording'}
                </button>
              </div>
            </section>

            <section className="card span-2">
              <div className="card-header"><h3>Recordings ({recordings.length})</h3></div>
              <div style={{ padding: '16px', maxHeight: '420px', overflowY: 'auto' }}>
                {recordings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>No recordings found — load or log one above.</div>
                ) : recordings.map((rec) => (
                  <div
                    key={rec.id}
                    onClick={async () => {
                      setSelectedRecording(rec);
                      setRecordingAccessLogs([]);
                      try {
                        const r = await fetch(`/api/recordings/${rec.id}/access-logs?client_id=1`);
                        const d = await r.json();
                        setRecordingAccessLogs(Array.isArray(d) ? d : d.logs || []);
                      } catch { /* ignore */ }
                    }}
                    style={{ padding: '14px 16px', borderRadius: '10px', border: `2px solid ${selectedRecording?.id === rec.id ? '#f5576c' : 'var(--border)'}`, marginBottom: '8px', cursor: 'pointer', background: selectedRecording?.id === rec.id ? 'rgba(245,87,108,0.06)' : 'transparent', transition: 'all 0.2s' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: '700', marginBottom: '4px', fontFamily: 'monospace' }}>Call: {rec.call_id}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Duration: {rec.duration_seconds}s</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>ID: {rec.id}</span>
                        <button
                          type="button"
                          className="ghost"
                          style={{ fontSize: '0.75rem', padding: '2px 8px', color: '#ff4d4d', borderColor: '#ff4d4d44' }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm('Delete this recording record?')) return;
                            const r = await fetch(`/api/recordings/${rec.id}`, { method: 'DELETE' });
                            if (r.ok) {
                              setRecordings((prev) => prev.filter((x) => x.id !== rec.id));
                              if (selectedRecording?.id === rec.id) setSelectedRecording(null);
                            }
                          }}
                        >Delete</button>
                      </div>
                    </div>
                    {rec.file_url && <div style={{ marginTop: '6px', fontSize: '0.75rem', color: '#4facfe' }}>🔗 {rec.file_url}</div>}
                  </div>
                ))}
              </div>
            </section>

            {selectedRecording && (
              <section className="card span-3">
                <div className="card-header"><h3>Access Log — Recording #{selectedRecording.id}</h3></div>
                <div style={{ padding: '20px' }}>
                  {recordingAccessLogs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>No access events logged for this recording.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border)' }}>
                          {['Accessed By', 'Purpose', 'IP Address', 'Timestamp'].map((h) => (
                            <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {recordingAccessLogs.map((log, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '10px 12px', fontWeight: '600' }}>{log.accessed_by || '—'}</td>
                            <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{log.purpose || '—'}</td>
                            <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{log.ip_address || '—'}</td>
                            <td style={{ padding: '10px 12px', color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>{log.accessed_at ? new Date(log.accessed_at).toLocaleString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>
            )}
          </main>
        )}

        {view === 'bookings' && (
          <main className="grid">
            <Bookings token={authToken} />
          </main>
        )}

        {view === 'calendar-sync' && (
          <main className="grid">
            <CalendarSync token={authToken} />
          </main>
        )}

        {view === 'payments' && (
          <main className="grid">
            <Payments />
          </main>
        )}

        {view === 'notifications' && (
          <main className="grid">
            <NotificationPreferences token={authToken} />
          </main>
        )}

        {view === 'crm-sync' && (
          <main className="grid">
            <CrmSync token={authToken} />
          </main>
        )}

        {view === 'analytics-dashboard' && (
          <main className="grid">
            <AnalyticsDashboard token={authToken} />
          </main>
        )}

        {view === 'recordings' && (
          <main className="grid">
            <Recordings />
          </main>
        )}

        {view === 'ivr' && (
          <main className="grid">
            <IVRBuilder />
          </main>
        )}

        {view === 'campaigns' && (
          <main className="grid">
            <Campaigns isPaid={isAdmin || isDeveloper || trialStatus?.is_paid} onUpgrade={() => setView('pricing')} />
          </main>
        )}

        {view === 'my-ai-chat' && (
          <main className="grid">
            <MyAIChat isPaid={isAdmin || isDeveloper || trialStatus?.is_paid} onUpgrade={() => setView('pricing')} />
          </main>
        )}

        {view === 'ai-feedback' && (
          <main className="grid">
            <AIFeedback />
          </main>
        )}

        {view === 'live-chat' && (
          <main className="grid">
            <LiveChat />
          </main>
        )}

        {view === 'module-select' && (
          <main className="auth">
            <section className="auth-card" style={{ maxWidth: '680px' }}>
              <div className="auth-header">
                <div>
                  <h2>What do you need?</h2>
                  <p>Pick the tools that matter to your business. You can always change this later in Settings.</p>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', margin: '1.5rem 0' }}>
                {MODULE_DEFINITIONS.map((mod) => {
                  const active = selectedModules.includes(mod.id);
                  return (
                    <div
                      key={mod.id}
                      onClick={() => setSelectedModules((prev) =>
                        prev.includes(mod.id) ? prev.filter((m) => m !== mod.id) : [...prev, mod.id]
                      )}
                      style={{
                        padding: '1rem',
                        borderRadius: '8px',
                        border: `2px solid ${active ? 'var(--accent, #4f6ef7)' : 'var(--border, #e2e8f0)'}`,
                        background: active ? 'var(--accent-subtle, #eef2ff)' : 'var(--surface, #fff)',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{mod.label}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--muted, #64748b)' }}>{mod.description}</div>
                    </div>
                  );
                })}
              </div>
              {selectedModules.length === 0 && (
                <p style={{ fontSize: '0.85rem', color: 'var(--muted, #64748b)', marginBottom: '1rem' }}>
                  Select at least one module to continue.
                </p>
              )}
              <button
                className="primary"
                style={{ width: '100%' }}
                disabled={selectedModules.length === 0}
                onClick={handleSaveModules}
              >
                Continue — {selectedModules.length} selected
              </button>
            </section>
          </main>
        )}

        {(view === 'onboarding' || view === 'onboarding-wizard') && (
          <main className="grid">
            <ModernContainer>
              <OnboardingWizard
                onComplete={() => {
                  handleNavigate('manager');
                }}
              />
            </ModernContainer>
          </main>
        )}

        {view === 'business-context' && (
          <main className="grid">
            <ModernContainer>
              <BusinessContextEditor />
            </ModernContainer>
          </main>
        )}

        {view === 'booking-settings' && (
          <main className="grid">
            <ModernContainer>
              <BookingSettings />
            </ModernContainer>
          </main>
        )}

        {view === 'sales-dashboard' && (
          <SalesDashboard 
            API_BASE_URL="/api" 
            token={authToken}
            onNavigate={handleNavigate}
          />
        )}

        {view === 'tokens' && (
          <main className="grid">
            <section className="card span-2">
              <div className="card-header">
                <div>
                  <h2>AI Token Balance</h2>
                  <p>Each AI interaction (call, message, chat) consumes tokens. Top up to keep AI active.</p>
                </div>
                <button className="ghost" type="button" onClick={loadTokenBalance}>Refresh</button>
              </div>
              {tokenBalance && (
                <div className="landing-grid" style={{ padding: '1rem 0' }}>
                  <div className="landing-card">
                    <h3>Current Balance</h3>
                    <p style={{ fontSize: '1.8rem', fontWeight: 700, color: tokenBalance.balance < 50 ? 'var(--danger, #e53e3e)' : 'var(--success, #38a169)' }}>
                      {tokenBalance.balance?.toLocaleString()}
                    </p>
                  </div>
                  <div className="landing-card">
                    <h3>Lifetime Used</h3>
                    <p style={{ fontSize: '1.5rem', fontWeight: 700 }}>{tokenBalance.lifetime_used?.toLocaleString()}</p>
                  </div>
                </div>
              )}
            </section>

            {isAdmin && (
              <section className="card span-2">
                <div className="card-header"><div><h2>Top Up Client</h2><p>Admin: add tokens to any client account.</p></div></div>
                <form className="form form-row" onSubmit={handleTokenTopUp}>
                  <label>
                    Client ID
                    <input
                      type="number"
                      placeholder="Client ID"
                      value={tokenTopUpForm.client_id}
                      onChange={(e) => setTokenTopUpForm({ ...tokenTopUpForm, client_id: e.target.value })}
                      required
                    />
                  </label>
                  <label>
                    Tokens to add
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 1000"
                      value={tokenTopUpForm.amount}
                      onChange={(e) => setTokenTopUpForm({ ...tokenTopUpForm, amount: e.target.value })}
                      required
                    />
                  </label>
                  <label>
                    Notes (optional)
                    <input
                      type="text"
                      placeholder="Reason for top-up"
                      value={tokenTopUpForm.notes}
                      onChange={(e) => setTokenTopUpForm({ ...tokenTopUpForm, notes: e.target.value })}
                    />
                  </label>
                  <div className="form-actions">
                    <button className="primary" type="submit" disabled={tokenTopUpStatus === 'loading'}>
                      {tokenTopUpStatus === 'loading' ? 'Adding…' : 'Add tokens'}
                    </button>
                    {tokenTopUpStatus === 'success' && <span style={{ color: 'var(--success, #38a169)' }}>Done!</span>}
                    {tokenTopUpStatus === 'error' && <span style={{ color: 'var(--danger, #e53e3e)' }}>Failed — check client ID</span>}
                  </div>
                </form>
              </section>
            )}

            {tokenTransactions.length > 0 && (
              <section className="card span-2">
                <div className="card-header"><div><h2>Recent Transactions</h2></div></div>
                <table className="table">
                  <thead><tr><th>Type</th><th>Amount</th><th>Balance After</th><th>Reference</th><th>Date</th></tr></thead>
                  <tbody>
                    {tokenTransactions.map((t) => (
                      <tr key={t.id}>
                        <td>{t.tx_type}</td>
                        <td style={{ color: t.amount > 0 ? 'var(--success, #38a169)' : 'var(--danger, #e53e3e)' }}>
                          {t.amount > 0 ? '+' : ''}{t.amount}
                        </td>
                        <td>{t.balance_after}</td>
                        <td>{t.reference_id || '—'}</td>
                        <td>{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </main>
        )}

        {view === 'social-channels' && <Suspense fallback={<LazyFallback />}><SocialChannelsSettings /></Suspense>}

        {view === 'support' && <SupportDashboard isAdmin={isAdmin} />}

        {view === 'legal' && <LegalCentreAuth onNavigate={handleNavigate} />}

        {/* ── Compliance dashboard (admin) ── */}
        {view === 'compliance' && isAdmin && <ComplianceDashboard />}

        {/* ── AI roleplay demo ── */}
        {(view === 'ai-roleplay' || view === 'ai-test') && <RoleplayDemo />}

        {/* ── User profile ── */}
        {view === 'profile' && (
          <UserProfile user={authUser} onUpdated={() => loadCurrentUser(authToken || localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token'))} />
        )}

        {/* ── 404 fallback — must be last ── */}
        {view === '404' && (
          <NotFoundPage
            onHome={() => setView(isAdmin || isDeveloper ? 'ops-console' : 'client-workspace')}
            onBack={() => window.history.back()}
          />
        )}


        {view === 'financial' && <Suspense fallback={<LazyFallback />}><FinancialDashboard /></Suspense>}

        {/* ── Onboarding checklist ── */}
        {view === 'onboarding-checklist' && (
          <OnboardingChecklist onNavigate={handleNavigate} onDismiss={() => handleNavigate('client-workspace')} />
        )}

        {/* ── Billing history ── */}
        {view === 'billing-history' && (
          <BillingHistory user={authUser} onNavigate={handleNavigate} />
        )}

        {/* ── System health (admin only) ── */}
        {view === 'system-health' && isAdmin && <SystemHealth />}

        {view === 'pricing' && (
          <PricingPage
            user={authUser}
            onContactSales={() => window.open('mailto:sales@oakdesk.co.za?subject=Enterprise inquiry', '_blank')}
          />
        )}

        {view === 'referrals' && isAdmin && (
          <ReferralManager clients={clients} />
        )}

        {view === 'affiliate-dashboard' && (
          <AffiliateDashboard />
        )}

        {view === 'callbacks' && isAdmin && (
          <CallbacksManager />
        )}

        {view === 'permissions' && isAdmin && (
          <PermissionManager currentUser={authUser} />
        )}

        {view === 'invite-member' && isAdmin && (
          <TeamMemberInvite onSuccess={() => setView('permissions')} />
        )}

        {/* ── Ops console — developer/admin only full operational view ── */}
        {view === 'ops-console' && (isAdmin || isDeveloper) && (
          <OpsConsole
            clients={clients}
            onNavigate={setView}
            systemStatus={systemStatus}
          />
        )}

        {/* ── Client workspace — what paying clients (business owners) see ── */}
        {view === 'client-workspace' && (
          <ClientWorkspace
            user={authUser}
            clientId={selectedClientId}
            onNavigate={setView}
            trialStatus={trialStatus}
          />
        )}

        {/* ── Team manager — developer-only cross-client user creation ── */}
        {view === 'team-manager' && isDeveloper && (
          <TeamManager clients={clients} />
        )}

        {/* ── Global number inventory — vendor admin number pool management ── */}
        {view === 'global-number-inventory' && isDeveloper && (
          <Lazy component={GlobalNumberInventory} />
        )}

          </>
        ))}
        {!isLandingLayout && stickyActions && (
          <div className="sticky-actions">
            <div className="sticky-actions__inner">
              <button className="primary" type="button" onClick={stickyActions.primary.onClick}>
                {stickyActions.primary.label}
              </button>
              {stickyActions.secondary && (
                <button className="ghost" type="button" onClick={stickyActions.secondary.onClick}>
                  {stickyActions.secondary.label}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {commandPaletteOpen && (
        <div className="overlay" role="dialog" aria-modal="true">
          <div className="command-palette">
            <div className="command-palette__header">
              <input
                type="search"
                autoFocus
                placeholder="Type a view or action"
                value={commandQuery}
                onChange={(event) => setCommandQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && filteredCommands[0]) {
                    event.preventDefault();
                    handleCommandSelect(filteredCommands[0]);
                  }
                }}
              />
              <button className="ghost" type="button" onClick={() => setCommandPaletteOpen(false)}>
                Close
              </button>
            </div>
            <div className="command-palette__list">
              {filteredCommands.length === 0 ? (
                <p className="empty">No matches yet.</p>
              ) : (
                filteredCommands.map((item) => (
                  <button
                    key={item.id}
                    className="command-palette__item"
                    type="button"
                    onClick={() => handleCommandSelect(item)}
                  >
                    <div>
                      <strong>{item.label}</strong>
                      <span>{item.description}</span>
                    </div>
                    {item.viewId && <span className="pill">View</span>}
                  </button>
                ))
              )}
            </div>
            <div className="command-palette__footer">
              <span>Enter to open - Esc to close</span>
            </div>
          </div>
          <button
            className="overlay__backdrop"
            type="button"
            aria-label="Close search"
            onClick={() => setCommandPaletteOpen(false)}
          />
        </div>
      )}

      {shortcutsOpen && (
        <div className="overlay" role="dialog" aria-modal="true">
          <div className="shortcuts-drawer">
            <div className="shortcuts-drawer__header">
              <h3>Keyboard shortcuts</h3>
              <button className="ghost" type="button" onClick={() => setShortcutsOpen(false)}>
                Close
              </button>
            </div>
            <div className="shortcuts-drawer__list">
              <div className="shortcuts-row">
                <span>Open quick search</span>
                <kbd>Ctrl</kbd><kbd>K</kbd>
              </div>
              <div className="shortcuts-row">
                <span>Open shortcuts</span>
                <kbd>Shift</kbd><kbd>?</kbd>
              </div>
              <div className="shortcuts-row">
                <span>Close overlay</span>
                <kbd>Esc</kbd>
              </div>
            </div>
          </div>
          <button
            className="overlay__backdrop"
            type="button"
            aria-label="Close shortcuts"
            onClick={() => setShortcutsOpen(false)}
          />
        </div>
      )}

      {confirmDialog.open && (
        <div className="overlay" role="dialog" aria-modal="true">
          <div className="confirm-drawer">
            <div className="confirm-drawer__header">
              <h3>{confirmDialog.title}</h3>
              <button className="ghost" type="button" onClick={closeConfirm}>
                Close
              </button>
            </div>
            <p className="confirm-drawer__detail">{confirmDialog.detail}</p>
            <div className="confirm-drawer__actions">
              <button
                className="primary"
                type="button"
                onClick={() => {
                  if (typeof confirmDialog.onConfirm === 'function') {
                    confirmDialog.onConfirm();
                  }
                  closeConfirm();
                }}
              >
                {confirmDialog.confirmLabel}
              </button>
              <button className="ghost" type="button" onClick={closeConfirm}>
                Cancel
              </button>
            </div>
          </div>
          <button
            className="overlay__backdrop"
            type="button"
            aria-label="Close confirmation"
            onClick={closeConfirm}
          />
        </div>
      )}

      {upgradeDialogOpen && (
        <div className="overlay" role="dialog" aria-modal="true">
          <div className="confirm-drawer">
            <div className="confirm-drawer__header">
              <h3>Upgrade to Full Account</h3>
              <button className="ghost" type="button" onClick={() => {
                setUpgradeDialogOpen(false);
                setUpgradeError('');
                setUpgradeStatus('idle');
              }}>
                Close
              </button>
            </div>
            <div className="p-24">
              <p style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
                Create your own client account and start your personalized onboarding. You'll be able to configure your AI receptionist for your specific business needs.
              </p>
              
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Business Name
                <input
                  type="text"
                  value={upgradeBusinessName}
                  onChange={(e) => setUpgradeBusinessName(e.target.value)}
                  placeholder="Enter your business name"
                  style={{
                    width: '100%',
                    marginTop: '8px',
                    padding: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)'
                  }}
                  disabled={upgradeStatus === 'loading'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && upgradeBusinessName.trim()) {
                      handleUpgradeAccount();
                    }
                  }}
                />
              </label>
              
              {upgradeError && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: 'rgba(255, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 0, 0, 0.3)',
                  borderRadius: '8px',
                  color: '#ff4444'
                }}>
                  {upgradeError}
                </div>
              )}
            </div>
            <div className="confirm-drawer__actions">
              <button
                className="primary"
                type="button"
                onClick={handleUpgradeAccount}
                disabled={upgradeStatus === 'loading' || !upgradeBusinessName.trim()}
                style={{
                  background: upgradeStatus === 'loading' ? '#666' : '#7CFF4F',
                  cursor: (upgradeStatus === 'loading' || !upgradeBusinessName.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {upgradeStatus === 'loading' ? 'Upgrading...' : 'Start Onboarding →'}
              </button>
              <button 
                className="ghost" 
                type="button" 
                onClick={() => {
                  setUpgradeDialogOpen(false);
                  setUpgradeError('');
                  setUpgradeStatus('idle');
                }}
                disabled={upgradeStatus === 'loading'}
              >
                Cancel
              </button>
            </div>
          </div>
          <button
            className="overlay__backdrop"
            type="button"
            aria-label="Close upgrade dialog"
            onClick={() => {
              if (upgradeStatus !== 'loading') {
                setUpgradeDialogOpen(false);
                setUpgradeError('');
                setUpgradeStatus('idle');
              }
            }}
          />
        </div>
      )}

      {toasts.length > 0 && (
        <div className="toast-stack" aria-live="polite">
          {toasts.map((toast) => (
            <div key={toast.id} className={`toast toast--${toast.type || 'info'}`}>
              <span>{toast.message}</span>
              <button className="ghost" type="button" onClick={() => dismissToast(toast.id)}>
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}
    </Layout>
    </div>
    </ErrorBoundary>
  );
}

export default App;
